import React, { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import SendIcon from "@mui/icons-material/Send";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import PersonIcon from "@mui/icons-material/Person";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import ThumbUpOffAltIcon from "@mui/icons-material/ThumbUpOffAlt";
import ThumbDownOffAltIcon from "@mui/icons-material/ThumbDownOffAlt";
import ReplayIcon from "@mui/icons-material/Replay";
import StopIcon from "@mui/icons-material/Stop";
/* 답장 — 아래로 꺾여 왼쪽을 가리키는 화살표 (사용자 2026-10-17) */
import ReplyIcon from "@mui/icons-material/SubdirectoryArrowLeftRounded";
import MenuIcon from "@mui/icons-material/Menu";
/* 아이콘은 카톡 PC 와 비슷한 모양으로 (2026-10-16) — 새 대화 말풍선＋ · 대화 목록 말풍선 · 내보내기 · 캡처 영역 */
import MapsUgcOutlinedIcon from "@mui/icons-material/MapsUgcOutlined";
import CropFreeOutlinedIcon from "@mui/icons-material/CropFreeOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ChatBubbleIcon from "@mui/icons-material/ChatBubble";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import IosShareOutlinedIcon from "@mui/icons-material/IosShareOutlined";
import {
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Popover,
} from "@mui/material";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import { TagIcon, TagPalette, pushRecentTag } from "./chatTags";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import CloseIcon from "@mui/icons-material/Close";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import ChatTip from "./ChatTip";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { ApiError } from "@shared/type/ApiType";
import { aiScreenOpen } from "@shared/libs/aiScreenOpen";
import {
  clearChatContext,
  compactChatV2,
  fetchChatAttachmentCapabilities,
  fetchChatHelpV2,
  helpSectionsToMarkdown,
  isChatHelpCommandV2,
  sendChatFeedback,
  sendChatMessage,
  sendChatMessageV2,
  cancelChatV2,
  newChatRequestId,
  agentOfRole,
  uploadChatAttachmentV2,
  deleteChatAttachmentV2,
  checkChatAttachmentV2,
} from "@shared/api/chatApi";
import type {
  ChatAttachmentRefV2,
  ChatDocumentAnswerMeta,
  ChatDocumentCapability,
  ChatEngine,
  ChatReply,
} from "@shared/api/chatApi";
import type { ChatRoomAttachmentMeta } from "@shared/api/chatRoomApi";
import ChatAttachmentImage, { openAttachmentFile } from "./ChatAttachmentImage";
import { readTableFile } from "./chatTableFile";
import {
  cancelExcelUploadOffer,
  offerExcelUploadTable,
} from "@shared/libs/excelUploadOffer";
import { useChatAttachment } from "./useChatAttachment";
import {
  documentKindOf,
  useChatDocumentAttachment,
} from "./useChatDocumentAttachment";
import {
  attachmentAcceptFor,
  ChatbotDocumentChip,
  ChatbotDocumentDisclosure,
} from "./ChatbotDocumentChip";
import {
  DocumentAnswerExtras,
  DocumentCandidateOption,
  DocumentQualityBadge,
} from "./ChatbotDocumentAnswer";
import type {
  ChatAgentInfo,
  ChatChartPayload,
  ChatFeedback,
  ChatScreenAction,
  ChatTablePayload,
} from "@shared/api/chatApi";
import type {
  ChatAddRowRequest,
  ChatFieldPick,
  ChatSearchApplyResult,
  ChatSearchFormData,
  ChatSearchFormValue,
} from "@shared/type/CommonType";
import {
  onSearchStore,
  globalMenu,
  global,
  useMenuStore,
  runActiveScreenExcelUpload,
} from "@shared/libs/Global";
import { DESIGNER_MENU } from "@shared/api/designApi";
import { studioAiActions } from "@shared/libs/studioAiActions";
import { aiSignal } from "@shared/libs/aiSignal";
import DesignerSidebar from "./DesignerSidebar";
import RoomSidebar, { ROOMS_MIN_WIDTH } from "./RoomSidebar";
import type { RoomView } from "./RoomSidebar";
import {
  LEGACY_ROOM_NOTICE,
  fetchChatRoom,
  fetchChatRoomTurns,
  searchChatRoom,
  fetchChatTags,
  createChatTag,
  updateChatTag,
  hideChatTag,
  fetchTagHits,
  toggleBubbleTag,
  searchChatRoomByTag,
  hideChatRoom,
  filterRoomsByTitle,
  listChatRooms,
  renameChatRoom,
  restoreChatRoom,
  turnsToHistory,
  chatTimeline,
  roomTitle,
} from "@shared/api/chatRoomApi";
import type {
  ChatRoom,
  ChatRoomTurn,
  ChatTag,
  ChatTagHit,
} from "@shared/api/chatRoomApi";
import {
  chatRoomStore,
  forgetRoom,
  recallRoom,
  rememberRoom,
  roomStorageKey,
} from "@shared/libs/chatRoomStore";
import type { RoomSnapshot } from "@shared/libs/chatRoomStore";
import ChatPrivacyNotice from "./ChatPrivacyNotice";
import {
  isChatCommandTarget,
  sendChatCommand,
} from "@shared/libs/chatCommandBridge";
import AiMarkdown from "@shared/components/AiMarkdown";
import SnChart from "@shared/components/Grid/SnChart";
import type { SnChartHandle } from "@shared/components/Grid/SnChart";
import {
  copyOrDownloadImage,
  copyOrDownloadTable,
  downloadImage,
  downloadTableCsv,
  exportFileName,
  downloadChatFile,
  copyChatText,
  buildChatExportText,
  saveChatText,
} from "./chatExport";
import { ChatExportProgress } from "./ChatExportProgress";
import { showPopupModal } from "@widgets/CommonModal";
import "./Chatbot.css";

/** 사용자 말풍선에 칩으로 표시할 붙여넣기 첨부 메타(내용은 전송 시에만 사용). */
/** 코드 · 콤보 칸 후보 버튼의 종류 — 서버 되묻기 버튼과 가른다 (2026-10-02) */
const FIELD_PICK_KIND = "AI_FIELD_PICK";

type MessageAttachmentMeta = {
  name: string;
  charCount: number;
  /** file: 문서(V1 표 포함), image: 그림(V2 첨부, 2026-10-26), table: V2 표(엑셀 · CSV, 2026-10-28). 없으면 붙여넣은 글 */
  kind?: "file" | "image" | "table";
  /** 그림 미리보기(object URL) — 이 세션에서 보낸 것만. 복원한 턴에는 없다 */
  previewUrl?: string;
  /** 그림 — 첨부 번호. 다시 연 방에서는 이것으로 서버에서 받아 온다 (2026-10-27) */
  fileId?: string;
};

/** 복원한 턴의 첨부 메타 → 말풍선 칩 (2026-10-26) */
function restoredAttachmentMeta(
  list?: ChatRoomAttachmentMeta[]
): MessageAttachmentMeta[] | undefined {
  if (!list || list.length === 0) return undefined;
  return list.map((a) => ({
    name: a.name,
    charCount: a.chars ?? 0,
    ...(a.kind === "IMAGE"
      ? { kind: "image" as const, ...(a.id ? { fileId: a.id } : {}) }
      : a.kind === "TEXT"
        ? {}
        : a.kind === "TABLE"
          ? { kind: "table" as const }
          : { kind: "file" as const, ...(a.id ? { fileId: a.id } : {}) }),
  }));
}

/** 입력창 트레이의 그림 칩(V2 첨부, 2026-10-26). 서버 id 가 생기면 READY */
type V2ImageChip = {
  key: number;
  id?: string;
  name: string;
  size: number;
  previewUrl: string;
  status: "UPLOADING" | "READY";
  /** 그림 · PDF · 표(엑셀 · CSV) (2026-10-28) */
  kind: "IMAGE" | "DOCUMENT" | "TABLE";
  /** PDF 쪽 수 — 올린 뒤 서버가 알려 준다 */
  pages?: number;
};

/** 한 질문에 보낼 수 있는 그림 수 · 한 장 크기 — 서버 AiAttachmentLimits 와 같은 값(표시 · 1차 거르기용) */
const V2_IMAGES_PER_QUESTION = 10;
const V2_IMAGE_MAX_BYTES = 10 * 1024 * 1024;
const IMAGE_PRIVACY_NOTICE =
  "그림(png · jpeg)은 글과 달리 개인정보를 가리지 못하고 그대로 AI 에 전송됩니다. 대화에 함께 암호화해 보관되며 보존 기간이 지나면 지워집니다.";

/** PDF 하나 크기 — 서버 SAI0004 기본값과 같다(표시 · 1차 거르기용). 서버가 다시 본다 */
const V2_DOCUMENT_MAX_BYTES = 20 * 1024 * 1024;
/** 엑셀 · CSV 하나 크기 — 서버 표 읽기(AttachmentLimits.MAX_SOURCE_BYTES)와 같다 (리뷰, 2026-10-29) */
const V2_TABLE_MAX_BYTES = 10 * 1024 * 1024;

function isPdfFile(file: File): boolean {
  return (
    file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf")
  );
}

function isTableFile(file: File): boolean {
  const lower = file.name.toLowerCase();
  return lower.endsWith(".xlsx") || lower.endsWith(".csv");
}

function isImageFile(file: File): boolean {
  if (file.type === "image/png" || file.type === "image/jpeg") return true;
  const lower = file.name.toLowerCase();
  return (
    lower.endsWith(".png") || lower.endsWith(".jpg") || lower.endsWith(".jpeg")
  );
}

/** 답장 인용을 화면에 보일 때 — 마크다운 기호를 걷고 한 줄로. 보내는 원문은 그대로 (2026-10-15) */
function plainQuote(text: string): string {
  return text
    .replace(/[*_`#>|]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

type ChatMessage = {
  id: number;
  role: "user" | "assistant";
  text: string;
  chart?: ChatChartPayload;
  table?: ChatTablePayload;
  /** 이 답변의 히스토리 id — 있으면 👍/👎를 보낼 수 있다(v0.5.9). */
  noChat?: string;
  /** AI Center(v2) 답변의 호출 id — noChat 대신 이것으로 👍/👎 신호를 보낸다(2026-09-12). */
  idCall?: string;
  /** 이 답을 만든 에이전트(v0.6.0) — 아이콘+이름 배지로 표시. */
  agent?: ChatAgentInfo;
  attachments?: MessageAttachmentMeta[];
  /**
   * 데이터 소스 판별자(첨부 v1) — "ATTACHMENT"면 본문을 마크다운이 아니라 literal 텍스트로
   * 렌더한다(파일 유래 값의 마크다운/HTML 해석 차단, draft §66). 없음/"SCREEN"은 기존 그대로.
   */
  sourceKind?: string;
  /**
   * 되묻기 버튼 목록 — 시트 선택(TABLE_SELECTION, selectionAttachmentId 있음) 또는 화면 경로
   * 되묻기(ROW_KEY_SELECTION/ADD_ROW_SCOPE, kind 있음 → {kind, optionId} 구조 전송, 2026-09-02).
   */
  selectionOptions?: { label: string; optionId: string; kind?: string }[];
  /** 시트 선택 버튼이 대상하는 첨부 id — 클릭 시 선택 전송에 실린다. */
  selectionAttachmentId?: string;
  /** 문서 답 메타(plan §Task 23) — 품질 배지·라인 페이징 렌더 재료(값 없음). */
  documentMeta?: ChatDocumentAnswerMeta;
  /** 문서 되묻기 후보 — questionId+candidateId 전송용(로컬 사실 데이터 수정 없음). */
  documentCandidates?: DocumentCandidateOption[];
  documentQuestionId?: string;
  documentAttachmentId?: string;
  /** 실패로 끝난 턴 (표시만 다르게 한다) */
  isError?: boolean;
  /** 복원한 대화에서 이미 남긴 평가 (평가 UI의 초기 상태). */
  feedbackValue?: ChatFeedback;
  /** 이 질문이 답장한 앞 답 — 카톡 답장처럼 내 말풍선 위에 보인다 (2026-10-15) */
  quote?: string;
  /** 이 질문에 실어 보낸 첨부 참조 — "다시 시도" 가 그대로 다시 보낸다 (2026-10-26) */
  sentAttachments?: ChatAttachmentRefV2[];
  /** 보낸 · 받은 시각(ISO). 다시 연 방은 턴 시각. 날짜 구분선 · 시간에 쓴다 (2026-10-15) */
  at?: string;
  /**
   * 메뉴디자이너 대화의 턴 순번(1부터). 질문/답변 말풍선이 같은 값을 갖는다.
   * 검색 결과(hits[].seq)와 같은 값 체계라, 클릭하면 이 값으로 말풍선을 찾아 스크롤한다.
   */
  turnSeq?: number;
  /** 복원한 방의 마지막 답 — 호출 id 는 없지만 서버에 턴이 있어 "다시 시도" 할 수 있다 (2026-10-14) */
  retryable?: boolean;
  /** 이 답에 쓴 포인트 — 늘 보이지 않고 시간에 마우스를 올리면 보인다 (2026-10-21) */
  point?: number;
  /** 이 말풍선에 단 태그 번호 (2026-10-22). 턴 번호(turnSeq)가 있어야 달 수 있다 */
  tags?: number[];
};

/** 포인트 풍선 — "사용 7.5 P"(숫자와 P 사이 한 칸). 0 이면 0 P(AI 없이 처리), 아주 작으면 "0.1 P 미만" */
const pointTip = (point: number) =>
  `사용 ${point === 0 ? "0 P" : point < 0.1 ? "0.1 P 미만" : `${point.toFixed(1)} P`}`;

/** 에이전트 아이콘 키(백엔드) → MUI 아이콘. 미매핑 키는 기본 로봇 아이콘. */
const AGENT_ICONS: Record<string, React.ElementType> = {
  analytics: AnalyticsIcon,
  search: SearchIcon,
  menu_book: MenuBookIcon,
  playlist_add: PlaylistAddIcon,
  smart_toy: SmartToyIcon,
  /* 보고서 도우미 (2026-10-22) */
  description: DescriptionOutlinedIcon,
};

/** 답변 상단의 "이 답을 만든 에이전트" 배지 (아이콘 + 이름). */
const AgentBadge: React.FC<{ agent: ChatAgentInfo }> = ({ agent }) => {
  const Icon = AGENT_ICONS[agent.icon] ?? SmartToyIcon;
  return (
    <div className={`chatbot-agent-badge chatbot-agent-${agent.id}`}>
      <Icon fontSize="inherit" />
      <span>{agent.label}</span>
    </div>
  );
};

type RenderableChartType = "bar" | "line" | "pie" | "donut";

/** 입력창에 붙여넣은 긴 텍스트를 담아두는 첨부(전송 시 본문으로 합쳐 보낸다). */
type PastedAttachment = {
  id: number;
  name: string;
  content: string;
  charCount: number;
};

/**
 * 붙여넣은 텍스트가 이 글자 수 이상이면 입력창에 그대로 넣지 않고 텍스트 첨부(칩)로 전환한다.
 * (사용자 요청 시 이 값만 조절하면 민감도가 바뀐다.)
 */
const PASTE_AS_FILE_THRESHOLD = 300;

/** 좌측 작업 메뉴(DesignerSidebar)는 이 화면(메뉴 디자이너)에서만 노출한다. */
const MENU_DESIGNER_CODE = "SN_SFA_ISSUE_REG_006";

/** 첨부 파일명 생성: "2026-07-24 103505.900.txt" 형태(붙여넣은 시각 기준). */
function makePastedFileName(): string {
  const now = new Date();
  const p = (value: number, len = 2) => String(value).padStart(len, "0");
  const date = `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
  const time = `${p(now.getHours())}${p(now.getMinutes())}${p(now.getSeconds())}.${p(
    now.getMilliseconds(),
    3
  )}`;
  return `${date} ${time}.txt`;
}

/** 입력한 문장 + 첨부 본문을 하나의 메시지로 합친다(AI에게는 전체 내용이 전달됨). */
function buildOutgoingMessage(
  typed: string,
  attachments: PastedAttachment[]
): string {
  const parts: string[] = [];
  if (typed) parts.push(typed);
  for (const att of attachments) {
    parts.push(`[붙여넣은 내용: ${att.name}]\n${att.content}`);
  }
  return parts.join("\n\n");
}

/** 복사/저장 후 잠깐 결과를 보여주는 훅. */
function useFlash(): [string, (message: string) => void] {
  const [status, setStatus] = useState("");
  const flash = (message: string) => {
    setStatus(message);
    window.setTimeout(() => setStatus(""), 1600);
  };
  return [status, flash];
}

// SnChart의 options/seriesOptions 기본값({})은 렌더마다 새 객체라서 차트가 매번
// 파괴·재생성된다. 챗봇에서는 안정 참조를 넘겨 불필요한 재생성을 막는다.
const CHART_OPTIONS: Record<string, never> = {};
const CHART_SERIES_OPTIONS: Record<string, never> = {};
/** 업로드 전 개인정보 고지(첨부 draft §21·§22) — 첫 행=컬럼명 계약과 AI 전송 경계를 밝힌다. */
const ATTACHMENT_PRIVACY_NOTICE =
  "표 파일의 첫 행은 컬럼명으로 사용됩니다. 질문 해석에 필요한 경우 컬럼명·형식 정보만 AI로 전송되며, " +
  "데이터 값·파일명·시트명은 전송되지 않습니다. 컬럼명이 없는 파일은 컬럼명을 넣어 다시 내보내 주세요.";

/** 첨부 차트 — 인터랙티브 툴팁 비활성(파일 유래 라벨이 차트 라이브러리 HTML로 가지 않게, §66). */
const ATTACHMENT_CHART_OPTIONS = { tooltip: { enabled: false } } as Record<
  string,
  unknown
>;

function isChatAddRowScope(value: unknown): boolean {
  return value === "master" || value === "detail" || value === "all";
}

function isChatAddRowRequest(value: unknown): value is ChatAddRowRequest {
  if (
    typeof value !== "object" ||
    value === null ||
    !("mode" in value) ||
    !("scope" in value)
  ) {
    return false;
  }
  if (!isChatAddRowScope(value.scope)) {
    return false;
  }
  /* 바꿀 값 — 있으면 글자 값만 든 객체여야 한다 (2026-10-15) */
  if (
    "values" in value &&
    value.values !== undefined &&
    value.values !== null
  ) {
    const values = value.values;
    if (typeof values !== "object" || Array.isArray(values)) return false;
    if (
      !Object.values(values as Record<string, unknown>).every(
        (v) => typeof v === "string"
      )
    )
      return false;
  }
  if (value.mode === "copySelected" || value.mode === "new") {
    return true;
  }
  if (value.mode === "copyByRow") {
    /* 화면 왼쪽 No(1부터, 마지막은 -1) */
    const rowNo = "rowNo" in value ? value.rowNo : undefined;
    return typeof rowNo === "number" && Number.isInteger(rowNo) && rowNo !== 0;
  }
  if (value.mode === "copyByKey") {
    // 백엔드는 항상 문자열로 발행 — 비어 있거나 문자열이 아니면 계약 위반으로 거절(2026-09-01).
    const sourceKey = "sourceKey" in value ? value.sourceKey : undefined;
    return (
      typeof sourceKey === "object" &&
      sourceKey !== null &&
      "field" in sourceKey &&
      "value" in sourceKey &&
      typeof sourceKey.field === "string" &&
      sourceKey.field.trim() !== "" &&
      typeof sourceKey.value === "string" &&
      sourceKey.value.trim() !== ""
    );
  }
  return false;
}

/** 화면 경로 되묻기 중 버튼으로 그릴 kind — 회귀 범위를 좁히기 위해 명시 목록만(2026-09-01). */
const SCREEN_SELECTION_KINDS = new Set(["ROW_KEY_SELECTION", "ADD_ROW_SCOPE"]);

function screenSelectionOptions(
  reply: ChatReply | null | undefined
): { label: string; optionId: string; kind: string }[] | undefined {
  const clarification = reply?.clarification;
  if (
    !clarification?.kind ||
    !SCREEN_SELECTION_KINDS.has(clarification.kind) ||
    !clarification.options
  ) {
    return undefined;
  }
  const kind = clarification.kind;
  const options = Object.entries(clarification.options).map(
    ([label, optionId]) => ({ label, optionId, kind })
  );
  return options.length > 0 ? options : undefined;
}

function screenFunctionParams(paramsPatch: ChatScreenAction["paramsPatch"]): {
  readonly functionName: string | undefined;
  readonly request: ChatAddRowRequest | undefined;
} {
  if (!paramsPatch || typeof paramsPatch !== "object") {
    return { functionName: undefined, request: undefined };
  }
  const functionName =
    "function" in paramsPatch && typeof paramsPatch.function === "string"
      ? paramsPatch.function
      : undefined;
  const request = "request" in paramsPatch ? paramsPatch.request : undefined;
  return {
    functionName,
    request: isChatAddRowRequest(request) ? request : undefined,
  };
}

function searchFormData(
  paramsPatch: ChatScreenAction["paramsPatch"]
): ChatSearchFormData {
  if (!paramsPatch || typeof paramsPatch !== "object") {
    return {};
  }
  const formData: ChatSearchFormData = {};
  Object.entries(paramsPatch).forEach(([key, value]) => {
    if (isChatSearchFormValue(value)) {
      formData[key] = value;
    }
  });
  return formData;
}

function isChatSearchFormValue(value: unknown): value is ChatSearchFormValue {
  return (
    value === null ||
    value === undefined ||
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  );
}

const AssistantChart: React.FC<{
  chart: ChatChartPayload;
  type: RenderableChartType;
  sourceKind?: string;
}> = ({ chart, type, sourceKind }) => {
  const chartRef = useRef<SnChartHandle>(null);
  const [status, flash] = useFlash();

  const withImage = async (
    action: (dataUrl: string, fileName: string) => Promise<string> | string
  ) => {
    // 화면(240px)에선 범례를 화살표로 넘겨 보지만, 내보낼 땐 크게 재렌더해
    // 그룹(범례) 전체가 한 장에 담기게 한다.
    const dataUrl = await chartRef.current?.getImageDataURL({
      width: 900,
      height: 540,
    });
    if (!dataUrl) {
      flash("이미지를 만들지 못했습니다");
      return;
    }
    const result = await action(dataUrl, exportFileName(chart.title, "png"));
    flash(result === "copied" ? "차트를 복사했습니다" : "차트를 저장했습니다");
  };

  return (
    <div className="chatbot-chart" aria-label={chart.title || "chart"}>
      <SnChart
        ref={chartRef}
        data={chart.data}
        height={240}
        title={chart.title || ""}
        type={type}
        width="100%"
        xKey={chart.categoryKey}
        yKey={chart.valueKey}
        yName={chart.valueLabel || chart.valueKey}
        options={
          sourceKind === "ATTACHMENT"
            ? (ATTACHMENT_CHART_OPTIONS as typeof CHART_OPTIONS)
            : CHART_OPTIONS
        }
        seriesOptions={CHART_SERIES_OPTIONS}
      />
      <div className="chatbot-export">
        <button
          type="button"
          className="chatbot-export-btn"
          onClick={() => withImage(copyOrDownloadImage)}
        >
          <ContentCopyIcon fontSize="inherit" /> 복사
        </button>
        <button
          type="button"
          className="chatbot-export-btn"
          onClick={() => withImage(downloadImage)}
        >
          <DownloadIcon fontSize="inherit" /> 저장
        </button>
        {status && <span className="chatbot-export-status">{status}</span>}
      </div>
      {chart.basis && <div className="chatbot-chart-basis">{chart.basis}</div>}
    </div>
  );
};

const AssistantTable: React.FC<{
  table: ChatTablePayload;
}> = ({ table }) => {
  const [status, flash] = useFlash();
  // 내보내기는 늘 수식 중화(§67) — V2 는 첨부 표 집계(sys_file_aggregate)도 화면 집계와 같은 표로 와서
  // 출처로 가를 수 없다. 파일의 =… 글이 붙여넣기 · CSV 에서 수식이 되지 않게 (리뷰, 2026-10-29)
  const exportOptions = { neutralize: true };

  const handleCopy = async () => {
    const result = await copyOrDownloadTable(
      table,
      exportFileName(table.title, "csv"),
      exportOptions
    );
    flash(
      result === "copied"
        ? "표를 복사했습니다 (엑셀에 붙여넣기)"
        : "표를 저장했습니다"
    );
  };

  const handleSave = () => {
    downloadTableCsv(table, exportFileName(table.title, "csv"), exportOptions);
    flash("표를 저장했습니다");
  };

  return (
    <div className="chatbot-table" aria-label={table.title || "table"}>
      <table>
        <thead>
          <tr>
            {table.columns.map((column, index) => (
              <th key={index}>{column}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {row.map((cell, cellIndex) => (
                <td
                  key={cellIndex}
                  className={cellIndex > 0 ? "chatbot-table-num" : undefined}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="chatbot-export">
        <button
          type="button"
          className="chatbot-export-btn"
          onClick={handleCopy}
        >
          <ContentCopyIcon fontSize="inherit" /> 복사
        </button>
        <button
          type="button"
          className="chatbot-export-btn"
          onClick={handleSave}
        >
          <DownloadIcon fontSize="inherit" /> 저장
        </button>
        {status && <span className="chatbot-export-status">{status}</span>}
      </div>
      {table.basis && <div className="chatbot-chart-basis">{table.basis}</div>}
    </div>
  );
};

/**
 * 평가 대상 — 일반 챗의 히스토리 id.
 * 메뉴디자이너(화면 명령) 평가 DESIGN 은 뺐다 — 보낼 곳이 없어 눌러도 기록되지 않았고, release 에는 없는 칸이라
 * appendMessage 매개변수가 dev 에만 하나 더 있어 체리픽 때마다 인수가 어긋났다 (2026-10-28)
 */
type FeedbackTarget = { kind: "CHAT"; noChat: string };

/** 말풍선에서 평가 대상을 뽑는다 — 없으면 평가 UI를 그리지 않는다. */
function feedbackTargetOf(message: ChatMessage): FeedbackTarget | null {
  if (message.role !== "assistant") return null;
  if (message.noChat) return { kind: "CHAT", noChat: message.noChat };
  return null;
}

/**
 * 평가 전송. 반환 false = 전송 실패 또는 서버가 대상 레코드를 못 찾음(기록 안 됨).
 * CHAT → POST /api/chat/{noChat}/feedback
 */
async function sendFeedbackTo(
  target: FeedbackTarget,
  feedback: ChatFeedback,
  comment?: string
): Promise<boolean> {
  const result = await sendChatFeedback(target.noChat, feedback, comment);
  return result?.data !== false;
}

/**
 * 답변 피드백(v0.5.9 파일럿 수집): 👍는 즉시 전송, 👎는 한 줄 코멘트를 받은 뒤 전송(건너뛰기 가능).
 * 전송 실패는 조용히 무시한다 — 수집용 부가 기능이라 대화 흐름을 막지 않는다.
 * 평가 대상이 없는 답변(서버가 기록하지 못한 턴)에는 아무것도 그리지 않는다.
 */
/**
 * AI Center(v2) 답변 평가 — 👍/👎 만. 코멘트 입력은 두지 않는다(원문을 중앙에 남기지 않는다, D33). 다시 눌러 바꿀 수 있다(2026-10-01).
 * noChat 이 아니라 idCall 로 답을 가리키고, 행동 신호(GOOD/BAD)로 보낸다.
 */
const AssistantFeedbackV2: React.FC<{
  idCall: string;
  /** "다시 시도" — 마지막 답에만 온다 (2026-10-14). 없으면 버튼을 그리지 않는다 */
  onRetry?: () => void;
  retryDisabled?: boolean;
  /** 답변 글 복사 (2026-10-15). 비면 버튼을 그리지 않는다 */
  copyText?: string;
}> = ({ idCall, onRetry, retryDisabled, copyText }) => {
  const [sent, setSent] = useState<ChatFeedback | null>(null);
  /* 바꿀 수 있다 — 중앙이 같은 답의 이전 평가를 지우고 새것만 남긴다 (2026-10-01). 같은 것을 또 누르면 보내지 않는다 */
  const rate = (good: boolean) => {
    const next: ChatFeedback = good ? "GOOD" : "BAD";
    if (sent === next) return;
    setSent(next);
    void aiSignal.rate(
      idCall,
      globalMenu.getState().activeMenuInfo?.cd_menu,
      good
    );
  };
  return (
    <div className="chatbot-feedback">
      {copyText && <CopyButton text={copyText} />}
      <ChatTip title="좋아요">
        <button
          aria-label="좋아요"
          className={`chatbot-feedback-btn${sent === "GOOD" ? " is-active" : ""}`}
          onClick={() => rate(true)}
          type="button"
        >
          <ThumbUpOffAltIcon fontSize="inherit" />
        </button>
      </ChatTip>
      <ChatTip title="아쉬워요">
        <button
          aria-label="아쉬워요"
          className={`chatbot-feedback-btn${sent === "BAD" ? " is-active" : ""}`}
          onClick={() => rate(false)}
          type="button"
        >
          <ThumbDownOffAltIcon fontSize="inherit" />
        </button>
      </ChatTip>
      {onRetry && <RetryButton disabled={retryDisabled} onClick={onRetry} />}
      {sent && <span className="chatbot-feedback-status">의견 감사합니다</span>}
    </div>
  );
};

/**
 * 답변 글 복사 (2026-10-15). 마크다운 원문을 그대로 복사한다(ChatGPT 와 같다). 꼬리말 "_화면 기준 (…)_" 의 밑줄만 벗긴다.
 * 1.5초 동안 "복사했습니다" · 실패면 "복사하지 못했습니다" 를 옆에 띄운다
 */
const CopyButton: React.FC<{ text: string }> = ({ text }) => {
  const [status, setStatus] = useState<string | null>(null);
  const timer = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (timer.current) window.clearTimeout(timer.current);
    },
    []
  );
  const copy = async () => {
    const plain = text.replace(/\n\n_(화면 기준 \([^)]*\))_\s*$/, "\n\n$1");
    const ok = await copyChatText(plain);
    setStatus(ok ? "복사했습니다" : "복사하지 못했습니다");
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setStatus(null), 1500);
  };
  return (
    <>
      <ChatTip title="복사">
        <button
          aria-label="답변 글 복사"
          className="chatbot-feedback-btn"
          onClick={() => void copy()}
          type="button"
        >
          <ContentCopyIcon fontSize="inherit" />
        </button>
      </ChatTip>
      {status && <span className="chatbot-feedback-status">{status}</span>}
    </>
  );
};

/** "다시 시도" — 같은 질문으로 답을 다시 만든다 (2026-10-14, aibot-v2-chat-retry). 평가 줄 안에 같이 선다 */
const RetryButton: React.FC<{ disabled?: boolean; onClick: () => void }> = ({
  disabled,
  onClick,
}) => (
  <button
    aria-label="같은 질문으로 답을 다시 만들기"
    className="chatbot-feedback-btn"
    disabled={disabled}
    onClick={onClick}
    type="button"
  >
    <ReplayIcon fontSize="inherit" /> 다시 시도
  </button>
);

/* 아래는 기존(v1) 평가. dev 의 v0.10.0 판을 그대로 둔다 — 복원한 대화의 이전 평가를 살린다.
   복사 버튼(2026-10-15)만 줄 앞에 더했다 */
const AssistantFeedback: React.FC<{ message: ChatMessage }> = ({ message }) => {
  const target = feedbackTargetOf(message);
  // 복원한 대화라면 이미 남긴 평가가 초기 상태가 된다
  const [sent, setSent] = useState<ChatFeedback | null>(
    message.feedbackValue ?? null
  );
  // 복원된 평가에는 "의견 감사합니다"를 띄우지 않는다 — 방금 누른 것처럼 보이니까
  const [justSent, setJustSent] = useState(false);
  const [commenting, setCommenting] = useState(false);
  const [comment, setComment] = useState("");

  const send = async (
    feedback: ChatFeedback,
    text?: string,
    keepOpen = false
  ) => {
    if (!target) return;
    setSent(feedback);
    setJustSent(true);
    if (!keepOpen) {
      setCommenting(false);
      setComment("");
    }
    try {
      const ok = await sendFeedbackTo(
        target,
        feedback,
        text?.trim() || undefined
      );
      // 기록 실패. 사용자 흐름은 막지 않고 콘솔로만 알린다.
      if (!ok) {
        console.warn("CHAT-FEEDBACK 기록 실패: 대상 레코드 없음", {
          target,
          feedback,
        });
      }
    } catch (error) {
      // 사용자 흐름은 막지 않되(수집용 부가 기능), 진단을 위해 콘솔에는 남긴다.
      console.error("CHAT-FEEDBACK 전송 실패", { target, feedback }, error);
    }
  };

  // 평가할 대상이 없는 답변(서버가 기록하지 못한 턴) — 엄지를 아예 그리지 않는다
  if (!target) return null;

  if (commenting) {
    return (
      <div className="chatbot-feedback">
        <input
          aria-label="피드백 코멘트"
          className="chatbot-feedback-input"
          autoFocus
          maxLength={1000}
          onChange={(event) => setComment(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              void send("BAD", comment);
            } else if (event.key === "Escape") {
              void send("BAD");
            }
          }}
          placeholder="어떤 답을 기대하셨나요? (선택)"
          value={comment}
        />
        <button
          type="button"
          className="chatbot-feedback-btn"
          onClick={() => void send("BAD", comment)}
        >
          보내기
        </button>
        <button
          type="button"
          className="chatbot-feedback-btn"
          onClick={() => void send("BAD")}
        >
          건너뛰기
        </button>
      </div>
    );
  }

  return (
    <div className="chatbot-feedback">
      {message.text.trim() && <CopyButton text={message.text} />}
      <button
        aria-label="도움이 됐어요"
        aria-pressed={sent === "GOOD"}
        className={`chatbot-feedback-btn${sent === "GOOD" ? " is-active" : ""}`}
        onClick={() => void send("GOOD")}
        type="button"
      >
        <ThumbUpOffAltIcon fontSize="inherit" />
      </button>
      <button
        aria-label="아쉬워요"
        aria-pressed={sent === "BAD"}
        className={`chatbot-feedback-btn${sent === "BAD" ? " is-active" : ""}`}
        onClick={() => {
          // 클릭 즉시 BAD를 기록한다 — 코멘트를 안 쓰고 떠나도 신호가 유실되지 않게(수집이 목적).
          // 코멘트를 쓰면 같은 레코드를 덮어쓴다(API가 덮어쓰기 허용).
          setCommenting(true);
          void send("BAD", undefined, true);
        }}
        type="button"
      >
        <ThumbDownOffAltIcon fontSize="inherit" />
      </button>
      {justSent && (
        <span className="chatbot-feedback-status">의견 감사합니다</span>
      )}
    </div>
  );
};

/** 중지한 턴의 답. 서버(AiGatewayService.STOPPED_TEXT)가 저장하는 문구와 같아야 복원했을 때 같아 보인다 (2026-10-14) */
const STOPPED_TEXT = "답변을 중지했습니다.";
/** 캡처 그림에 넣을 글꼴 — 변환이 가장 오래 걸려 처음 한 번만 하고 다시 쓴다 (2026-10-15) */
let captureFontCss: string | null = null;
/** 대화 목록 펼침 · 접힘을 기억하는 브라우저 저장 키 (2026-10-16) */
const ROOMS_OPEN_KEY = "snotes.chatbot.roomsOpen";
/** 대화 목록 폭을 기억하는 브라우저 저장 키 (2026-10-21) */
const ROOMS_WIDTH_KEY = "snotes.chatbot.roomsWidth";
/** 중앙 ScreenActions.ANSWER_HERE — [지금 화면으로 답하기] 버튼의 후보 코드 (2026-10-21) */
const ANSWER_HERE = "_here";

/** 위쪽 메뉴의 묶음 제목 — 조회조건 우클릭 메뉴(SearchPanel)와 같은 모양 */
const menuHeadSx = {
  lineHeight: "28px",
  fontSize: 12,
  color: "#7d7e80",
  bgcolor: "transparent",
};

const initialMessages: ChatMessage[] = [
  {
    id: 1,
    role: "assistant",
    text: "SNotes AI Bot입니다. 무엇을 도와드릴까요?\n\n사용 가능한 질문 예시는 /도움 을 입력해 확인할 수 있습니다.",
  },
];

const Chatbot: React.FC = () => {
  const nextId = useRef(2);
  const attachmentId = useRef(1);
  const [messages, setMessages] = useState<ChatMessage[]>(initialMessages);
  /* 엔진. 대화 도중에는 못 바꾼다. 중간에 갈아타면 앞뒤 맥락이 끊긴 답이 나온다.
     2026-10-16 레거시(V1)는 쓰지 않는다 — 화면에서 고르는 칸을 없애고 AI Center(V2)로만 부른다. V1 경로는 나중에 걷어낸다(할일목록) */
  const [engine] = useState<ChatEngine>("V2");
  const [input, setInput] = useState("");
  const [attachments, setAttachments] = useState<PastedAttachment[]>([]);
  /* V2 그림 첨부 트레이 (2026-10-26) — 붙여넣기 · 드롭 · 파일 선택. 서버 id 로 질문에 실린다 */
  const [v2Images, setV2Images] = useState<V2ImageChip[]>([]);
  /* 핸들러가 최신 트레이를 바로 읽게(상태 updater 밖에서 개수 · 대상을 본다) */
  const v2ImagesRef = useRef<V2ImageChip[]>([]);
  v2ImagesRef.current = v2Images;
  const v2ImageKey = useRef(1);
  // 업로드 표 파일(첨부 v1) — 붙여넣기 텍스트 칩과 상호 배타(§39)
  const chatAttachment = useChatAttachment();
  // 문서(PDF/PNG/JPEG) 첨부(IDP plan §Task 22) — 표 첨부와 상호 배타, capability로만 노출.
  const chatDocument = useChatDocumentAttachment();
  const [docCapability, setDocCapability] =
    useState<ChatDocumentCapability | null>(null);
  useEffect(() => {
    let mounted = true;
    fetchChatAttachmentCapabilities()
      .then((response) => {
        if (mounted) setDocCapability(response.data?.document ?? null);
      })
      .catch(() => {
        if (mounted) setDocCapability(null); // capability 불가 — 문서 선택 자체 비노출(fail-closed)
      });
    return () => {
      mounted = false;
    };
  }, []);
  const attachmentFileInputRef = useRef<HTMLInputElement>(null);
  /** 마지막으로 말풍선에 표시한 첨부 id — 같은 파일로 이어지는 후속 턴엔 칩을 반복하지 않는다. */
  const announcedAttachmentIdRef = useRef<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  /** 시트 선택 되묻기가 떠 있는 동안의 후보 — 타이핑 선택도 로컬 selectedTableId에 반영(재리뷰 #5). */
  const pendingSelectionRef = useRef<{
    attachmentId: string;
    labels: string[];
  } | null>(null);
  /** 코드 · 콤보 칸 후보 — 채팅 번호 버튼이 떠 있는 동안. 고르면 AI 없이 그 코드로 다시 조회한다 (2026-10-02) */
  /** idCall — 이 후보를 낸 AI 답의 호출 id. 후보로 조회 · 메뉴 열기에 성공하면 이것으로 행동 신호를 남긴다 (2026-10-08) */
  /* refs — 그 답을 만든 질문에 실린 첨부. [조회하고 답하기] 가 같은 질문을 다시 보낼 때 같이 싣는다 (리뷰 2026-10-27) */
  const fieldPickRef = useRef<
    (ChatFieldPick & { idCall?: string; refs?: ChatAttachmentRefV2[] }) | null
  >(null);
  /* 지금 답을 기다리는 V2 질문의 첨부 — 답에 후보 버튼이 붙으면 그 버튼이 이어받는다 */
  const lastSentRefsRef = useRef<ChatAttachmentRefV2[] | undefined>(undefined);
  const [error, setError] = useState("");
  const [isSending, setIsSending] = useState(false);
  /** 기다린 초 — 답을 기다리는 동안 "분석 중… 12초" (2026-10-21). 토큰은 답이 끝나야 알아 시간만 */
  const [waitSec, setWaitSec] = useState(0);
  useEffect(() => {
    if (!isSending) return;
    setWaitSec(0);
    const started = Date.now();
    const timer = window.setInterval(
      () => setWaitSec(Math.floor((Date.now() - started) / 1000)),
      1000
    );
    return () => window.clearInterval(timer);
  }, [isSending]);
  /* 답변 중지 (aibot-v2-chat-cancel, 2026-10-14) — 진행 중인 V2 요청의 ID 와 "화면에서 중지했는가".
     중지한 요청의 답이 늦게 돌아오면 버린다. 전송 번호는 중지 뒤 새 전송이 시작됐을 때 옛 전송의 뒷정리가 isSending 을 끄지 못하게 한다 */
  const activeRequestRef = useRef<{
    idRequest: string;
    stopped: boolean;
  } | null>(null);
  const sendSeqRef = useRef(0);
  const [stopping, setStopping] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  // 메시지 목록 컨테이너 — 자동 스크롤(v0.8.1)에서 쓴다.
  const messagesRef = useRef<HTMLDivElement>(null);
  /* 답장 — 고른 AI 답 원문. 보내면 비운다 (2026-10-15) */
  const inputRef = useRef<HTMLTextAreaElement>(null);
  /** 답을 기다리는 동안 보낸 질문 — 앞 답이 끝나면 차례로 나간다 (2026-10-17) */
  /** screenOnly — 지금 화면으로 답하라는 표시를 실어 보낸다(화면 버튼이 줄 세운 질문, 2026-10-21) */
  const [queued, setQueued] = useState<
    {
      id: number;
      text: string;
      screenOnly?: boolean;
      refs?: ChatAttachmentRefV2[];
    }[]
  >([]);
  const queueIdRef = useRef(0);
  const [replyTo, setReplyTo] = useState<string | null>(null);
  /* 위쪽 메뉴(≡) — 대화 내용 > 대화 내보내기 (카톡 PC 처럼, 2026-10-15) */
  const [headerMenuEl, setHeaderMenuEl] = useState<HTMLElement | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  /* 대화 캡처 — 고른 말풍선 id(화면 순서), 저장 중이면 고르지 않은 것을 숨기고 목록을 펼친다 (카톡처럼, 2026-10-15) */
  const [captureOn, setCaptureOn] = useState(false);
  const [captureSel, setCaptureSel] = useState<number[]>([]);
  const [captureSaving, setCaptureSaving] = useState(false);
  /* 캡처를 복사 · 저장한 뒤 잠깐 보이는 안내 (3초) */
  const [captureNotice, setCaptureNotice] = useState("");
  /* 끌어서 고르기 — 누른 말풍선 · 누르기 전 선택 · 더하기/빼기 · 움직였는가 · 마지막 마우스 자리 */
  const captureDragRef = useRef<{
    anchor: number;
    base: number[];
    remove: boolean;
    moved: boolean;
    x: number;
    y: number;
    /** 마지막으로 넘어간 말풍선 — 같은 말풍선 안에서 움직이면 다시 고르지 않는다(대화 전체를 다시 그려 삐걱거렸다) */
    lastId: number;
    /** 누른 높이 — 조금(4px)이라도 끌면 그때부터 고른다 */
    y0: number;
  } | null>(null);
  /* 우클릭 메뉴 — 누른 자리 · 말풍선 · 그때 고른 글자 · 고른 말풍선 위인가 (카톡 PC 처럼, 2026-10-17) */
  const [msgMenu, setMsgMenu] = useState<{
    x: number;
    y: number;
    id: number;
    text: string;
    inSel: boolean;
  } | null>(null);
  /* 마우스 추적은 한 번만 붙이므로 캡처 모드 여부는 여기서 본다 */
  const captureOnRef = useRef(captureOn);
  captureOnRef.current = captureOn;
  const shellRef = useRef<HTMLDivElement>(null);

  // 활성 메뉴를 반응형으로 구독 — 메뉴 디자이너일 때만 좌측 작업 메뉴 노출
  const activeMenuCode = globalMenu((state) => state.activeMenuInfo?.cd_menu);
  const isMenuDesigner = (activeMenuCode || "").trim() === MENU_DESIGNER_CODE;

  // 사이드바 실행기(새 채팅) — 디자이너 탭이 열려 있을 때만 등록된다
  const designerActions = studioAiActions((state) => state.actions);

  /* ── V2 대화방 (aibot-v2-conversation-rooms, 2026-10-07) ──
     목록은 AI Center 엔진이고 디자이너 탭이 아닐 때만 보인다. 디자이너는 자기 사이드바를 그대로 쓴다.
     방을 고르면 대화만 바뀐다 — ERP 메뉴는 열지 않는다. 어느 메뉴에서든 이어서 물을 수 있다 */
  const roomSelectedId = chatRoomStore((state) => state.selectedId);
  /* 날짜 구분선 · 시간 — 말풍선 순서대로 (카톡처럼, 2026-10-15) */
  const timeline = useMemo(() => chatTimeline(messages), [messages]);
  /* 대화 내용 검색 (카톡 PC 🔍, 2026-10-22) — 검색줄 · 맞는 글자 칠하기 · ▲▼ 이동 */
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  /** 검색한 말 — Enter 로 정한다. 바뀌거나 말풍선이 늘면 다시 칠한다 */
  const [searchTerm, setSearchTerm] = useState("");
  const [searchCount, setSearchCount] = useState(0);
  const [searchIndex, setSearchIndex] = useState(-1);
  const [searchBusy, setSearchBusy] = useState(false);
  /** 태그로 찾기(대화방 🔍 의 🏷) — 고른 태그. 있으면 글자 대신 그 태그가 달린 말풍선을 칠한다 (2026-10-22) */
  const [searchTag, setSearchTag] = useState<ChatTag | null>(null);
  const searchRangesRef = useRef<Range[]>([]);
  const searchIndexRef = useRef(-1);
  /** 새로 찾았으면 가장 최근 결과로 간다 */
  const searchFreshRef = useRef(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  /* 방을 바꾸면 답장 고른 것을 버린다 — 다른 방의 답에 답장하게 된다 */
  useEffect(() => {
    setReplyTo(null);
    /* 캡처 중이던 것도 끝낸다 — 다른 방의 말풍선이 섞인다 */
    setCaptureOn(false);
    setCaptureSel([]);
    /* 줄 세운 질문도 버린다 — 다른 방으로 나간다 */
    setQueued([]);
    /* 대화 검색도 닫는다 — 다른 방의 결과가 남는다 (2026-10-22) */
    setSearchOpen(false);
    setSearchTerm("");
    setSearchTag(null);
  }, [roomSelectedId]);
  const showRooms = engine === "V2" && !isMenuDesigner;
  /** 목록 패널 열림. 헤더 버튼으로 여닫는다. 좁은 창에서는 대화창 위에 겹치고, 넓으면 옆에 붙는다 */
  const [roomsOpen, setRoomsOpen] = useState<boolean>(() => {
    try {
      return localStorage.getItem(ROOMS_OPEN_KEY) === "Y";
    } catch {
      return false;
    }
  });
  /* 펼침 · 접힘은 다음에 열 때도 그대로 — 이 브라우저에만 남는다 (2026-10-16) */
  useEffect(() => {
    try {
      localStorage.setItem(ROOMS_OPEN_KEY, roomsOpen ? "Y" : "N");
    } catch {
      /* 저장소를 못 쓰는 브라우저 — 기억만 못 한다 */
    }
  }, [roomsOpen]);
  /** 대화 목록 폭 — 경계선을 끌어 바꾸고, 놓으면 기억한다 (2026-10-21) */
  const [roomsWidth, setRoomsWidth] = useState<number>(() => {
    try {
      const saved = Number(localStorage.getItem(ROOMS_WIDTH_KEY));
      return saved >= ROOMS_MIN_WIDTH ? saved : 232;
    } catch {
      return 232;
    }
  });
  const handleRoomsResize = (width: number, done: boolean) => {
    setRoomsWidth(width);
    if (!done) return;
    try {
      localStorage.setItem(ROOMS_WIDTH_KEY, String(width));
    } catch {
      /* 저장소를 못 쓰는 브라우저 — 기억만 못 한다 */
    }
  };
  /** 대화창 폭이 좁은가 — 760px 미만이면 목록을 옆에 붙이지 않고 겹친다 (헤더 제목이 줄바꿈되던 문제, 2026-10-07) */
  const [isNarrow, setIsNarrow] = useState(true);
  const pageRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const el = pageRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const measure = () => setIsNarrow(el.clientWidth < 760);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  /* 보내는 동안 입력칸을 잠가 포커스가 빠진다 — 답이 끝나면 입력칸으로 되돌린다.
     그사이 사람이 업무 화면 같은 다른 곳을 눌렀으면 뺏지 않는다 (2026-10-16) */
  const wasBusyRef = useRef(false);
  const busyRef = useRef(false);
  /** 답을 기다리는 동안 사람이 챗봇 밖을 눌렀는가 — 화면이 스스로 옮긴 포커스(조회 뒤 그리드 첫 줄)와 가른다 (2026-10-21).
      떠 있는 창은 덮는 서랍과 달리 포커스를 붙잡지 않아, 조회가 그리드로 옮긴 포커스를 사람이 누른 것으로 봤다 */
  const leftChatRef = useRef(false);
  /** 답이 끝난 뒤 사람이 움직일 때까지(챗봇 밖 누름 · Tab) 화면이 옮기는 포커스를 되찾는다 (2026-10-22).
      시간(1.5초)으로 끊었더니 새로 연 메뉴가 화면을 다 그린 뒤 늦게 가져간 포커스를 못 되찾았다 */
  const guardOnRef = useRef(false);
  useEffect(() => {
    const busy = isSending || isClearing;
    busyRef.current = busy;
    if (busy && !wasBusyRef.current) {
      leftChatRef.current = false;
    }
    if (wasBusyRef.current && !busy) {
      const active = document.activeElement;
      const page = pageRef.current;
      /* 사람이 밖을 누르지 않았으면 화면이 포커스를 어디로 옮겼든 되돌린다.
         눌렀어도 포커스가 body · 챗봇 안 · 챗봇을 감싼 곳에 있으면 되돌린다 */
      const lost =
        !leftChatRef.current ||
        !active ||
        active === document.body ||
        (page ? page.contains(active) || active.contains(page) : false);
      if (lost) {
        guardOnRef.current = true;
        requestAnimationFrame(() => inputRef.current?.focus());
      }
    }
    wasBusyRef.current = busy;
  }, [isSending, isClearing]);
  /* 사람의 누름과 프로그램의 포커스 이동을 가른다 (2026-10-21).
     누름 — 챗봇 밖을 누르면 그때부터 뺏지 않는다. 챗봇이 띄운 메뉴(우클릭 등)는 챗봇 안으로 친다.
     포커스 이동 — 답이 끝난 뒤 사람이 움직이지 않았는데 챗봇 밖으로 나가면 화면이 옮긴 것이다. 입력칸으로 되찾는다.
     확인창 · 도움창 같은 창이 가져간 포커스는 둔다 — 사람이 답해야 하는 창이다 */
  useEffect(() => {
    const outside = (target: Element | null) =>
      !!target && !pageRef.current?.contains(target);
    const onDown = (e: PointerEvent) => {
      const target = e.target as Element | null;
      if (outside(target) && !target!.closest?.(".MuiPopover-root")) {
        leftChatRef.current = true;
        guardOnRef.current = false;
      }
    };
    /* Tab 으로 빠져나가는 것도 사람이 움직인 것이다 */
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Tab") {
        leftChatRef.current = true;
        guardOnRef.current = false;
      }
    };
    const onFocusIn = (e: FocusEvent) => {
      if (busyRef.current || leftChatRef.current || !guardOnRef.current) return;
      const target = e.target as Element | null;
      if (
        !outside(target) ||
        target!.closest?.(".MuiModal-root, .MuiPopover-root, [role='dialog']")
      )
        return;
      requestAnimationFrame(() => inputRef.current?.focus());
    };
    document.addEventListener("pointerdown", onDown, true);
    document.addEventListener("keydown", onKey, true);
    document.addEventListener("focusin", onFocusIn, true);
    return () => {
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("keydown", onKey, true);
      document.removeEventListener("focusin", onFocusIn, true);
    };
  }, []);
  /* 대화 목록은 펼치면 늘 대화창 옆에 붙는다 — 카톡처럼 펼치면 붙고 접으면 사라진다. 좁은 창에서 떠서 겹치던 것 · 📌 고정은 없앴다
     (사용자 2026-10-16 "붙여 보자"). 좁으면 창을 넓히거나 목록을 접는다 */
  const roomsDocked = showRooms && roomsOpen;
  const [roomView, setRoomView] = useState<RoomView>("active");
  /** 비동기 작업이 끝났을 때 "지금" 보기를 보려고 — 클로저의 roomView 는 클릭 때 값이다 */
  const roomViewRef = useRef<RoomView>("active");
  roomViewRef.current = roomView;
  const [roomQuery, setRoomQuery] = useState("");
  const [rooms, setRooms] = useState<ChatRoom[]>([]);
  const [roomsLoading, setRoomsLoading] = useState(false);
  const [roomsError, setRoomsError] = useState<string | null>(null);
  /** 지금 열어 둔 방의 메타. 머리에 "시작 메뉴" 로 보인다 */
  const [activeRoom, setActiveRoom] = useState<ChatRoom | null>(null);
  /** 복원한 대화의 페이징. hasMore 면 맨 위에 "이전 대화 더 보기" */
  const [history, setHistory] = useState<{
    oldestSeq: number;
    hasMore: boolean;
  } | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  /** 방을 열고 첫 이력이 아직 안 왔다. 이 동안은 전송을 막는다 (이전 대화 더 보기 로딩은 막지 않는다) */
  const [isRoomLoading, setIsRoomLoading] = useState(false);
  /** 목록 응답 순서 방어 — 늦게 온 옛 목록이 새 목록을 덮지 않게 */
  const roomListSeq = useRef(0);
  /** 옛 턴을 위에 끼울 때 읽던 자리를 지키기 위한 높이 */
  const prependAnchorRef = useRef<number | null>(null);

  const roomKey = () => {
    const info = global.getState().userInfo;
    return roomStorageKey(info?.cd_firm, info?.cd_user);
  };

  const menuName = (cdMenu: string | null) => {
    if (!cdMenu) return "";
    const found = useMenuStore
      .getState()
      .menuItems.find((m) => m.cd_menu === cdMenu);
    return found?.nm_menu || cdMenu;
  };
  /** 대화 목록 아이콘 글자 — 시작 메뉴의 맨 위 묶음(구매관리 → "구매"). 최근사용 · 즐겨찾기 복사본은 건너뛴다. 모르면 null (2026-10-16) */
  const menuGroup = (cdMenu: string | null): string | null => {
    if (!cdMenu) return null;
    const items = useMenuStore.getState().menuItems || [];
    const shadow = new Set(["SN_REC", "SN_FAV"]);
    const byCode = (code: string) =>
      items.find(
        (m) => m.cd_menu === code && !shadow.has(m.cd_menu_parent || "")
      ) || items.find((m) => m.cd_menu === code);
    let cur = byCode(cdMenu);
    for (let guard = 0; cur?.cd_menu_parent && guard < 10; guard += 1) {
      const parent = byCode(cur.cd_menu_parent);
      if (!parent) break;
      cur = parent;
    }
    const root = (cur?.nm_menu || "").trim().replace(/관리$/, "");
    return root ? root.slice(0, 2) : null;
  };

  /** 방 선택을 비운다. 메시지는 호출자가 정한다. keepRemembered 면 저장소의 마지막 방 id 는 둔다 */
  const resetRoomSelection = (keepRemembered = false) => {
    chatRoomStore.getState().newRoom();
    if (!keepRemembered) forgetRoom(roomKey());
    setActiveRoom(null);
    setHistory(null);
    setIsRoomLoading(false);
    prependAnchorRef.current = null;
  };

  /** 목록을 전부 읽는다. 제목 검색은 받은 목록에서 거른다 — 글자마다 서버에 묻지 않는다 (2026-10-08) */
  const loadRooms = async () => {
    const seq = ++roomListSeq.current;
    setRoomsLoading(true);
    setRoomsError(null);
    try {
      /* 지금 보기를 읽는다 — 늦게 불린 콜백이 클릭 당시 보기를 들고 오면 엉뚱한 목록을 채운다 (2026-10-08 리뷰) */
      const viewNow = roomViewRef.current;
      const all = await listChatRooms(viewNow);
      if (seq !== roomListSeq.current || viewNow !== roomViewRef.current)
        return; // 그 사이 보기를 바꿔 다시 읽었다
      setRooms(all);
    } catch (err) {
      if (seq !== roomListSeq.current) return;
      setRoomsError(
        err instanceof ApiError
          ? err.message
          : "대화 목록을 불러오지 못했습니다."
      );
    } finally {
      if (seq === roomListSeq.current) setRoomsLoading(false);
    }
  };

  /** 방을 연다 — 대화만 바꾼다. 메뉴는 열지 않는다. 늦게 온 결과는 버린다 */
  const openRoom = async (room: ChatRoom) => {
    chatRoomStore.getState().select(room.idConv);
    const snap = chatRoomStore.getState().snapshot();
    rememberRoom(roomKey(), room.idConv);
    setActiveRoom(room);
    setHistory(null);
    setError("");
    setInput("");
    setAttachments([]);
    fieldPickRef.current = null;
    pendingSelectionRef.current = null;
    prependAnchorRef.current = null;
    setHistoryLoading(true);
    setIsRoomLoading(true);
    try {
      const page = await fetchChatRoomTurns(room.idConv, { limit: 50 });
      if (!chatRoomStore.getState().isCurrent(snap)) return;
      const items = turnsToHistory(page.items);
      let id = 1;
      const restored: ChatMessage[] = items.map((it) => ({
        id: id++,
        role: it.role,
        text: it.text,
        chart: it.chart,
        table: it.table,
        isError: it.error,
        quote: it.quote,
        attachments: restoredAttachmentMeta(it.attachments),
        ...(it.sentAttachments ? { sentAttachments: it.sentAttachments } : {}),
        at: it.at,
        point: it.point,
        turnSeq: it.turnSeq,
        tags: it.tags,
        /* 답한 역할 배지 — 저장된 역할, 없으면 AI Center (2026-10-22) */
        ...(it.role === "assistant"
          ? { agent: agentOfRole(it.answerRole) }
          : {}),
      }));
      /* 마지막 답은 "다시 시도" 할 수 있다 — 호출 id 는 안 내려오지만 서버에 턴이 있다 (2026-10-14) */
      const last = restored[restored.length - 1];
      if (
        last &&
        last.role === "assistant" &&
        restored.length >= 2 &&
        restored[restored.length - 2].role === "user"
      ) {
        last.retryable = true;
      }
      if (page.hasLegacy) {
        restored.unshift({
          id: id++,
          role: "assistant",
          text: LEGACY_ROOM_NOTICE,
        });
      }
      setMessages(restored);
      nextId.current = id;
      setHistory({ oldestSeq: page.oldestSeq, hasMore: page.hasMore });
    } catch (err) {
      if (!chatRoomStore.getState().isCurrent(snap)) return;
      setMessages(initialMessages);
      nextId.current = 2;
      setError(
        err instanceof ApiError
          ? err.message
          : "대화를 불러오지 못했습니다. 목록을 새로고침해 주세요."
      );
      resetRoomSelection();
      void loadRooms();
    } finally {
      if (chatRoomStore.getState().isCurrent(snap)) {
        setHistoryLoading(false);
        setIsRoomLoading(false);
      }
    }
  };

  /** 옛 턴을 위에 끼운다. 읽던 자리는 그대로 */
  const loadOlderTurns = async () => {
    if (!history?.hasMore || historyLoading || !roomSelectedId) return;
    const snap = chatRoomStore.getState().snapshot();
    setHistoryLoading(true);
    try {
      const page = await fetchChatRoomTurns(roomSelectedId, {
        beforeSeq: history.oldestSeq,
        limit: 50,
      });
      if (!chatRoomStore.getState().isCurrent(snap)) return;
      const items = turnsToHistory(page.items);
      const container = messagesRef.current;
      prependAnchorRef.current = container
        ? container.scrollHeight - container.scrollTop
        : null;
      setMessages((prev) => {
        /* 옛 턴은 음수 id — 기존 번호와 겹치지 않는다 */
        let id = -(prev.length + items.length + 1);
        const older: ChatMessage[] = items.map((it) => ({
          id: id++,
          role: it.role,
          text: it.text,
          chart: it.chart,
          table: it.table,
          isError: it.error,
          quote: it.quote,
          attachments: restoredAttachmentMeta(it.attachments),
          ...(it.sentAttachments
            ? { sentAttachments: it.sentAttachments }
            : {}),
          at: it.at,
          point: it.point,
          turnSeq: it.turnSeq,
          tags: it.tags,
          ...(it.role === "assistant"
            ? { agent: agentOfRole(it.answerRole) }
            : {}),
        }));
        /* 안내 한 줄(LEGACY) 이 맨 위에 있으면 그 아래에 끼운다 */
        const noticeFirst =
          prev.length > 0 && prev[0].text === LEGACY_ROOM_NOTICE;
        return noticeFirst
          ? [prev[0], ...older, ...prev.slice(1)]
          : [...older, ...prev];
      });
      setHistory({ oldestSeq: page.oldestSeq, hasMore: page.hasMore });
    } catch {
      if (chatRoomStore.getState().isCurrent(snap))
        setError("이전 대화를 더 불러오지 못했습니다.");
    } finally {
      if (chatRoomStore.getState().isCurrent(snap)) setHistoryLoading(false);
    }
  };

  /** 검색 결과가 있는 턴(target)까지 옛 턴을 한 번에 불러온다 (2026-10-22) */
  const loadOlderUntil = async (target: number) => {
    if (!roomSelectedId || !history?.hasMore || history.oldestSeq <= target)
      return;
    const snap = chatRoomStore.getState().snapshot();
    let before = history.oldestSeq;
    let more: boolean = history.hasMore;
    const items: ReturnType<typeof turnsToHistory> = [];
    setHistoryLoading(true);
    try {
      /* 끝없이 돌지 않게 100쪽(5,000턴)에서 멈춘다 — 대화 내보내기와 같다 */
      for (let i = 0; i < 100 && more && before > target; i += 1) {
        const page = await fetchChatRoomTurns(roomSelectedId, {
          beforeSeq: before,
          limit: 50,
        });
        if (!chatRoomStore.getState().isCurrent(snap)) return;
        items.unshift(...turnsToHistory(page.items));
        before = page.oldestSeq;
        more = page.hasMore;
        if (page.items.length === 0) break;
      }
      if (items.length === 0) return;
      setMessages((prev) => {
        let id = -(prev.length + items.length + 1);
        const older: ChatMessage[] = items.map((it) => ({
          id: id++,
          role: it.role,
          text: it.text,
          chart: it.chart,
          table: it.table,
          isError: it.error,
          quote: it.quote,
          attachments: restoredAttachmentMeta(it.attachments),
          ...(it.sentAttachments
            ? { sentAttachments: it.sentAttachments }
            : {}),
          at: it.at,
          point: it.point,
          turnSeq: it.turnSeq,
          tags: it.tags,
          ...(it.role === "assistant"
            ? { agent: agentOfRole(it.answerRole) }
            : {}),
        }));
        const noticeFirst =
          prev.length > 0 && prev[0].text === LEGACY_ROOM_NOTICE;
        return noticeFirst
          ? [prev[0], ...older, ...prev.slice(1)]
          : [...older, ...prev];
      });
      setHistory({ oldestSeq: before, hasMore: more });
    } finally {
      if (chatRoomStore.getState().isCurrent(snap)) setHistoryLoading(false);
    }
  };

  /** 말풍선 글에서 검색어 자리를 찾는다 — 버튼 · 평가 줄 · 배지 글은 빼고, 위(오래된 것)부터 */
  const collectSearchRanges = (term: string): Range[] => {
    const box = messagesRef.current;
    if (!box || !term) return [];
    const want = term.toLowerCase();
    const out: Range[] = [];
    box
      .querySelectorAll<HTMLElement>(
        ".chatbot-message[data-msg-id] .chatbot-message-content"
      )
      .forEach((content) => {
        const walker = document.createTreeWalker(
          content,
          NodeFilter.SHOW_TEXT,
          {
            acceptNode: (node) =>
              node.parentElement?.closest(
                "button, .chatbot-feedback, .chatbot-agent-badge, .chatbot-message-failed, .chatbot-selection-options"
              )
                ? NodeFilter.FILTER_REJECT
                : NodeFilter.FILTER_ACCEPT,
          }
        );
        for (let node = walker.nextNode(); node; node = walker.nextNode()) {
          const text = (node.nodeValue || "").toLowerCase();
          let at = text.indexOf(want);
          while (at >= 0) {
            const range = document.createRange();
            range.setStart(node, at);
            range.setEnd(node, at + want.length);
            out.push(range);
            at = text.indexOf(want, at + want.length);
          }
        }
      });
    return out;
  };

  /** 칠하기 — 글자를 바꾸지 않고 브라우저 강조(CSS Highlight)로. 지원하지 않는 브라우저면 이동만 된다 */
  const paintSearch = (ranges: Range[], index: number) => {
    const highlights = (
      globalThis as { CSS?: { highlights?: Map<string, unknown> } }
    ).CSS?.highlights;
    const HighlightCtor = (
      globalThis as { Highlight?: new (...r: Range[]) => unknown }
    ).Highlight;
    if (!highlights || !HighlightCtor) return;
    highlights.set(
      "chat-search",
      new HighlightCtor(...ranges.filter((_, i) => i !== index))
    );
    if (index >= 0 && ranges[index])
      highlights.set("chat-search-current", new HighlightCtor(ranges[index]));
    else highlights.delete("chat-search-current");
  };
  const clearSearchPaint = () => {
    const highlights = (
      globalThis as { CSS?: { highlights?: Map<string, unknown> } }
    ).CSS?.highlights;
    highlights?.delete("chat-search");
    highlights?.delete("chat-search-current");
  };

  /** 결과로 옮긴다 — 화면 가운데로 */
  const showSearchHit = (index: number) => {
    searchIndexRef.current = index;
    setSearchIndex(index);
    paintSearch(searchRangesRef.current, index);
    searchRangesRef.current[
      index
    ]?.startContainer.parentElement?.scrollIntoView({ block: "center" });
  };

  /** ▲(-1) 오래된 쪽 · ▼(+1) 최근 쪽 — 끝에서는 반대 끝으로 돈다 */
  const moveSearch = (delta: number) => {
    const count = searchRangesRef.current.length;
    if (count === 0) return;
    showSearchHit((searchIndexRef.current + delta + count) % count);
  };

  /** 찾기 — 안 불러온 옛 턴에 있으면 거기까지 불러온 뒤 칠한다. 같은 말로 다시 Enter 면 이전(오래된) 결과로 */
  const runSearch = async () => {
    const term = searchText.trim();
    if (!term || searchBusy) return;
    if (term === searchTerm && !searchTag) {
      moveSearch(-1);
      return;
    }
    setSearchTag(null);
    setSearchBusy(true);
    try {
      if (roomSelectedId && history?.hasMore) {
        const seqs = await searchChatRoom(roomSelectedId, term);
        if (seqs.length > 0) await loadOlderUntil(Math.min(...seqs));
      }
    } catch {
      /* 서버 검색을 못 하면 불러온 대화에서만 찾는다 */
    } finally {
      setSearchBusy(false);
    }
    searchFreshRef.current = true;
    setSearchTerm(term);
  };

  const closeSearch = () => {
    setSearchOpen(false);
    setSearchTerm("");
    setSearchText("");
    setSearchTag(null);
  };

  /** 태그 찾기 — 그 태그가 달린 말풍선 글 전체를 칠한다 */
  const collectTagRanges = (noTag: number): Range[] => {
    const box = messagesRef.current;
    if (!box) return [];
    const out: Range[] = [];
    for (const m of messages) {
      if (!m.tags?.includes(noTag)) continue;
      const content = box.querySelector(
        `[data-msg-id="${m.id}"] .chatbot-message-content`
      );
      if (!content) continue;
      const range = document.createRange();
      range.selectNodeContents(content);
      out.push(range);
    }
    return out;
  };

  /* 검색어가 정해지거나 말풍선이 바뀌면 다시 찾아 칠한다. 새로 찾았으면 가장 최근 결과로 */
  useEffect(() => {
    if (!searchOpen || (!searchTerm && !searchTag)) {
      clearSearchPaint();
      searchRangesRef.current = [];
      searchIndexRef.current = -1;
      setSearchCount(0);
      setSearchIndex(-1);
      return;
    }
    const ranges = searchTag
      ? collectTagRanges(searchTag.noTag)
      : collectSearchRanges(searchTerm);
    searchRangesRef.current = ranges;
    setSearchCount(ranges.length);
    if (searchFreshRef.current) {
      searchFreshRef.current = false;
      if (ranges.length > 0) showSearchHit(ranges.length - 1);
      else {
        searchIndexRef.current = -1;
        setSearchIndex(-1);
        clearSearchPaint();
      }
      return;
    }
    const keep = Math.min(searchIndexRef.current, ranges.length - 1);
    searchIndexRef.current = keep;
    setSearchIndex(keep);
    paintSearch(ranges, keep);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchOpen, searchTerm, searchTag, messages]);

  /* ------------------------------------------------------------------ 말풍선 태그 (카톡 나와의 채팅 태그처럼, 2026-10-22)
     태그 = 이름 ↔ 아이콘(사용자마다). 말풍선 우클릭 [태그]로 달고 떼고, 대화방 🔍 의 🏷 는 이 방, 대화 목록 🏷 는 모든 방에서 찾는다 */
  const [myTags, setMyTags] = useState<ChatTag[]>([]);
  /** 태그 테이블이 없는 DB 면 태그 메뉴 · 버튼을 숨긴다 */
  const [tagsReady, setTagsReady] = useState(false);
  const loadTags = async () => {
    try {
      setMyTags(await fetchChatTags());
      setTagsReady(true);
    } catch {
      setTagsReady(false);
    }
  };
  useEffect(() => {
    if (showRooms) void loadTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showRooms]);
  /** 태그 판 — apply 말풍선에 달기, pick 이 방에서 찾기, filter 모든 방에서 모아 보기 */
  const [tagPalette, setTagPalette] = useState<{
    x: number;
    y: number;
    mode: "apply" | "pick" | "filter";
    msgId?: number;
  } | null>(null);
  const [tagFilter, setTagFilter] = useState<ChatTag | null>(null);
  const [tagHits, setTagHits] = useState<ChatTagHit[] | null>(null);
  const [tagHitsLoading, setTagHitsLoading] = useState(false);
  /** 모아 보기에서 누른 말풍선 — 그 방이 열리고 그 턴이 불러와지면 그리로 옮긴다 */
  const [pendingJump, setPendingJump] = useState<{
    idConv: string;
    seq: number;
    answer: boolean;
  } | null>(null);

  const openTagPalette = (anchor: HTMLElement, mode: "pick" | "filter") => {
    const rect = anchor.getBoundingClientRect();
    setTagPalette({ x: rect.left, y: rect.bottom + 4, mode });
  };

  const createTag = async (cdIcon: string, nmTag: string) => {
    try {
      const made = await createChatTag(cdIcon, nmTag);
      await loadTags();
      return made;
    } catch {
      setError("태그를 만들지 못했습니다.");
      return null;
    }
  };
  const updateTag = async (noTag: number, cdIcon: string, nmTag: string) => {
    try {
      await updateChatTag(noTag, cdIcon, nmTag);
      await loadTags();
      return true;
    } catch {
      setError("태그를 고치지 못했습니다.");
      return false;
    }
  };
  const hideTag = async (noTag: number) => {
    try {
      await hideChatTag(noTag);
      await loadTags();
      return true;
    } catch {
      setError("태그를 숨기지 못했습니다.");
      return false;
    }
  };

  /** 말풍선에 달기 · 떼기 — 이미 달려 있으면 뗀다 */
  const applyTag = async (tag: ChatTag, msgId?: number) => {
    const msg = messages.find((m) => m.id === msgId);
    if (!msg || msg.turnSeq == null || !roomSelectedId) return;
    try {
      const now = await toggleBubbleTag(
        roomSelectedId,
        msg.turnSeq,
        msg.role === "assistant",
        tag.noTag
      );
      setMessages((prev) =>
        prev.map((m) => (m.id === msg.id ? { ...m, tags: now } : m))
      );
      pushRecentTag(tag.noTag);
    } catch {
      setError("태그를 달지 못했습니다.");
    }
  };

  /** 대화방 🔍 의 🏷 — 이 방에서 그 태그가 달린 말풍선. 안 불러온 옛 턴이면 거기까지 불러온다 */
  const runTagSearch = async (tag: ChatTag) => {
    setSearchText("");
    setSearchBusy(true);
    try {
      if (roomSelectedId && history?.hasMore) {
        const seqs = await searchChatRoomByTag(roomSelectedId, tag.noTag);
        if (seqs.length > 0) await loadOlderUntil(Math.min(...seqs));
      }
    } catch {
      /* 서버에서 못 찾으면 불러온 대화에서만 */
    } finally {
      setSearchBusy(false);
    }
    pushRecentTag(tag.noTag);
    searchFreshRef.current = true;
    setSearchTerm("");
    setSearchTag(tag);
  };

  /** 대화 목록 🏷 — 모든 방에서 모아 보기 */
  const pickTagFilter = async (tag: ChatTag) => {
    setTagFilter(tag);
    setTagHits(null);
    setTagHitsLoading(true);
    pushRecentTag(tag.noTag);
    try {
      setTagHits(await fetchTagHits(tag.noTag));
    } catch {
      setTagHits([]);
    } finally {
      setTagHitsLoading(false);
    }
  };

  const openTagHit = async (hit: ChatTagHit) => {
    setPendingJump({ idConv: hit.idConv, seq: hit.noSeq, answer: hit.answer });
    if (roomSelectedId === hit.idConv) return;
    const room =
      rooms.find((r) => r.idConv === hit.idConv) ??
      (await fetchChatRoom(hit.idConv).catch(() => null));
    if (room) await openRoom(room);
    else setPendingJump(null);
  };

  /* 모아 보기에서 누른 말풍선으로 옮긴다 — 방이 열리고 그 턴까지 불러온 뒤, 잠깐 표시 */
  useEffect(() => {
    const jump = pendingJump;
    if (
      !jump ||
      roomSelectedId !== jump.idConv ||
      isRoomLoading ||
      historyLoading
    )
      return;
    const found = messages.find(
      (m) => m.turnSeq === jump.seq && (m.role === "assistant") === jump.answer
    );
    if (!found) {
      if (history?.hasMore && history.oldestSeq > jump.seq) {
        void loadOlderUntil(jump.seq);
        return;
      }
      setPendingJump(null);
      return;
    }
    setPendingJump(null);
    /* 방을 열면 맨 아래로 내려가는 것보다 뒤에 옮긴다 */
    window.setTimeout(() => {
      const el = messagesRef.current?.querySelector<HTMLElement>(
        `[data-msg-id="${found.id}"]`
      );
      if (!el) return;
      el.scrollIntoView({ block: "center" });
      el.classList.add("is-jump");
      window.setTimeout(() => el.classList.remove("is-jump"), 2000);
    }, 150);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pendingJump,
    roomSelectedId,
    isRoomLoading,
    historyLoading,
    messages,
    history,
  ]);

  /** 새 대화. V1 서버 맥락은 건드리지 않는다 */
  const startNewRoom = () => {
    resetRoomSelection();
    setMessages(initialMessages);
    nextId.current = 2;
    setInput("");
    setAttachments([]);
    setError("");
    fieldPickRef.current = null;
  };

  const handleRoomRename = async (room: ChatRoom, title: string) => {
    try {
      const updated = await renameChatRoom(room.idConv, title);
      /* 지금 열린 방과 비교한다 — 기다리는 사이 다른 방을 열었으면 그 머리를 덮지 않는다 (2026-10-08 리뷰) */
      if (updated && chatRoomStore.getState().selectedId === updated.idConv)
        setActiveRoom(updated);
      void loadRooms();
    } catch (err) {
      setRoomsError(
        err instanceof ApiError ? err.message : "제목을 바꾸지 못했습니다."
      );
    }
  };

  const handleRoomHide = async (room: ChatRoom) => {
    try {
      await hideChatRoom(room.idConv);
      /* 클릭 때가 아니라 지금 선택을 본다 — 기다리는 사이 다른 방을 골랐으면 그 방을 지우지 않는다 */
      if (chatRoomStore.getState().selectedId === room.idConv) startNewRoom();
      void loadRooms();
    } catch (err) {
      setRoomsError(
        err instanceof ApiError ? err.message : "대화를 숨기지 못했습니다."
      );
    }
  };

  const handleRoomRestore = async (room: ChatRoom) => {
    try {
      const snap = chatRoomStore.getState().snapshot();
      const viewAtClick = roomViewRef.current;
      const restored = await restoreChatRoom(room.idConv);
      /* 기다리는 사이 사용자가 보기를 바꿨으면 그대로 둔다 — 강제로 "대화" 보기로 옮기지 않는다 (2026-10-08 리뷰) */
      if (roomViewRef.current === viewAtClick) {
        setRoomView("active");
      } else {
        void loadRooms();
      }
      /* 기다리는 사이 다른 방을 골랐으면 그 선택을 덮지 않는다 — 되살린 방은 목록에만 돌아온다 */
      if (restored && chatRoomStore.getState().isCurrent(snap))
        void openRoom(restored);
    } catch (err) {
      setRoomsError(
        err instanceof ApiError ? err.message : "되살리지 못했습니다."
      );
      void loadRooms();
    }
  };

  /** 같은 탭 새로고침 — 마지막 방을 서버에 다시 확인한 뒤 연다. 아니면 조용히 잊는다 */
  const restoreRememberedRoom = async () => {
    const key = roomKey();
    const remembered = recallRoom(key);
    if (!remembered || chatRoomStore.getState().selectedId) return;
    /* 확인을 기다리는 사이 사용자가 다른 방을 고르거나 새 대화를 시작하면 그쪽이 이긴다 (2026-10-08).
       확인하는 동안은 전송을 막는다 — 보낸 뒤 복원이 끝나 방이 바뀌면 그 답이 화면에서 버려진다 (2차 리뷰) */
    const snap = chatRoomStore.getState().snapshot();
    setIsRoomLoading(true);
    try {
      const room = await fetchChatRoom(remembered);
      if (!chatRoomStore.getState().isCurrent(snap)) return;
      if (room && room.state === "ACTIVE") {
        void openRoom(room);
        return; // openRoom 이 로딩 표시를 이어받는다
      }
      forgetRoom(key);
    } catch {
      if (chatRoomStore.getState().isCurrent(snap)) forgetRoom(key);
    }
    if (chatRoomStore.getState().isCurrent(snap)) setIsRoomLoading(false);
  };

  /* 목록이 보일 때, 보기(대화/숨긴 대화)가 바뀔 때 전부 다시 읽는다. 검색어는 서버에 가지 않는다 */
  useEffect(() => {
    if (!showRooms) return;
    setRooms([]);
    void loadRooms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showRooms, roomView]);

  /** 검색어로 거른 목록 — 입력 즉시 */
  const visibleRooms = filterRoomsByTitle(rooms, roomQuery);

  /* AI Center 엔진으로 들어오면 마지막 방을 되살린다 */
  useEffect(() => {
    if (!showRooms) return;
    void restoreRememberedRoom();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showRooms]);

  useEffect(() => {
    const container = messagesRef.current;
    if (!container) return;
    const anchor = prependAnchorRef.current;
    if (anchor !== null) {
      /* 옛 턴을 위에 끼웠다 — 읽던 자리를 지킨다 (2026-10-07) */
      container.scrollTop = container.scrollHeight - anchor;
      prependAnchorRef.current = null;
      return;
    }
    container.scrollTop = container.scrollHeight;
  }, [messages, isSending]);

  /** 채팅 클리어(v0.8.2) — 화면 대화 + 서버 멀티턴 버퍼를 함께 초기화(맥락 오염 방지). */
  const handleClearChat = async () => {
    if (isClearing) return;
    setIsClearing(true);
    setMessages(initialMessages);
    nextId.current = 2;
    setAttachments([]);
    chatAttachment.clearLocal(); // 서버 첨부 상태는 clearChatContext가 함께 정리(§46)
    chatDocument.clearLocal();
    announcedAttachmentIdRef.current = null;
    setInput("");
    setError("");
    /* 새 엔진은 대화방 id 로 맥락을 잇는다. 끈을 끊어야 새 대화가 된다.
       아래 clearChatContext 는 기존 엔진 경로 그대로 둔다. 분기를 넣으면
       기존 동작이 조건에 따라 달라진다 */
    resetRoomSelection();
    fieldPickRef.current = null;
    try {
      await clearChatContext();
    } catch {
      // 서버 버퍼는 TTL(3분)로도 소멸하지만, 그 사이 후속 질문이 안 보이는 맥락에
      // 이어붙을 수 있다 — 조용히 삼키지 않고 알린다.
      setError(
        "대화 기록 초기화가 서버에 완전히 반영되지 않았습니다. 잠시 후 다시 시도해 주세요."
      );
    } finally {
      setIsClearing(false);
    }
  };

  /**
   * 대화 압축. 답을 화면에 보여준 뒤에 부른다.
   *
   * 같이 돌리면 사용자가 그만큼 더 기다린다. 압축도 LLM 을 부르는 일이라 시간이 걸린다.
   * 그래서 답을 먼저 띄우고 뒤에서 조용히 돌린다.
   *
   * 실패해도 아무것도 하지 않는다. 대화는 그대로 이어지고 다음 질문에서 다시 시도된다.
   * 여기서 오류를 띄우면 답을 이미 받은 사용자가 무슨 일인지 모른다.
   */
  const runCompact = async () => {
    /* 어느 방을 압축하는지 지금 잡아 둔다. 돌아왔을 때 다른 방이면 안내도 붙이지 않는다 (2026-10-07) */
    const snap = chatRoomStore.getState().snapshot();
    try {
      const result = await compactChatV2(snap.idConv, false);
      if (
        result?.done &&
        result.notice &&
        chatRoomStore.getState().isCurrent(snap)
      ) {
        // 조용히 압축하면 나중에 "왜 앞의 얘기를 잊었지" 가 된다
        appendMessage("assistant", result.notice);
      }
    } catch {
      // 삼킨다. 다음 질문에서 다시 시도된다
    }
  };

  const appendMessage = (
    role: ChatMessage["role"],
    text: string,
    chart?: ChatChartPayload,
    table?: ChatTablePayload,
    noChat?: string,
    agent?: ChatAgentInfo,
    attachmentsMeta?: MessageAttachmentMeta[],
    sourceKind?: string,
    selectionOptions?: { label: string; optionId: string; kind?: string }[],
    selectionAttachmentId?: string,
    documentInfo?: {
      meta?: ChatDocumentAnswerMeta;
      candidates?: DocumentCandidateOption[];
      questionId?: string;
      attachmentId?: string;
    },
    /** v2 답변의 호출 id. v1 은 넘기지 않는다 */
    idCall?: string,
    /** 답장한 앞 답 — 내 말풍선 위에 보인다 */
    quote?: string,
    /** 보낸 첨부 참조 — 다시 시도용 (2026-10-26) */
    sentAttachments?: ChatAttachmentRefV2[]
  ) => {
    setMessages((prev) => [
      ...prev,
      {
        id: nextId.current++,
        role,
        text,
        chart,
        table,
        noChat,
        agent,
        attachments: attachmentsMeta,
        sourceKind,
        selectionOptions,
        selectionAttachmentId,
        idCall,
        documentMeta: documentInfo?.meta,
        documentCandidates: documentInfo?.candidates,
        documentQuestionId: documentInfo?.questionId,
        documentAttachmentId: documentInfo?.attachmentId,
        quote,
        sentAttachments,
        at: new Date().toISOString(),
      },
    ]);
  };

  /** 대화 내보내기 — 화면에 안 불러온 옛 턴까지 서버에서 다 읽어 카톡 PC 모양의 .txt 로 받는다 */
  const handleExportChat = async () => {
    setHeaderMenuEl(null);
    const room = activeRoom;
    if (!room) {
      setError(
        "저장된 대화가 없습니다. 질문을 한 번 한 뒤 내보낼 수 있습니다."
      );
      return;
    }
    /* 본체 — 50턴씩 옛 쪽으로 읽으며 진행률을 알린다. 끝없이 돌지 않게 100쪽(5,000턴)에서 멈춘다 */
    const run = async (onProgress: (done: number, total: number) => void) => {
      const all: ChatRoomTurn[] = [];
      let before: number | undefined;
      try {
        for (let i = 0; i < 100; i += 1) {
          const page = await fetchChatRoomTurns(room.idConv, {
            beforeSeq: before,
            limit: 50,
          });
          all.unshift(...page.items);
          onProgress(all.length, Math.max(room.qtTurn || 0, all.length));
          if (!page.hasMore || page.items.length === 0) break;
          before = page.oldestSeq;
        }
      } catch (err) {
        throw new Error(
          err instanceof ApiError ? err.message : "대화를 내보내지 못했습니다."
        );
      }
      const user = global.getState().userInfo as {
        fg_user?: string;
        nm_user?: string;
        nm_emp?: string;
      };
      const userName =
        (user?.fg_user === "2" ? user?.nm_user : user?.nm_emp) || "나";
      const title = roomTitle(room);
      saveChatText(
        buildChatExportText(title, userName, all, new Date()),
        exportFileName(title, "txt")
      );
    };
    setIsExporting(true);
    try {
      /* 진행 · 완료는 공통 팝업으로 — 카톡 PC 의 내보내기 창처럼 (2026-10-15) */
      await showPopupModal({
        title: "대화 내보내기",
        children: <ChatExportProgress run={run} />,
        width: "360px",
        height: "220px",
        okayBtn: true,
        okayBtnText: "확인",
        closeBtn: false,
        checkData: false,
      });
    } finally {
      setIsExporting(false);
    }
  };

  /* 끄는 동안 쓰는 말풍선 목록 — 마우스 추적은 캡처를 시작할 때 한 번만 붙이므로 늘 최신을 여기서 본다 */
  const messagesListRef = useRef(messages);
  messagesListRef.current = messages;

  /** 캡처 고르기 — 하나 고른 뒤 다른 것을 누르면 그 사이 전부(카톡처럼). 그 뒤로는 하나씩 더하고 뺀다 */
  const toggleCapture = (id: number) => {
    setCaptureSel((prev) => {
      const ids = messagesListRef.current.map((m) => m.id);
      if (prev.length === 1 && prev[0] !== id) {
        const a = ids.indexOf(prev[0]);
        const b = ids.indexOf(id);
        return a < b ? ids.slice(a, b + 1) : ids.slice(b, a + 1);
      }
      return prev.includes(id)
        ? prev.filter((x) => x !== id)
        : ids.filter((x) => x === id || prev.includes(x));
    });
  };

  /** 마우스 높이에 있는 말풍선 — 말풍선 사이 틈 · 날짜줄 위면 가장 가까운 말풍선. 없으면 null */
  const rowAtY = (y: number): number | null => {
    const box = messagesRef.current;
    if (!box) return null;
    let best: { id: number; gap: number } | null = null;
    for (const el of Array.from(
      box.querySelectorAll<HTMLElement>(".chatbot-message[data-msg-id]")
    )) {
      const r = el.getBoundingClientRect();
      const gap = y < r.top ? r.top - y : y > r.bottom ? y - r.bottom : 0;
      if (!best || gap < best.gap)
        best = { id: Number(el.getAttribute("data-msg-id")), gap };
      if (gap === 0) break;
    }
    return best && !Number.isNaN(best.id) ? best.id : null;
  };

  /** 끄는 중 — 마우스 높이의 말풍선까지 누른 말풍선부터 그 사이를 더하거나 뺀다.
      마우스가 대화 영역 위 · 아래로 벗어나도 맨 위 · 맨 아래 말풍선까지 이어 고른다 */
  const extendCaptureDrag = () => {
    const drag = captureDragRef.current;
    const box = messagesRef.current;
    if (!drag || !box) return;
    const rect = box.getBoundingClientRect();
    const id = rowAtY(
      Math.min(Math.max(drag.y, rect.top + 4), rect.bottom - 4)
    );
    if (id === null) return;
    const startMoving =
      !drag.moved && (id !== drag.anchor || Math.abs(drag.y - drag.y0) > 4);
    if (startMoving) drag.moved = true;
    if (!drag.moved || (!startMoving && id === drag.lastId)) return;
    drag.lastId = id;
    const ids = messagesListRef.current.map((m) => m.id);
    const a = ids.indexOf(drag.anchor);
    const b = ids.indexOf(id);
    if (a < 0 || b < 0) return;
    const range = a < b ? ids.slice(a, b + 1) : ids.slice(b, a + 1);
    setCaptureSel(
      drag.remove
        ? drag.base.filter((x) => !range.includes(x))
        : ids.filter((x) => drag.base.includes(x) || range.includes(x))
    );
  };

  /**
   * 대화 영역에서 누르기 (카톡 PC 처럼, 2026-10-17).
   * 평소: 말풍선 안은 글자 선택(브라우저 그대로), 말풍선 밖 빈 곳에서 끌면 말풍선 고르기. 새로 누르면 앞서 고른 말풍선은 풀린다.
   * 캡처 모드: 어디서 끌어도 말풍선 고르기, 안 끌고 놓으면 그 말풍선 하나 더하기 · 빼기
   */
  const startBlockDrag = (event: React.MouseEvent) => {
    if (event.button !== 0 || captureSaving) return;
    const target = event.target as HTMLElement;
    if (target.closest("button, a, input, textarea, select")) return;
    if (!captureOn) {
      if (captureSel.length > 0) setCaptureSel([]);
      if (target.closest(".chatbot-message-content")) return;
    }
    const id = rowAtY(event.clientY);
    if (id === null) return;
    /* 글자가 파랗게 선택되지 않게 */
    event.preventDefault();
    window.getSelection()?.removeAllRanges();
    captureDragRef.current = {
      anchor: id,
      base: captureOn ? captureSel : [],
      remove: captureOn && captureSel.includes(id),
      moved: false,
      x: event.clientX,
      y: event.clientY,
      lastId: id,
      y0: event.clientY,
    };
  };

  /* 끄는 동안 — 마우스를 따라 넓히고, 창 위 · 아래 끝(48px)에 가까울수록 빨리 저절로 스크롤한다(카톡처럼 부드럽게).
     놓았을 때 안 움직였으면 누르기 한 번. 추적은 캡처를 켤 때 한 번만 붙인다 — 전에는 다시 그릴 때마다 뗐다 붙였다 (2026-10-17) */
  useEffect(() => {
    let frame = 0;
    const EDGE = 48;
    const tick = () => {
      const drag = captureDragRef.current;
      const box = messagesRef.current;
      if (drag && box) {
        const rect = box.getBoundingClientRect();
        const up = rect.top + EDGE - drag.y;
        const down = drag.y - (rect.bottom - EDGE);
        const depth =
          up > 0 ? -Math.min(up, EDGE) : down > 0 ? Math.min(down, EDGE) : 0;
        if (depth !== 0) {
          box.scrollTop += Math.round((depth / EDGE) * 18);
          extendCaptureDrag();
        }
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    const onMove = (event: MouseEvent) => {
      const drag = captureDragRef.current;
      if (!drag) return;
      drag.x = event.clientX;
      drag.y = event.clientY;
      extendCaptureDrag();
    };
    const onUp = () => {
      const drag = captureDragRef.current;
      captureDragRef.current = null;
      /* 캡처 모드에서 안 끌고 놓으면 그 말풍선 하나 더하기 · 빼기. 평소엔 아무것도 안 한다 */
      if (drag && !drag.moved && captureOnRef.current)
        toggleCapture(drag.anchor);
    };
    /* 평소 고른 말풍선은 Esc 로 푼다 */
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !captureOnRef.current) setCaptureSel([]);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const endCapture = () => {
    setCaptureOn(false);
    setCaptureSel([]);
  };

  /**
   * 캡처 — 고르지 않은 말풍선을 숨기고 목록을 펼친 뒤 그림으로. 단추 · 고르기 동그라미는 빼고 찍는다.
   * copy 면 클립보드로(원하는 메신저 · 메일에 붙인다). https 가 아니거나 막히면 파일로 (2026-10-15)
   */
  const handleCaptureSave = async (copy = false, ids?: number[]) => {
    const node = messagesRef.current;
    const sel = ids ?? captureSel;
    if (!node || sel.length === 0) return;
    /* 우클릭 복사 — 그 말풍선만 골라 찍는다. 캡처 모드가 아니어도 같은 길 */
    if (ids) setCaptureSel(ids);
    setCaptureSaving(true);
    /* 숨김 · 펼침이 그려진 뒤에 찍는다 */
    await new Promise((resolve) =>
      requestAnimationFrame(() => requestAnimationFrame(resolve))
    );
    try {
      const { toPng, getFontEmbedCSS } = await import("html-to-image");
      /* 고르지 않은 말풍선(is-capture-hidden)은 복사부터 하지 않는다 — 긴 대화에서 느렸다 */
      const skip = [
        "chatbot-capture-check",
        "chatbot-reply-btn",
        "chatbot-feedback",
        "chatbot-export",
        "chatbot-history-more",
        "chatbot-privacy-notice",
        "is-capture-hidden",
      ];
      if (captureFontCss === null) {
        captureFontCss = await getFontEmbedCSS(node);
      }
      const dataUrl = await toPng(node, {
        backgroundColor: "#ffffff",
        pixelRatio: 2,
        width: node.scrollWidth,
        height: node.scrollHeight,
        fontEmbedCSS: captureFontCss,
        filter: (n) =>
          !(
            n instanceof HTMLElement &&
            skip.some((c) => n.classList.contains(c))
          ),
      });
      const fileName = exportFileName(
        activeRoom ? roomTitle(activeRoom) : "chatbot",
        "png"
      );
      const result = copy
        ? await copyOrDownloadImage(dataUrl, fileName)
        : downloadImage(dataUrl, fileName);
      endCapture();
      setCaptureNotice(
        result === "copied"
          ? "그림을 복사했습니다. 메신저나 메일에 붙여 넣으세요."
          : copy
            ? "이 주소에서는 복사할 수 없어 그림 파일로 저장했습니다."
            : "그림 파일로 저장했습니다."
      );
      window.setTimeout(() => setCaptureNotice(""), 3000);
    } catch {
      setError("대화를 캡처하지 못했습니다.");
    } finally {
      setCaptureSaving(false);
    }
  };

  /** 답장 — 카톡처럼 AI 답 말풍선 하나를 통째로 인용한다. 보낼 때 같이 실리고 중앙은 고르기 모델을 건너뛴다 (2026-10-15) */
  const startReply = (text: string) => {
    setReplyTo(text);
    inputRef.current?.focus();
  };

  /** 내 말풍선 위 인용을 누르면 원래 답으로 — 다시 연 방은 번호가 없어 글 앞부분으로 찾는다 */
  const scrollToQuoted = (quote: string) => {
    const head = quote.slice(0, 40);
    const found = messages.find(
      (m) => m.role === "assistant" && m.text.includes(head)
    );
    if (!found) return;
    messagesRef.current
      ?.querySelector(`[data-msg-id="${found.id}"]`)
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  /**
   * 첨부/미지 소스 응답 렌더(§66) — true를 반환하면 소비 완료(화면 액션·파일 다운로드 경로
   * 미진입). sourceKind 없음/"SCREEN"은 false로 기존 경로에 넘긴다.
   */
  const renderAttachmentReply = (
    reply: ChatReply | null | undefined,
    capturedGeneration: number,
    capturedDocumentGeneration?: number
  ): boolean => {
    const sourceKind = reply?.sourceKind;
    if (sourceKind == null || sourceKind === "SCREEN") {
      return false;
    }
    if (sourceKind !== "ATTACHMENT") {
      // fail-closed: 미지의 non-null 판별자 — 어떤 페이로드도 렌더·실행하지 않는다(§66)
      appendMessage(
        "assistant",
        "서버 응답 형식을 확인할 수 없어 표시하지 않았습니다. 다시 시도해 주세요."
      );
      return true;
    }
    if (chatAttachment.generation() !== capturedGeneration) {
      // §58: 교체/제거 이후 도착한 구 파일 결과는 새 칩 아래 표시하지 않는다
      appendMessage(
        "assistant",
        "이전 첨부 파일 기준 결과가 도착해 표시하지 않았습니다. 현재 파일로 다시 질문해 주세요."
      );
      return true;
    }
    const clarification = reply?.clarification;
    const isDocumentReply =
      reply?.document !== undefined ||
      clarification?.kind === "DOCUMENT_CLARIFICATION";
    if (
      isDocumentReply &&
      capturedDocumentGeneration !== undefined &&
      chatDocument.generation() !== capturedDocumentGeneration
    ) {
      // 문서 교체/삭제 이후 도착한 구 문서 결과 — 새 칩/사실 아래 표시하지 않는다.
      appendMessage(
        "assistant",
        "이전 문서 기준 결과가 도착해 표시하지 않았습니다. 현재 문서로 다시 질문해 주세요."
      );
      return true;
    }
    const selectionOptions =
      clarification?.kind === "TABLE_SELECTION" && clarification.options
        ? Object.entries(clarification.options).map(([label, optionId]) => ({
            label,
            optionId,
          }))
        : undefined;
    const readyId =
      chatAttachment.state.status === "READY"
        ? chatAttachment.state.ready.attachmentId
        : undefined;
    if (selectionOptions && readyId) {
      pendingSelectionRef.current = {
        attachmentId: readyId,
        labels: selectionOptions.map((option) => option.label),
      };
    } else if (readyId) {
      pendingSelectionRef.current = null; // 선택이 해소된 첨부 응답 — 보류 종료
    }
    // 문서 되묻기(plan §Task 23) — 후보는 questionId+candidateId 전송용 버튼으로만 쓰인다.
    const documentAttachmentId =
      chatDocument.state.status === "PROCESSING" ||
      chatDocument.state.status === "READY"
        ? chatDocument.state.attachmentId
        : undefined;
    const documentCandidates =
      clarification?.kind === "DOCUMENT_CLARIFICATION" && clarification.options
        ? Object.entries(clarification.options).map(([candidateId, label]) => ({
            label,
            candidateId,
          }))
        : undefined;
    appendMessage(
      "assistant",
      reply?.message ?? "SNotes AI Bot 응답을 불러오지 못했습니다.",
      reply?.chart,
      reply?.table,
      reply?.noChat,
      reply?.agent,
      undefined,
      "ATTACHMENT",
      selectionOptions,
      readyId,
      isDocumentReply
        ? {
            meta: reply?.document,
            candidates: documentCandidates,
            questionId:
              clarification?.kind === "DOCUMENT_CLARIFICATION"
                ? clarification.originalMessage
                : undefined,
            attachmentId: documentAttachmentId,
          }
        : undefined,
      reply?.idCall
    );
    return true;
  };

  const handleChatReply = (
    reply: ChatReply | null | undefined,
    capturedGeneration: number,
    capturedDocumentGeneration?: number
  ) => {
    if (
      !renderAttachmentReply(
        reply,
        capturedGeneration,
        capturedDocumentGeneration
      )
    ) {
      appendMessage(
        "assistant",
        reply?.message ?? "SNotes AI Bot 응답을 불러오지 못했습니다.",
        reply?.chart,
        reply?.table,
        reply?.noChat,
        reply?.agent,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined, // documentInfo
        reply?.idCall
      );
    }
  };

  /**
   * 화면 경로 응답 처리(2026-09-01 추출) — handleSubmit과 되묻기 버튼 클릭이 같은 경로를 쓴다.
   * 순서 불변식(§66): 소스 판별자 검증·첨부 렌더가 먼저 — 소비되면 화면 액션·파일 경로에 진입하지 않는다.
   * sourceKind가 "ATTACHMENT"이거나 알 수 없는 값이면 화면 함수는 절대 실행되지 않는다.
   */
  const handleScreenReply = async (
    reply: ChatReply | null | undefined,
    capturedAttachmentGeneration: number,
    capturedDocumentGeneration?: number,
    /** 기다린 뒤 결과를 붙이기 전에 "아직 그 방 · 그 메뉴인가" (V2, 2026-10-08). 없으면 확인하지 않는다 */
    isLive?: () => boolean
  ): Promise<boolean> => {
    /* 돌려주는 값 = 화면 조작이 있었다면 성공했는가 (없으면 true). 행동 신호 기억을 이것으로 정한다 */
    // §66: 소스 판별자 검증·첨부 렌더가 먼저 — 소비되면 화면 액션·파일 경로 미진입
    // (문서 첨부 세대도 함께 검증 — IDP 병합 2026-09-02)
    if (
      renderAttachmentReply(
        reply,
        capturedAttachmentGeneration,
        capturedDocumentGeneration
      )
    ) {
      return true;
    }
    /* 생성 파일(PPT 보고서) 즉시 다운로드(v0.8.x) — 인증 헤더를 실은 fetch로 받아 저장한다.
       화면 조작 분기와 따로 둔다 — 행추가와 함께 오면 행추가 분기가 먼저 돌아가 내려받지 못했다 (2026-10-16 리뷰).
       파일은 사용자 것이라 방을 바꿔도 내려받는다. 실패 문구만 그 방일 때 */
    const downloadReplyFile = async () => {
      if (!reply?.file?.url) return;
      const downloadResult = await downloadChatFile(
        reply.file.url,
        reply.file.fileName || "report.pptx"
      );
      if (downloadResult === "failed" && (!isLive || isLive())) {
        setError(
          "보고서 다운로드에 실패했습니다. 챗봇에서 다시 요청해 주세요."
        );
      }
    };
    // 행추가 v2(화면 함수) — 백엔드가 CALL_SCREEN_FUNCTION 액션을 주면 활성 화면의 onChatAddRow를
    // 호출하고, 그 결과 message "하나만" 렌더한다(백엔드 낙관 메시지와의 모순 방지, 단일 진실원).
    if (reply?.screenAction?.type === "CALL_SCREEN_FUNCTION") {
      const { functionName, request } = screenFunctionParams(
        reply.screenAction.paramsPatch
      );
      let actionMessage = "행추가 요청 정보를 확인하지 못했습니다.";
      let executed = false;
      /* 바꿀 코드 값의 후보가 여럿 — 행을 만들지 않고 멈췄다. 번호 버튼을 띄운다 (2026-10-21) */
      let addRowPick: ChatFieldPick | undefined;
      if (functionName !== "onChatAddRow") {
        actionMessage = "지원하지 않는 화면 함수입니다.";
      } else if (request) {
        try {
          const actionResult = await onSearchStore
            .getState()
            .callChatAddRow(request, reply.screenContext?.fields?.menuCode);
          switch (actionResult.status) {
            case "EXECUTED":
              addRowPick = actionResult.result.pick;
              executed = !addRowPick;
              actionMessage =
                actionResult.result.message ||
                "행추가 결과를 확인하지 못했습니다.";
              break;
            case "MENU_MISMATCH":
              actionMessage = "화면이 바뀌어 행 추가를 실행하지 않았습니다.";
              break;
            case "UNSUPPORTED":
              actionMessage = "이 메뉴는 행추가를 지원하지 않습니다.";
              break;
            default: {
              const unexpected: never = actionResult;
              throw new TypeError(
                `Unknown add-row call result: ${String(unexpected)}`
              );
            }
          }
        } catch (error) {
          if (!(error instanceof Error)) {
            throw error;
          }
          actionMessage = "행추가 중 오류가 발생했습니다.";
        }
      }
      await downloadReplyFile();
      /* 행추가를 기다리는 사이 방을 바꿨으면 결과 문구를 새 방 화면에 붙이지 않는다 (2026-10-08 리뷰) */
      if (isLive && !isLive()) {
        return false;
      }
      if (addRowPick) {
        showFieldPick(
          { ...addRowPick, menuCode: reply.screenContext?.fields?.menuCode },
          reply?.idCall
        );
        return false;
      }
      appendMessage(
        "assistant",
        actionMessage,
        undefined,
        undefined,
        reply?.noChat,
        reply?.agent,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        /* 조회 · 행추가 결과 답에도 👍👎 — idCall 이 있어야 평가 버튼이 그려진다 (2026-10-01) */
        reply?.idCall
      );
      return executed;
    } else if (reply?.screenAction?.type === "CLOSE_MENU") {
      /* 메뉴 닫기 — 화면 공통 닫기가 결과 한 줄을 만든다. 중앙 답 대신 그 한 줄만(행추가와 같은 단일 진실원, 2026-10-16).
         닫으면 메뉴가 바뀌니 "아직 그 메뉴인가" 는 닫기 전에만 본다 */
      if (isLive && !isLive()) {
        return false;
      }
      const closer = (
        window as unknown as {
          onCloseMenus?: (cdMenu?: string, all?: boolean) => Promise<string>;
        }
      ).onCloseMenus;
      const closeParams = (reply.screenAction.paramsPatch || {}) as {
        all?: unknown;
      };
      const closeMessage =
        typeof closer === "function"
          ? await closer(
              reply.screenContext?.fields?.menuCode,
              closeParams.all === true
            )
          : "메뉴를 닫을 수 있는 화면 상태가 아닙니다.";
      appendMessage(
        "assistant",
        closeMessage,
        undefined,
        undefined,
        reply?.noChat,
        reply?.agent,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        reply?.idCall
      );
      return true;
    } else {
      appendMessage(
        "assistant",
        reply?.message ?? "SNotes AI Bot 응답을 불러오지 못했습니다.",
        reply?.chart,
        reply?.table,
        reply?.noChat,
        reply?.agent,
        undefined,
        undefined,
        // 화면 경로 되묻기(ROW_KEY_SELECTION/ADD_ROW_SCOPE)는 클릭 선택 — 값을 일반 텍스트로 전송.
        screenSelectionOptions(reply),
        undefined,
        undefined,
        /* 일반 답에도 👍👎 — idCall 이 있어야 평가 버튼이 그려진다 (2026-10-01) */
        reply?.idCall
      );
      await downloadReplyFile();
      if (reply?.screenAction?.type === "APPLY_SEARCH_AND_RUN") {
        /* 내려받기를 기다리는 사이 방을 바꿨으면 옛 방의 조회를 새 화면에 실행하지 않는다 — 실행 직전에 본다 (2026-10-16 리뷰) */
        if (isLive && !isLive()) {
          return false;
        }
        const actionResult = await onSearchStore
          .getState()
          .applySearchParamsAndRun(
            searchFormData(reply.screenAction.paramsPatch),
            reply.screenContext?.fields?.menuCode,
            { reset: reply.screenAction.reset, run: reply.screenAction.run }
          );
        /* 조회를 기다리는 사이 방을 바꿨으면 결과 안내 · 후보 버튼을 새 방에 띄우지 않는다 */
        if (isLive && !isLive()) {
          return false;
        }
        return handleApplyResult(actionResult, reply?.idCall);
      }
      return true;
    }
  };

  /** 코드 · 콤보 칸 또는 메뉴 후보를 채팅에 번호 버튼으로 띄운다. 번호를 입력해도 된다 (2026-10-02) */
  const showFieldPick = (pick: ChatFieldPick, idCall?: string) => {
    fieldPickRef.current = {
      ...pick,
      idCall,
      refs: pick.ask ? lastSentRefsRef.current : undefined,
    };
    /* [조회하고 답하기] [지금 화면으로 답하기] — 번호 · 메뉴 코드를 붙이지 않는다 (2026-10-21) */
    const plain = !!pick.ask;
    const options = pick.candidates.map((c, i) => ({
      label: plain
        ? c.name
        : `${i + 1}. ${c.name}${c.code && c.code !== c.name ? ` (${c.code})` : ""}`,
      optionId: String(i),
      kind: FIELD_PICK_KIND,
    }));
    appendMessage(
      "assistant",
      pick.prompt,
      undefined,
      undefined,
      undefined,
      { id: "aicenter", label: "AI Center", icon: "smart_toy" },
      undefined,
      undefined,
      options
    );
  };

  /** 조건 넣기 결과 — 후보가 여럿이면 버튼, 아니면 실패 문구 */
  const handleApplyResult = (
    result: ChatSearchApplyResult,
    idCall?: string
  ) => {
    if (result.pick) {
      showFieldPick(result.pick, idCall);
      return false;
    }
    if (!result.applied) {
      setError(result.message || searchApplyErrorMessage(result.reason));
      return false;
    }
    return true;
  };

  /** 메뉴 열기 결과 — 메뉴를 열고 조건을 넣는다. 조건 칸 후보가 여럿이면 버튼, 못 넣었으면 문구.
      ask(조회하고 답하기의 질문)는 칸 후보 버튼으로 넘긴다 — 고른 뒤 조회가 끝나면 다시 묻는다 */
  const handleOpenResult = (
    opened: Awaited<ReturnType<typeof aiScreenOpen.run>>,
    idCall?: string,
    ask?: string
  ) => {
    if (opened.status === "OPENED" && opened.searchPick) {
      showFieldPick(
        ask ? { ...opened.searchPick, ask } : opened.searchPick,
        idCall
      );
      return false;
    }
    const note = aiScreenOpen.message(opened);
    if (note) {
      setError(note);
      return false;
    }
    return true;
  };

  /** 후보 고르기 — AI 를 다시 부르지 않는다. 칸 후보면 그 코드로 조건을 다시 넣어 조회, 메뉴 후보면 그 메뉴를 열어 조건 적용 */
  const runFieldPick = async (index: number, userText: string) => {
    const pick = fieldPickRef.current;
    const chosen = pick?.candidates[index];
    if (!pick || !chosen) return;
    fieldPickRef.current = null;
    setError("");
    /* [지금 화면으로 답하기] — 조회하지 않고 버튼 이름을 질문으로 보낸다. 중앙이 "지금 화면으로 답하라" 로 읽는다 (2026-10-21) */
    if (pick.ask && chosen.code === ANSWER_HERE) {
      setQueued((prev) => [
        ...prev,
        { id: ++queueIdRef.current, text: chosen.name, screenOnly: true },
      ]);
      return;
    }
    appendMessage("user", userText);
    setIsSending(true);
    /* 기다리는 사이 다른 방을 고르면 완료 문구 · 후보 버튼을 그 방에 붙이지 않는다 (2026-10-08 리뷰) */
    const snap = chatRoomStore.getState().snapshot();
    const sameRoom = () => chatRoomStore.getState().isCurrent(snap);
    /* 조회하고 답하기 — 조회가 끝나면 같은 질문을 줄 세운다. 이 처리가 끝나면(isSending 해제) 차례로 나간다 (2026-10-21).
       방금 그 조건으로 조회했으니 지금 화면으로 답하라는 표시를 싣는다 — 조건을 다시 견주지 않는다.
       그리드는 그린 다음 한 박자 늦게 첫 줄을 고른다 — 다 그린 뒤 보낸다. 바로 보내면 "질문한 뒤 화면이 바뀌어" 로 막혔다 */
    const askAgain = async () => {
      if (!pick.ask) return;
      await new Promise((resolve) =>
        requestAnimationFrame(() =>
          requestAnimationFrame(() => setTimeout(resolve, 300))
        )
      );
      setQueued((prev) => [
        ...prev,
        {
          id: ++queueIdRef.current,
          text: pick.ask!,
          screenOnly: true,
          refs: pick.refs,
        },
      ]);
    };
    try {
      if (pick.target === "menu") {
        /* 후보마다 그 메뉴의 칸으로 옮긴 조건이 있으면 그것 — 메뉴마다 기간 칸 이름이 다르다 (2026-10-13) */
        const patch = chosen.patch ?? pick.patch;
        const opened = await aiScreenOpen.run({
          menuCode: chosen.code,
          paramsPatch: patch,
          search: pick.search,
        });
        if (sameRoom() && handleOpenResult(opened, pick.idCall, pick.ask)) {
          /* 후보로 메뉴를 열었다 — 원래 답의 호출 id 와 연 메뉴 · 넣은 조건으로 신호를 남긴다 (2026-10-08 리뷰).
             조건이 없으면 "답이 있었다" 만 남긴다. toChatReply 의 기억 규칙과 같다 */
          if (patch && Object.keys(patch).length > 0) {
            aiSignal.rememberAiSearch(chosen.code, pick.idCall, patch);
          } else {
            aiSignal.rememberAnswer(chosen.code, pick.idCall);
          }
          if (pick.ask) {
            await askAgain();
            return;
          }
          appendMessage(
            "assistant",
            `${chosen.name} 메뉴를 열었습니다.`,
            undefined,
            undefined,
            undefined,
            { id: "aicenter", label: "AI Center", icon: "smart_toy" }
          );
        }
        return;
      }
      if (pick.target === "addRow") {
        /* 행복사의 바꿀 코드 값 — 고른 코드로 행복사를 처음부터 한다. 다른 칸도 여럿이면 다시 버튼 (2026-10-21) */
        if (!pick.addRow || !pick.field) return;
        const request = {
          ...pick.addRow,
          codes: { ...pick.addRow.codes, [pick.field]: chosen.code },
        };
        const call = await onSearchStore
          .getState()
          .callChatAddRow(request, pick.menuCode);
        if (!sameRoom()) return;
        if (call.status === "EXECUTED" && call.result.pick) {
          showFieldPick(
            { ...call.result.pick, menuCode: pick.menuCode },
            pick.idCall
          );
          return;
        }
        appendMessage(
          "assistant",
          call.status === "EXECUTED"
            ? call.result.message || "행추가 결과를 확인하지 못했습니다."
            : call.status === "MENU_MISMATCH"
              ? "화면이 바뀌어 행 추가를 실행하지 않았습니다."
              : "이 메뉴는 행추가를 지원하지 않습니다.",
          undefined,
          undefined,
          undefined,
          { id: "aicenter", label: "AI Center", icon: "smart_toy" }
        );
        return;
      }
      if (!pick.field) return;
      const patch: ChatSearchFormData = {
        ...pick.patch,
        [pick.field]: chosen.code,
        ...(pick.displayField ? { [pick.displayField]: chosen.name } : {}),
      };
      /* 넣기만("설정해줘")이었으면 고른 뒤에도 조회하지 않는다. 초기화는 버튼을 띄우기 전에 이미 했다 (2026-10-16) */
      const result = await onSearchStore
        .getState()
        .applySearchParamsAndRun(patch, pick.menuCode, { run: pick.run });
      if (sameRoom() && handleApplyResult(result, pick.idCall)) {
        /* 후보로 조회했다 — 최종 적용 조건(고른 코드 포함)으로 신호를 남긴다. 이후 사람이 이 조건을 고치면 EDIT 로 이어진다 */
        const searched = pick.run !== false;
        if (searched) {
          aiSignal.rememberAiSearch(pick.menuCode, pick.idCall, patch);
        } else {
          aiSignal.rememberAnswer(pick.menuCode, pick.idCall);
        }
        if (searched) await askAgain();
        appendMessage(
          "assistant",
          searched
            ? `${pick.label} '${chosen.name}' (으)로 조회했습니다.`
            : `${pick.label} '${chosen.name}' (으)로 조회조건을 넣었습니다. 조회는 직접 눌러 주세요.`,
          undefined,
          undefined,
          undefined,
          { id: "aicenter", label: "AI Center", icon: "smart_toy" }
        );
      }
    } finally {
      setIsSending(false);
    }
  };

  /** 입력한 글이 떠 있는 후보 중 하나인가 — "3", "3번", 이름, 코드. 아니면 -1 */
  const matchFieldPick = (typed: string): number => {
    const pick = fieldPickRef.current;
    if (!pick) return -1;
    const m = typed.match(/^\s*(\d+)\s*번?\s*$/);
    if (m) {
      const n = Number(m[1]) - 1;
      return n >= 0 && n < pick.candidates.length ? n : -1;
    }
    const want = typed.replace(/\s+/g, "").toLowerCase();
    return pick.candidates.findIndex(
      (c) =>
        c.name.replace(/\s+/g, "").toLowerCase() === want ||
        c.code.toLowerCase() === want
    );
  };

  /**
   * 화면 경로 되묻기 버튼 클릭(2026-09-02, 구조화 continuation) — 말풍선에는 라벨을, 서버에는
   * {kind, optionId}를 보낸다. 백엔드는 보류 상태(원문·mode·keyField·scope)와 합쳐 텍스트 재파싱
   * 없이 다음 단계로 잇는다. kind가 없는 옵션(구버전 응답)은 값을 일반 텍스트로 보낸다.
   */
  const handleScreenOptionClick = async (option: {
    label: string;
    optionId: string;
    kind?: string;
  }) => {
    if (isSending || isClearing) return;
    if (option.kind === FIELD_PICK_KIND) {
      await runFieldPick(Number(option.optionId), option.label);
      return;
    }
    setError("");
    appendMessage("user", option.label);
    setIsSending(true);
    const capturedAttachmentGeneration = chatAttachment.generation();
    try {
      const response = option.kind
        ? await sendChatMessage("", {
            clarificationSelection: {
              kind: option.kind,
              optionId: option.optionId,
            },
          })
        : await sendChatMessage(option.optionId);
      await handleScreenReply(response.data, capturedAttachmentGeneration);
    } catch (err) {
      handleChatError(err);
    } finally {
      setIsSending(false);
    }
  };

  const handleChatError = (err: unknown, sentRefs?: ChatAttachmentRefV2[]) => {
    if (
      err instanceof ApiError &&
      err.error?.errorCode === "AI_ATTACHMENT_EXPIRED"
    ) {
      /* V2 첨부 만료(410, 2026-10-26) — 서버 보관이 끝났다. 그 요청에 실린 그림 칩만 걷고 다시 첨부를 안내한다 */
      const ids = (sentRefs ?? [])
        .map((r) => r.id)
        .filter((id): id is string => Boolean(id));
      if (ids.length > 0) void pruneExpiredV2Images(ids);
      setError(err.message);
      return;
    }
    if (
      err instanceof ApiError &&
      err.error?.errorCode?.startsWith("ATTACHMENT_")
    ) {
      if (err.code === 410) {
        // 410이 권위(§58) — 로컬 소프트 타이머 없이 칩을 걷고 재업로드 안내
        chatAttachment.clearLocal();
        chatDocument.clearLocal();
      }
      setError(err.message);
      return;
    }
    setError(
      err instanceof ApiError
        ? err.message
        : "SNotes AI Bot 요청 중 오류가 발생했습니다."
    );
  };

  /** 문서 되묻기 후보 클릭 — questionId+candidateId만 전송(사실 데이터 수정 없음). */
  const handleDocumentCandidateClick = async (
    attachmentId: string,
    questionId: string,
    candidateId: string
  ) => {
    if (isSending || isClearing) return;
    setIsSending(true);
    const capturedGeneration = chatAttachment.generation();
    const capturedDocumentGeneration = chatDocument.generation();
    try {
      const response = await sendChatMessage("", {
        attachmentRef: { attachmentId },
        documentFollowUp: { kind: "CLARIFICATION", questionId, candidateId },
      });
      handleChatReply(
        response.data,
        capturedGeneration,
        capturedDocumentGeneration
      );
    } catch (err) {
      handleChatError(err);
    } finally {
      setIsSending(false);
    }
  };

  /** 라인 페이징 — 서버가 돌려준 nextOffset만 전송한다(로컬 절단·재계산 금지). */
  const handleDocumentPageClick = async (
    attachmentId: string,
    offset: number
  ) => {
    if (isSending || isClearing) return;
    setIsSending(true);
    const capturedGeneration = chatAttachment.generation();
    const capturedDocumentGeneration = chatDocument.generation();
    try {
      const response = await sendChatMessage("", {
        attachmentRef: { attachmentId },
        documentFollowUp: { kind: "PAGE", operation: "LINE_LIST", offset },
      });
      handleChatReply(
        response.data,
        capturedGeneration,
        capturedDocumentGeneration
      );
    } catch (err) {
      handleChatError(err);
    } finally {
      setIsSending(false);
    }
  };

  /**
   * 새 채팅 — 디자이너 화면/서버 대화를 비우고 대화창도 처음 상태로 되돌린다.
   * 임시저장·배포는 Agent가 처리하므로 사이드바에서 남은 버튼은 이것뿐이다.
   */
  const startNewChat = () => {
    designerActions?.newChat();
    setMessages(initialMessages);
    nextId.current = initialMessages.length + 1;
    setInput("");
    setAttachments([]);
    setError("");
  };

  /** 긴 텍스트를 붙여넣으면 입력창 대신 첨부 칩으로 전환한다. */
  /* 위 · 아래 화살표로 이 방에서 내가 보낸 글을 다시 꺼낸다 (2026-10-14).
     셸 히스토리와 같다 — 위는 더 오래된 글, 아래는 더 새 글, 맨 끝을 지나면 쓰던 초안으로 돌아온다.
     글 안에서 줄을 옮기는 중이면 (위는 첫 줄, 아래는 마지막 줄이 아닐 때) 건드리지 않는다. 한글 조합 중에도 건드리지 않는다.
     목록은 messages 에서 그때그때 뽑는다 — 방을 열어 복원한 턴도 그대로 나오고 따로 둘 상태가 없다.
     recallIndexRef 가 null 이면 탐색 중이 아니다. 글을 고치거나 메시지가 바뀌면 탐색을 끝낸다 */
  const recallIndexRef = useRef<number | null>(null);
  const recallDraftRef = useRef("");
  useEffect(() => {
    recallIndexRef.current = null;
  }, [messages]);
  const recallHistory = (): string[] => {
    const out: string[] = [];
    for (const m of messages) {
      if (m.role !== "user") continue;
      const t = (m.text || "").trim();
      if (!t || out[out.length - 1] === t) continue; // 빈 글(첨부만) · 연달아 같은 글은 건너뛴다
      out.push(t);
    }
    return out;
  };
  const handleRecallKey = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;
    if (event.nativeEvent.isComposing) return;
    const up = event.key === "ArrowUp";
    const area = event.currentTarget;
    const onEdge = up
      ? !area.value.slice(0, area.selectionStart).includes("\n")
      : !area.value.slice(area.selectionEnd).includes("\n");
    if (!onEdge) return;
    const history = recallHistory();
    if (history.length === 0) return;
    let index = recallIndexRef.current;
    if (index === null) {
      if (!up) return;
      recallDraftRef.current = area.value;
      index = history.length;
    }
    const next = up ? index - 1 : index + 1;
    event.preventDefault();
    if (next < 0) return; // 가장 오래된 글 — 더 올라가지 않는다
    if (next >= history.length) {
      recallIndexRef.current = null;
      setInput(recallDraftRef.current);
      return;
    }
    recallIndexRef.current = next;
    setInput(history[next]);
  };

  /* ── V2 그림 첨부 트레이 (aibot-v2-attachments 0단계, 2026-10-26) ── */

  /** 그림 하나를 트레이에 넣고 바로 올린다. 실패하면 칩을 걷고 이유를 보인다 */
  const addV2Image = async (file: File) => {
    if (isMenuDesigner) {
      setError("메뉴 디자이너 대화에는 그림을 첨부할 수 없습니다.");
      return;
    }
    const pdf = isPdfFile(file);
    const table = !pdf && isTableFile(file);
    if (!pdf && !table && !isImageFile(file)) {
      setError(
        "그림(png · jpeg) · PDF · 엑셀(xlsx) · CSV 만 첨부할 수 있습니다."
      );
      return;
    }
    if (pdf || table) {
      if (file.size > (pdf ? V2_DOCUMENT_MAX_BYTES : V2_TABLE_MAX_BYTES)) {
        setError(
          pdf
            ? "PDF 는 20MB 까지 올릴 수 있습니다."
            : "엑셀 · CSV 는 10MB 까지 올릴 수 있습니다."
        );
        return;
      }
      if (v2ImagesRef.current.some((c) => c.kind !== "IMAGE")) {
        setError(
          "문서 · 표 파일은 한 번에 1개까지 붙일 수 있습니다. 먼저 붙인 파일을 빼고 올려 주세요."
        );
        return;
      }
    } else {
      if (file.size > V2_IMAGE_MAX_BYTES) {
        setError("그림은 10MB 까지 올릴 수 있습니다.");
        return;
      }
      if (
        v2ImagesRef.current.filter((c) => c.kind === "IMAGE").length >=
        V2_IMAGES_PER_QUESTION
      ) {
        setError(
          `그림은 한 질문에 ${V2_IMAGES_PER_QUESTION}장까지 보낼 수 있습니다.`
        );
        return;
      }
    }
    const key = v2ImageKey.current++;
    const previewUrl = URL.createObjectURL(file);
    const chip: V2ImageChip = {
      key,
      name: file.name,
      size: file.size,
      previewUrl,
      status: "UPLOADING",
      kind: pdf ? "DOCUMENT" : table ? "TABLE" : "IMAGE",
    };
    v2ImagesRef.current = [...v2ImagesRef.current, chip];
    setV2Images(v2ImagesRef.current);
    if (error) setError("");
    try {
      const view = await uploadChatAttachmentV2(file);
      if (!v2ImagesRef.current.some((c) => c.key === key)) {
        /* 올리는 동안 칩을 지웠다 — 서버 것도 바로 지운다. 두면 만료까지 30장 한도를 먹는다 (리뷰 F7) */
        void deleteChatAttachmentV2(view.id).catch(() => undefined);
        return;
      }
      setV2Images((prev) =>
        prev.map((c) =>
          c.key === key
            ? {
                ...c,
                id: view.id,
                size: view.size,
                status: "READY",
                ...(view.pages ? { pages: view.pages } : {}),
              }
            : c
        )
      );
    } catch (err) {
      URL.revokeObjectURL(previewUrl);
      setV2Images((prev) => prev.filter((c) => c.key !== key));
      setError(
        err instanceof ApiError
          ? err.message
          : pdf
            ? "PDF 를 올리지 못했습니다."
            : table
              ? "표 파일을 올리지 못했습니다."
              : "그림을 올리지 못했습니다."
      );
    }
  };

  /**
   * 표 칩의 [화면에 넣기] — 지금 화면의 엑셀 업로드 팝업을 열고 파일 내용을 시트에 채운다(사용자 결정 2026-10-28).
   * 팝업 시트가 머리글로 칸을 맞춰 가져가고, 검증 · 저장은 팝업 그대로 사용자가 한다. 파일은 트레이의 원본을 읽는다
   */
  const putTableIntoScreen = async (chip: V2ImageChip) => {
    let read: Awaited<ReturnType<typeof readTableFile>>;
    try {
      read = await readTableFile(
        await (await fetch(chip.previewUrl)).blob(),
        chip.name
      );
    } catch (err) {
      setError(
        err instanceof Error && err.message
          ? `표 파일을 읽지 못했습니다. ${err.message}`
          : "표 파일을 읽지 못했습니다."
      );
      return;
    }
    if (error) setError("");
    const sheetNote =
      read.sheets > 1 ? ` (시트 ${read.sheets}개 중 '${read.sheet}')` : "";
    void offerExcelUploadTable(read.table).then((fill) => {
      if (!fill) return;
      setCaptureNotice(
        fill.positional
          ? `머리글이 화면 칸과 맞지 않아 왼쪽 칸부터 순서대로 ${fill.applied.toLocaleString()}행을 넣었습니다${sheetNote}. 확인 후 저장해 주세요.`
          : `${fill.applied.toLocaleString()}행을 넣었습니다${sheetNote}. 맞춘 칸: ${fill.matched.join(", ")}. 확인 후 저장해 주세요.`
      );
      window.setTimeout(() => setCaptureNotice(""), 8000);
    });
    const opened = await runActiveScreenExcelUpload().catch((err) => {
      cancelExcelUploadOffer();
      setError(
        err instanceof Error && err.message
          ? err.message
          : "엑셀 업로드를 열지 못했습니다."
      );
      return true;
    });
    if (!opened) {
      cancelExcelUploadOffer();
      setError(
        "지금 화면에는 엑셀 업로드가 없습니다. 엑셀 업로드가 있는 화면을 열고 다시 눌러 주세요."
      );
    } else if (cancelExcelUploadOffer()) {
      /* 팝업이 닫혔는데 아무 시트도 안 가져갔다 — 이 화면 업로드 창은 자동 넣기를 못 받는다 */
      setError(
        "이 화면의 엑셀 업로드 창에는 자동으로 넣지 못했습니다. 파일 내용을 복사해 붙여넣어 주세요."
      );
    }
  };

  const removeV2Image = (key: number) => {
    const target = v2ImagesRef.current.find((c) => c.key === key);
    if (target) {
      URL.revokeObjectURL(target.previewUrl);
      if (target.id)
        void deleteChatAttachmentV2(target.id).catch(() => undefined);
    }
    v2ImagesRef.current = v2ImagesRef.current.filter((c) => c.key !== key);
    setV2Images(v2ImagesRef.current);
  };

  /**
   * 만료 오류가 난 요청의 그림만 트레이에서 걷는다 — 서버에 하나씩 물어 없는 것만. 트레이 전체를 비우면
   * 그 사이 새로 붙인 그림까지 날아간다 (리뷰 2026-10-26). 보내기 실패로 되돌려 둔 칩은 이 뒤에 걷힌다
   */
  const pruneExpiredV2Images = async (ids: string[]) => {
    const gone = new Set<string>();
    await Promise.all(
      ids.map(async (id) => {
        /* 확정된 410 만 null — 통신 오류(예외)는 살아 있는 것으로 둔다 (재리뷰 5) */
        if ((await checkChatAttachmentV2(id).catch(() => undefined)) === null)
          gone.add(id);
      })
    );
    if (gone.size === 0) return;
    v2ImagesRef.current = v2ImagesRef.current.filter(
      (c) => !c.id || !gone.has(c.id)
    );
    setV2Images(v2ImagesRef.current);
  };

  /** 클립보드 · 드롭 · 파일 선택의 그림들 — 여러 장을 한 번에. 상한을 넘는 뒷장은 빼고 알린다 */
  const addV2ImageFiles = (files: FileList | File[]) => {
    if (isMenuDesigner) {
      setError("메뉴 디자이너 대화에는 그림을 첨부할 수 없습니다.");
      return;
    }
    const list = Array.from(files).filter((f) => f.size > 0);
    /* 그림 장수 상한은 그림에만 — PDF · 표는 addV2Image 가 따로 본다 (리뷰, 2026-10-29) */
    const others = list.filter((f) => isPdfFile(f) || isTableFile(f));
    const pictures = list.filter((f) => !others.includes(f));
    const room = Math.max(
      0,
      V2_IMAGES_PER_QUESTION -
        v2ImagesRef.current.filter((c) => c.kind === "IMAGE").length
    );
    if (pictures.length > room) {
      setError(
        `그림은 한 질문에 ${V2_IMAGES_PER_QUESTION}장까지 보낼 수 있습니다. 앞의 ${room}장만 붙였습니다.`
      );
    }
    for (const f of [...others, ...pictures.slice(0, room)]) void addV2Image(f);
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    /* 그림이 들어 있으면(캡처 붙여넣기 · 파일 복사) 글 대신 그림으로 — V2 만 (2026-10-26) */
    if (engine === "V2") {
      const imageFiles = Array.from(event.clipboardData.items || [])
        .filter((it) => it.kind === "file" && it.type.startsWith("image/"))
        .map((it) => it.getAsFile())
        .filter((f): f is File => Boolean(f));
      if (imageFiles.length > 0) {
        event.preventDefault();
        addV2ImageFiles(imageFiles);
        return;
      }
    }
    const pasted = event.clipboardData.getData("text");
    if (pasted.length < PASTE_AS_FILE_THRESHOLD) return; // 짧으면 일반 붙여넣기
    event.preventDefault();
    if (chatAttachment.state.status !== "IDLE") {
      // §39: 업로드 표 파일과 붙여넣기 텍스트 첨부는 한 전송에 공존 금지 — 어느 쪽도 조용히 삭제하지 않는다
      setError(
        "표 파일이 첨부된 상태입니다. 파일을 제거한 뒤 텍스트를 붙여넣어 주세요."
      );
      return;
    }
    setAttachments((prev) => [
      ...prev,
      {
        id: attachmentId.current++,
        name: makePastedFileName(),
        content: pasted,
        charCount: pasted.length,
      },
    ]);
    if (error) setError("");
  };

  const removeAttachment = (id: number) => {
    setAttachments((prev) => prev.filter((att) => att.id !== id));
  };

  /** 표/문서 파일 선택/드롭(§38, IDP §Task 22) — 선택 즉시 업로드. 붙여넣기 칩·상호 배타. */
  const handleAttachmentFile = async (file: File | undefined | null) => {
    if (!file) return;
    if (engine === "V2") {
      /* V2 는 그림만(0단계). 표 · 문서는 3 · 4단계에서 V2 경로로 — V1 업로드로 흘리지 않는다 (2026-10-26) */
      await addV2Image(file);
      if (attachmentFileInputRef.current)
        attachmentFileInputRef.current.value = "";
      return;
    }
    if (attachments.length > 0) {
      setError(
        "붙여넣은 텍스트 첨부가 있습니다. 텍스트 첨부를 제거한 뒤 파일을 올려 주세요."
      );
      return;
    }
    const resetInput = () => {
      // 같은 파일 재선택이 change 이벤트를 다시 내도록 입력을 리셋(§43)
      if (attachmentFileInputRef.current) {
        attachmentFileInputRef.current.value = "";
      }
    };
    const documentKind = documentKindOf(file.name);
    if (documentKind) {
      if (!docCapability?.enabled) {
        setError(
          "이 회사에서는 문서(PDF/PNG/JPEG) 분석을 사용할 수 없습니다. 표 파일(xlsx/csv)은 계속 사용할 수 있습니다."
        );
        resetInput();
        return;
      }
      if (chatAttachment.state.status !== "IDLE") {
        setError(
          "표 파일 첨부가 있습니다. 표 파일을 제거한 뒤 문서를 올려 주세요."
        );
        resetInput();
        return;
      }
      setError("");
      const documentResult = await chatDocument.selectDocument(file);
      if (!documentResult.ok && documentResult.message) {
        setError(documentResult.message);
      }
      resetInput();
      return;
    }
    if (chatDocument.state.status !== "IDLE") {
      setError("문서 첨부가 있습니다. 문서를 제거한 뒤 표 파일을 올려 주세요.");
      resetInput();
      return;
    }
    setError("");
    const result = await chatAttachment.selectFile(file);
    if (!result.ok && result.message) {
      setError(result.message);
    }
    resetInput();
  };

  const handleDocumentRemove = async () => {
    const result = await chatDocument.remove();
    if (!result.ok && result.message) {
      setError(result.message);
    }
  };

  const handleAttachmentRemove = async () => {
    const result = await chatAttachment.remove();
    if (!result.ok && result.message) {
      setError(result.message);
    }
  };

  // ── 표 파일 드래그&드롭(§38 "file selection/drop") — 챗봇 영역 전체가 드롭존 ──
  const dragDepthRef = useRef(0);

  const dragHasFiles = (event: React.DragEvent) =>
    Array.from(event.dataTransfer.types).includes("Files");

  const handleDragEnter = (event: React.DragEvent) => {
    if (!dragHasFiles(event)) return;
    event.preventDefault();
    dragDepthRef.current += 1;
    setIsDragOver(true);
  };

  const handleDragOver = (event: React.DragEvent) => {
    if (!dragHasFiles(event)) return;
    event.preventDefault(); // 드롭 허용(기본 = 브라우저가 파일을 연다)
  };

  const handleDragLeave = (event: React.DragEvent) => {
    if (!dragHasFiles(event)) return;
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setIsDragOver(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    if (!dragHasFiles(event)) return;
    event.preventDefault();
    dragDepthRef.current = 0;
    setIsDragOver(false);
    if (isSending || isClearing) return;
    const files = event.dataTransfer.files;
    if (!files || files.length === 0) return;
    if (engine === "V2") {
      /* 그림은 여러 장을 한 번에 (2026-10-26) */
      addV2ImageFiles(files);
      return;
    }
    if (files.length > 1) {
      setError("표 파일은 한 번에 1개만 첨부할 수 있습니다.");
      return;
    }
    void handleAttachmentFile(files[0]);
  };

  /** 시트 선택 되묻기 클릭(§39·§40) — 메시지 없이 선택만 전송, 서버 보관 원 질문으로 재디스패치. */
  const handleTableSelectionClick = async (
    attachmentId: string,
    optionId: string,
    optionLabel: string
  ) => {
    if (isSending || isClearing) return;
    setIsSending(true);
    const capturedGeneration = chatAttachment.generation();
    try {
      const response = await sendChatMessage("", {
        attachmentRef: { attachmentId },
        clarificationSelection: { kind: "TABLE_SELECTION", optionId },
      });
      // 선택이 수락됐으면 다음 질문부터 tableId가 실리도록 로컬 선택을 반영한다
      // (서버는 optionId만 알므로 라벨→tableId는 업로드 응답 tables에서 역조회, 리뷰 지적 반영)
      if (
        chatAttachment.state.status === "READY" &&
        chatAttachment.state.ready.attachmentId === attachmentId
      ) {
        const matched = chatAttachment.state.ready.tables.find(
          (table) => table.label === optionLabel
        );
        if (matched) {
          chatAttachment.selectTable(matched.tableId);
        }
        pendingSelectionRef.current = null;
      }
      handleChatReply(response.data, capturedGeneration);
    } catch (err) {
      handleChatError(err);
    } finally {
      setIsSending(false);
    }
  };

  /**
   * V2 한 턴 — 보내고, 답을 붙이고, 화면 조작 · 행동 신호 · 압축까지 (2026-10-14, handleSubmit 에서 추출).
   * handleSubmit 과 "다시 시도" 가 같은 길을 쓴다. isSending 은 부른 쪽이 켜고 끈다.
   * 돌려주는 값이 false 면 실패했고 아직 그 방이다 — 부른 쪽이 입력 · 내린 말풍선을 되돌린다.
   * 그 사이 다른 방을 골랐으면 화면에 아무것도 하지 않고 true (서버에는 그 방에 저장됐다)
   */
  const runV2Turn = async (
    outgoing: string,
    typed: string,
    retry: boolean,
    quote?: string,
    /** 지금 화면으로 답하라는 표시 (2026-10-21) */
    screenOnly = false,
    /** 첨부 참조 — 긴 글 인라인 · 그림 id (2026-10-26) */
    attachmentRefs?: ChatAttachmentRefV2[]
  ): Promise<boolean> => {
    lastSentRefsRef.current = attachmentRefs;
    const capturedAttachmentGeneration = chatAttachment.generation();
    const capturedDocumentGeneration = chatDocument.generation();
    /* 보내기 직전의 방 · 세대를 잡아 둔다. 돌아왔을 때 다른 방이면 어떤 효과도 내지 않는다 (2026-10-07) */
    let v2Snapshot: RoomSnapshot = chatRoomStore.getState().snapshot();
    /* 요청 ID 는 화면이 만든다 — ■ 가 이것으로 중지를 요청한다 (2026-10-14) */
    const idRequest = newChatRequestId();
    const active = { idRequest, stopped: false };
    activeRequestRef.current = active;
    try {
      const v2 = await sendChatMessageV2(outgoing, v2Snapshot.idConv, {
        retry,
        idRequest,
        quote,
        screenOnly,
        attachments: attachmentRefs,
      });
      if (!chatRoomStore.getState().isCurrent(v2Snapshot)) {
        /* 늦게 온 A 방의 답. 그 사이 B 방을 골랐다 — 서버에는 A 에 저장됐고 화면에는 아무것도 안 한다.
           파일만은 내려받는다 — 사용자가 만들라고 한 것이고 방과 무관하다. 실패 문구는 B 방에 띄우지 않는다 (2026-10-16 리뷰) */
        const lateFile = v2.data?.file;
        /* 화면에서 중지한 요청이면 내려받지 않는다 — 같은 방일 때(아래 active.stopped)와 같게 (2026-10-16 리뷰) */
        if (lateFile?.url && !active.stopped) {
          void downloadChatFile(
            lateFile.url,
            lateFile.fileName || "report.pptx"
          );
        }
        return true;
      }
      const adopted =
        v2Snapshot.idConv === null && v2.idConv
          ? chatRoomStore.getState().adopt(v2Snapshot, v2.idConv)
          : null;
      if (adopted && v2.idConv) {
        /* 이후 검사는 새 스냅샷으로 — 옛 스냅샷(id 없음)으로 보면 이 방을 "다른 방" 으로 판단한다 (2026-10-08 회귀 수정) */
        v2Snapshot = adopted;
        rememberRoom(roomKey(), v2.idConv);
        const adoptedId = v2.idConv;
        setActiveRoom({
          idConv: adoptedId,
          title: typed.slice(0, 100),
          cdMenu: globalMenu.getState().activeMenuInfo?.cd_menu || null,
          noIssue: null,
          qtTurn: 1,
          state: "ACTIVE",
        });
        /* 머리의 "시작 메뉴" 는 서버가 저장한 값으로 맞춘다 — 화면이 짐작한 메뉴와 다를 수 있다 (2026-10-08) */
        void fetchChatRoom(adoptedId)
          .then((room) => {
            if (room && chatRoomStore.getState().selectedId === room.idConv)
              setActiveRoom(room);
          })
          .catch(() => undefined);
      }
      /* 화면에서 중지한 요청의 답 — 방 id 만 받고(위) 나머지는 버린다. 서버도 stopped 로 답하고 중지 턴은 이미 저장돼 있다 */
      if (active.stopped) {
        return true;
      }
      let reply: ChatReply | null | undefined = v2.data;
      /* 화면 변경을 먼저 보고 나서 화면 밖 효과(신호 · 디자이너 알림)를 남긴다 (2026-10-08 2차 리뷰) */
      const screenMoved = v2.screenChanged();
      v2.notifyDesign();
      if (screenMoved) {
        /* 같은 방이지만 보낸 뒤 화면을 옮겼다 — 답 글은 보여 주고 조회 · 행추가 · 메뉴 열기는 하지 않는다 (2026-10-08) */
        const hadAction = Boolean(
          reply?.screenAction || reply?.screenOpen || reply?.screenPick
        );
        reply = reply
          ? {
              ...reply,
              screenAction: undefined,
              screenContext: undefined,
              screenOpen: undefined,
              screenPick: undefined,
            }
          : reply;
        if (hadAction) {
          setError(
            "질문한 뒤 화면이 바뀌어 화면 조작(조회·행추가·메뉴 열기)은 실행하지 않았습니다. 다시 질문해 주세요."
          );
        }
      }
      if (showRooms) void loadRooms();
      /* 기다린 뒤에 이어지는 효과는 아직 그 방 · 그 메뉴일 때만 낸다 (2026-10-08 리뷰).
         선택 상태는 보지 않는다 — 방금 우리가 적용한 조회가 선택을 바꿨을 수 있다 */
      const stillHere = () =>
        chatRoomStore.getState().isCurrent(v2Snapshot) && !v2.menuChanged();
      const actionOk = await handleScreenReply(
        reply,
        capturedAttachmentGeneration,
        capturedDocumentGeneration,
        stillHere
      );
      let openOk = true;
      /* 다른 메뉴 열기(OPEN_MENU). 답을 먼저 띄우고 연다 — 사용자가 무엇이 열리는지 읽은 뒤 화면이 바뀐다.
         방만 본다 — 메뉴 열기가 성공하면 메뉴가 바뀌는 게 정상이다 */
      const sameRoom = () => chatRoomStore.getState().isCurrent(v2Snapshot);
      if (reply?.screenOpen && stillHere()) {
        const opened = await aiScreenOpen.run(reply.screenOpen);
        /* 여는 사이 다른 방을 골랐으면 결과 안내 · 후보 버튼을 그 방에 붙이지 않는다 (2026-10-08 리뷰) */
        openOk = sameRoom() ? handleOpenResult(opened, reply.idCall) : false;
      }
      /* 메뉴 후보가 여럿(PICK_MENU) — 번호 버튼. 고르면 runFieldPick 이 연다 */
      if (reply?.screenPick && stillHere() && sameRoom()) {
        showFieldPick(reply.screenPick, reply.idCall);
      }
      /* 이 답에 쓴 포인트 · 저장된 턴 번호 — 같은 호출 id 의 말풍선에 붙인다 (2026-10-21 · 10-22).
         턴 번호는 그 답 바로 앞의 내 질문 말풍선에도 — 말풍선 태그는 턴 번호로 단다 */
      const answerCall = reply?.idCall;
      const answerPoint = reply?.point;
      const answerSeq = reply?.turnSeq;
      if (
        answerCall &&
        sameRoom() &&
        (typeof answerPoint === "number" || typeof answerSeq === "number")
      ) {
        setMessages((prev) => {
          const at = prev.findIndex((m) => m.idCall === answerCall);
          if (at < 0) return prev;
          const next = prev.slice();
          next[at] = {
            ...next[at],
            ...(typeof answerPoint === "number" && answerPoint >= 0
              ? { point: answerPoint }
              : {}),
            ...(typeof answerSeq === "number" ? { turnSeq: answerSeq } : {}),
          };
          if (typeof answerSeq === "number") {
            for (let i = at - 1; i >= 0; i -= 1) {
              if (next[i].role === "user") {
                if (next[i].turnSeq == null)
                  next[i] = { ...next[i], turnSeq: answerSeq };
                break;
              }
            }
          }
          return next;
        });
      }
      /* 행동 신호는 화면 조작이 실제로 성공했을 때만 기억한다. 화면이 바뀌어 조작을 건너뛰었으면 남기지 않는다.
         방만 확인한다 — 우리가 연 메뉴 때문에 바뀐 화면을 실패로 보지 않는다 (2026-10-08 리뷰) */
      if (!screenMoved && actionOk && openOk && sameRoom()) {
        v2.rememberSignals();
      }
      /* 답을 화면에 띄운 뒤에 압축한다. 기다리지 않는다. 방이 바뀌었으면 엉뚱한 방을 압축하지 않는다 */
      if (
        reply?.compactNeeded &&
        chatRoomStore.getState().isCurrent(v2Snapshot)
      ) {
        void runCompact();
      }
      return true;
    } catch (err) {
      if (!chatRoomStore.getState().isCurrent(v2Snapshot)) {
        /* 늦게 실패한 A 방의 전송 — B 방 입력칸에 A 의 글을 되돌려 놓지 않는다 */
        return true;
      }
      if (active.stopped) {
        /* 중지한 뒤 늦게 실패했다 — 사용자는 이미 중지 문구를 봤다. 오류를 띄우지 않는다 */
        return true;
      }
      handleChatError(err, attachmentRefs);
      return false;
    } finally {
      if (activeRequestRef.current === active) activeRequestRef.current = null;
    }
  };

  /**
   * ■ 답변 중지 (aibot-v2-chat-cancel, 2026-10-14). 서버에 중지 표시를 하고 "답변을 중지했습니다." 를 바로 붙인다.
   * 진행 중인 요청은 끊지 않는다 — 서버가 돌아오면 버린다(runV2Turn 의 active.stopped). 서버가 false 면 이미 답이 났다 — 기다린다.
   * 서버에 닿지 않으면(옛 BE · 통신 오류) 화면에서만 중지한다 — 그 답은 저장되고 다음에 방을 열 때 보일 수 있다
   */
  const handleStop = async () => {
    /* 중지하면 줄 세운 질문도 함께 취소한다 (2026-10-17) */
    setQueued([]);
    const active = activeRequestRef.current;
    if (!active || active.stopped || stopping) return;
    setStopping(true);
    const snap = chatRoomStore.getState().snapshot();
    let stoppedOnServer: boolean | null = null;
    try {
      stoppedOnServer = await cancelChatV2(active.idRequest);
    } catch {
      stoppedOnServer = null;
    } finally {
      setStopping(false);
    }
    if (stoppedOnServer === false) return;
    if (activeRequestRef.current !== active) return;
    active.stopped = true;
    if (!chatRoomStore.getState().isCurrent(snap)) return;
    setMessages((prev) => [
      ...prev,
      {
        id: nextId.current++,
        role: "assistant",
        text: STOPPED_TEXT,
        agent: { id: "aicenter", label: "AI Center", icon: "smart_toy" },
        /* 중지한 질문은 "다시 시도" 로 바로 다시 보낼 수 있다 — 서버에 중지 턴이 있어 보통 재시도와 같다 */
        retryable: true,
      },
    ]);
    if (stoppedOnServer === null) {
      setError(
        "서버에 중지가 전달되지 않아 화면에서만 중지했습니다. 답이 완성되면 다음에 방을 열 때 보일 수 있습니다."
      );
    }
    sendSeqRef.current += 1;
    setIsSending(false);
  };

  /**
   * "다시 시도" — 마지막 답을 내리고 같은 질문을 retry 로 다시 보낸다 (2026-10-14, aibot-v2-chat-retry).
   * 서버는 직전 턴을 맥락에서 빼고 새 답을 새 턴으로 붙인다. 옛 답은 서버에 남고(포인트 집계) 복원 때는 숨겨진다.
   * 포인트는 보통 질문과 같이 든다. 실패하면 내렸던 답을 되돌려 놓는다
   */
  const handleRetry = async (target: ChatMessage) => {
    if (isSending || isClearing || isRoomLoading || engine !== "V2") return;
    const index = messages.findIndex((m) => m.id === target.id);
    if (index < 1 || index !== messages.length - 1) return;
    const asked = messages[index - 1];
    const question = asked.role === "user" ? asked.text.trim() : "";
    const refs = asked.role === "user" ? asked.sentAttachments : undefined;
    /* 글 없이 긴 글 칩만 보낸 턴도 다시 만들 수 있다 (재리뷰 2) */
    if (!question && !(refs && refs.some((r) => r.kind === "TEXT" && r.text)))
      return;
    setError("");
    fieldPickRef.current = null;
    setMessages((prev) => prev.filter((m) => m.id !== target.id));
    const mySeq = ++sendSeqRef.current;
    setIsSending(true);
    /* 첨부 확인을 기다리는 사이 다른 방을 고를 수 있다 — 그러면 보내지 않는다. A 방의 재시도가 B 방에 들어간다 (재리뷰 1) */
    const retrySnapshot = chatRoomStore.getState().snapshot();
    const putBack = () =>
      setMessages((prev) =>
        prev[prev.length - 1]?.id === asked.id ? [...prev, target] : prev
      );
    /* 옛 답에는 "다시 물었다" 신호 — 평가 버튼보다 확실한 불만 신호다. 복원한 턴은 호출 id 가 없어 보내지 않는다 */
    if (target.idCall) {
      void aiSignal.reask(
        target.idCall,
        globalMenu.getState().activeMenuInfo?.cd_menu
      );
    }
    try {
      /* 첨부가 있던 질문 — 그림 id 가 아직 살아 있는지 먼저 본다. 만료면 멈춘다 — 글만 자동으로 다시 보내면
         PDF · 그림 없이 다른 답을 만든다(aibot-v2-attachments 결정 15). 글만 보내는 것은 사용자가 새 질문으로 */
      if (asked.attachments && asked.attachments.length > 0 && !refs) {
        /* 다시 연 방의 그림 — 바이트가 화면에 없어 그대로 다시 보낼 수 없다. 글만 보내면 다른 답이 된다 (리뷰 F1) */
        setError(
          "다시 연 대화의 그림은 다시 보낼 수 없습니다. 그림을 다시 첨부해 새로 질문해 주세요."
        );
        putBack();
        return;
      }
      if (refs && refs.some((r) => r.id)) {
        /* 통신 오류는 만료가 아니다 — 안내만 하고 옛 답을 되돌린다. 확정된 410 만 null 이다 (재리뷰 5).
           오류 표시도 방 확인 뒤에 — A 방의 오류가 B 방에 뜨면 안 된다 (3차 리뷰) */
        const alive = await Promise.all(
          refs
            .filter((r) => r.id)
            .map((r) => checkChatAttachmentV2(r.id as string))
        ).catch((err: unknown) => ({ failed: err }));
        if (!chatRoomStore.getState().isCurrent(retrySnapshot)) return;
        if (!Array.isArray(alive)) {
          handleChatError(alive.failed);
          putBack();
          return;
        }
        if (alive.some((a) => a === null)) {
          setError(
            "첨부가 만료되어 다시 만들 수 없습니다. 다시 첨부해 새로 질문하거나, 글만 다시 보내 주세요."
          );
          putBack();
          return;
        }
      }
      /* 답장으로 한 질문이면 답장 원문도 같이 — 빠지면 다시 만든 답이 무엇에 대한 것인지 모른다 */
      const ok = await runV2Turn(
        question,
        question,
        true,
        asked.quote,
        false,
        refs
      );
      if (!ok) {
        /* 새 답이 안 왔다 — 질문 말풍선 뒤가 비어 있을 때만 옛 답을 되돌린다 */
        putBack();
      }
    } finally {
      if (sendSeqRef.current === mySeq) setIsSending(false);
    }
  };

  /* "다시 시도" 는 V2 의 마지막 답에만. 서버에 턴이 있어야 한다(호출 id 가 있거나 복원한 턴) —
     /도움 · 화면 명령 · 안내 글은 턴이 없어 다시 만들 것이 없다. 디자이너 대화는 제외 — 직전 제안이 이미 초안을 바꿨다 */
  const canRetry = (message: ChatMessage, index: number) =>
    engine === "V2" &&
    !isMenuDesigner &&
    message.role === "assistant" &&
    index === messages.length - 1 &&
    index > 0 &&
    messages[index - 1].role === "user" &&
    Boolean(message.idCall || message.retryable);
  const retryBusy = isSending || isClearing || isRoomLoading;

  /**
   * 보내기 본체 (2026-10-17 handleSubmit 에서 뗐다). fromInput 이면 입력칸의 글 · 붙여넣기 칩 · 답장을 쓰고 비운다.
   * 줄 세워 둔 질문(fromInput=false)은 그것들을 건드리지 않는다 — 기다리는 동안 새로 쓰고 있는 초안이다
   */
  const sendMessage = async (
    typed: string,
    fromInput: boolean,
    screenOnly = false,
    /** 줄 세운 질문이 이어 싣는 첨부 — [조회하고 답하기] 의 원래 첨부 (리뷰 2026-10-27) */
    carriedRefs?: ChatAttachmentRefV2[]
  ) => {
    /* 방 이력을 불러오는 중에는 보내지 않는다 — 늦게 온 이력이 방금 보낸 질문을 화면에서 덮는다 (2026-10-08) */
    if (isSending || isClearing || isRoomLoading) return;
    const draftAttachments = fromInput ? attachments : [];
    /* 실패해 되돌릴 때 — 줄 세운 질문은 입력칸이 비어 있을 때만 돌려놓는다(쓰던 초안을 덮지 않게) */
    const setDraftInput = (value: string) => {
      if (fromInput) setInput(value);
      else if (value) setInput((prev) => prev || value);
    };
    if (!typed && draftAttachments.length === 0) {
      setError("메시지를 입력해 주세요.");
      return;
    }
    /* V2 "/도움" — 서버로 보내지 않고 중앙이 조립한 예시 질문만 보여준다 (aibot-v2-help, 2026-10-13).
       후보 선택(fieldPickRef) · 첨부 · 되묻기 상태는 건드리지 않는다 — 도움말을 보고 돌아와 "2번" 이라고 해도 이전 후보가 이어진다.
       그래서 후보 처리 블록(맞지 않는 입력이면 선택을 지운다)보다 앞에서 분기한다. 방 생성 · 포인트 · 행동 신호도 없다.
       V1 의 "/도움" 은 그대로 서버(ChatHelpAnswerer)가 답한다 */
    if (engine === "V2" && isChatHelpCommandV2(typed)) {
      setError("");
      setDraftInput("");
      appendMessage("user", typed);
      setIsSending(true);
      /* 늦게 온 도움말은 그 사이 다른 방을 골랐으면 붙이지 않는다 */
      const helpSnapshot = chatRoomStore.getState().snapshot();
      try {
        const sections = await fetchChatHelpV2();
        if (!chatRoomStore.getState().isCurrent(helpSnapshot)) return;
        appendMessage("assistant", helpSectionsToMarkdown(sections));
      } catch (err) {
        if (!chatRoomStore.getState().isCurrent(helpSnapshot)) return;
        handleChatError(err);
        setDraftInput(typed);
      } finally {
        setIsSending(false);
      }
      return;
    }
    /* 채팅 번호 버튼이 떠 있으면 "3번" · 이름 입력은 그 후보를 고른 것 — 서버로 보내지 않는다 */
    if (fieldPickRef.current && draftAttachments.length === 0) {
      const index = matchFieldPick(typed);
      if (index >= 0) {
        setDraftInput("");
        await runFieldPick(index, typed);
        return;
      }
      fieldPickRef.current = null;
    }
    // 표 파일 파싱/제거 진행 중에는 전송 금지(§38)
    if (
      chatAttachment.state.status === "UPLOADING" ||
      chatAttachment.state.status === "REMOVING"
    ) {
      setError("파일 처리 중입니다. 완료 후 다시 전송해 주세요.");
      return;
    }
    if (
      chatDocument.state.status === "UPLOADING" ||
      chatDocument.state.status === "REMOVING"
    ) {
      setError("문서 처리 중입니다. 완료 후 다시 전송해 주세요.");
      return;
    }
    const readyAttachment =
      chatAttachment.state.status === "READY"
        ? chatAttachment.state.ready
        : null;
    // 문서 첨부 참조 — PROCESSING(진행 안내 응답)·READY 모두 서버가 정직하게 답한다.
    const documentAttachment =
      chatDocument.state.status === "PROCESSING" ||
      chatDocument.state.status === "READY"
        ? chatDocument.state
        : null;
    if (documentAttachment && draftAttachments.length > 0) {
      setError(
        "문서 첨부와 텍스트 첨부는 함께 보낼 수 없습니다. 한쪽을 제거해 주세요."
      );
      return;
    }
    if (documentAttachment && !typed) {
      setError("메시지를 입력해 주세요.");
      return;
    }
    // §39 최종 불변식: 업로드 파일 + 붙여넣기 칩 혼합 전송 금지
    if (readyAttachment && draftAttachments.length > 0) {
      setError(
        "표 파일과 텍스트 첨부는 함께 보낼 수 없습니다. 한쪽을 제거해 주세요."
      );
      return;
    }
    if (readyAttachment && !typed) {
      setError("메시지를 입력해 주세요.");
      return;
    }
    const capturedAttachmentGeneration = chatAttachment.generation();
    const capturedDocumentGeneration = chatDocument.generation();

    // 표시용(말풍선)은 입력 문장 + 첨부 칩, 전송용(AI)은 첨부 본문까지 합친 전체
    // (V1 · 화면 명령 경로. V2 는 입력한 글만 보내고 긴 글 · 그림은 attachments 참조로 — BE 가 합친다, 2026-10-26)
    const outgoing = buildOutgoingMessage(typed, draftAttachments);
    const sentAttachments = draftAttachments;
    const attachmentsMeta: MessageAttachmentMeta[] = sentAttachments.map(
      (att) => ({
        name: att.name,
        charCount: att.charCount,
      })
    );
    /* V2 첨부 참조 — 긴 글 칩은 인라인, 그림은 서버 id. 올리는 중인 그림이 있으면 기다리게 한다 */
    /* 트레이 그림은 입력칸에서 보낼 때만 — 줄 세운 질문이 그 사이 새로 붙인 그림을 가져가면 안 된다 (리뷰 2026-10-27) */
    const sentImages = engine === "V2" && fromInput ? v2Images : [];
    if (sentImages.some((c) => c.status !== "READY" || !c.id)) {
      setError("그림을 올리는 중입니다. 잠시 후 보내 주세요.");
      return;
    }
    if (engine === "V2" && sentImages.length > 0 && !typed) {
      setError(
        sentImages.some((c) => c.kind === "DOCUMENT")
          ? "문서와 함께 보낼 질문을 입력해 주세요."
          : sentImages.some((c) => c.kind === "TABLE")
            ? "표와 함께 보낼 질문을 입력해 주세요."
            : "그림과 함께 보낼 질문을 입력해 주세요."
      );
      return;
    }
    /* 메뉴디자이너는 첨부를 받지 않는다 — 긴 글은 예전처럼 질문에 합쳐 보낸다 (리뷰 2026-10-27) */
    const attachmentRefs: ChatAttachmentRefV2[] | undefined =
      engine !== "V2" || isMenuDesigner
        ? undefined
        : carriedRefs && carriedRefs.length > 0
          ? carriedRefs
          : draftAttachments.length > 0 || sentImages.length > 0
            ? [
                ...draftAttachments.map((att) => ({
                  kind: "TEXT" as const,
                  name: att.name,
                  text: att.content,
                })),
                ...sentImages.map((c) => ({
                  id: c.id as string,
                  kind: c.kind,
                  name: c.name,
                })),
              ]
            : undefined;
    for (const c of sentImages) {
      attachmentsMeta.push({
        name: c.name,
        charCount: 0,
        kind:
          c.kind === "DOCUMENT"
            ? "file"
            : c.kind === "TABLE"
              ? "table"
              : "image",
        previewUrl: c.previewUrl,
        fileId: c.id,
      });
    }
    // 파일 칩은 새로 첨부·교체된 첫 턴에만 말풍선에 표시(입력창 칩이 지속 상태를 담당)
    if (
      readyAttachment &&
      announcedAttachmentIdRef.current !== readyAttachment.attachmentId
    ) {
      attachmentsMeta.push({
        name: readyAttachment.displayName,
        charCount: 0,
        kind: "file",
      });
      announcedAttachmentIdRef.current = readyAttachment.attachmentId;
    }
    if (
      documentAttachment &&
      announcedAttachmentIdRef.current !== documentAttachment.attachmentId
    ) {
      attachmentsMeta.push({
        name: documentAttachment.displayName,
        charCount: 0,
        kind: "file",
      });
      announcedAttachmentIdRef.current = documentAttachment.attachmentId;
    }

    const isScreenCommand = isChatCommandTarget();
    /* 답장은 AI Center 엔진만 — 보낸 뒤 띠를 비운다 */
    const quoteText =
      engine === "V2" && fromInput && replyTo ? replyTo : undefined;

    setError("");
    setDraftInput("");
    if (fromInput) setAttachments([]);
    /* 보낸 그림 칩은 트레이에서 빠진다(미리보기 URL 은 말풍선이 쓰므로 그대로). 서버 보관은 TTL 이 걷는다 */
    if (fromInput && sentImages.length > 0) setV2Images([]);
    if (fromInput) setReplyTo(null);
    appendMessage(
      "user",
      typed,
      undefined,
      undefined,
      undefined,
      undefined,
      attachmentsMeta,
      undefined,
      undefined,
      undefined,
      undefined,
      /* idCall 자리 — 전에는 quoteText 가 여기(13번째)에 들어가 quote 칸이 비었다. 답장 원문은 quote 칸으로 (2026-10-26) */
      undefined,
      quoteText,
      attachmentRefs
    );
    setIsSending(true);

    // 활성 탭이 명령 핸들러를 등록해 뒀으면 그 화면 명령으로 라우팅
    // (등록 주체는 chatCommandBridge가 관리 — 챗봇은 어떤 화면인지 알지 않는다)
    if (isScreenCommand) {
      try {
        const reply = await sendChatCommand(outgoing);
        appendMessage("assistant", reply);
      } finally {
        setIsSending(false);
      }
      return;
    }

    /* V2 — 보내기 · 답 붙이기 · 화면 조작 · 신호 · 압축은 runV2Turn 이 한다. "다시 시도" 와 같은 길이다 (2026-10-14) */
    if (engine === "V2") {
      const mySeq = ++sendSeqRef.current;
      try {
        /* 입력한 글만 보낸다 — 긴 글 · 그림은 attachmentRefs 로 (2026-10-26) */
        const ok = await runV2Turn(
          attachmentRefs ? typed : outgoing,
          typed,
          false,
          quoteText,
          screenOnly,
          attachmentRefs
        );
        if (!ok) {
          setDraftInput(typed);
          /* 실패한 요청의 칩만 앞에 되돌린다 — 기다리는 사이 새로 붙인 칩을 덮어쓰지 않는다 (재리뷰 4) */
          if (fromInput)
            setAttachments((prev) => [
              ...sentAttachments.filter((a) => !prev.includes(a)),
              ...prev,
            ]);
          if (fromInput && sentImages.length > 0)
            setV2Images((prev) => [
              ...sentImages.filter((c) => !prev.some((p) => p.key === c.key)),
              ...prev,
            ]);
        }
      } finally {
        /* 중지 뒤 새 전송이 시작됐으면 옛 전송의 뒷정리가 보내는 중 표시를 끄지 않는다 */
        if (sendSeqRef.current === mySeq) setIsSending(false);
      }
      return;
    }

    // 재리뷰 #5: 타이핑한 시트명 선택 — 보류 라벨과 정규화 일치하면 성공 후 로컬 선택 반영
    const normalizedTyped = typed.replace(/\s+/g, "").toUpperCase();
    const typedSelectionLabel =
      readyAttachment &&
      pendingSelectionRef.current?.attachmentId === readyAttachment.attachmentId
        ? pendingSelectionRef.current.labels.find(
            (label) =>
              label.replace(/\s+/g, "").toUpperCase() === normalizedTyped
          )
        : undefined;
    try {
      const response = await sendChatMessage(
        outgoing,
        readyAttachment
          ? {
              attachmentRef: {
                attachmentId: readyAttachment.attachmentId,
                ...(readyAttachment.selectedTableId
                  ? { tableId: readyAttachment.selectedTableId }
                  : {}),
              },
            }
          : documentAttachment
            ? {
                attachmentRef: {
                  attachmentId: documentAttachment.attachmentId,
                },
              }
            : undefined
      );
      const reply: ChatReply | null | undefined = response.data;
      if (
        typedSelectionLabel &&
        reply?.sourceKind === "ATTACHMENT" &&
        reply?.clarification?.kind !== "TABLE_SELECTION" &&
        readyAttachment
      ) {
        const matched = readyAttachment.tables.find(
          (table) => table.label === typedSelectionLabel
        );
        if (matched) {
          chatAttachment.selectTable(matched.tableId);
        }
        pendingSelectionRef.current = null;
      }
      await handleScreenReply(
        reply,
        capturedAttachmentGeneration,
        capturedDocumentGeneration
      );
    } catch (err) {
      handleChatError(err);
      setDraftInput(typed);
      if (fromInput) setAttachments(sentAttachments);
    } finally {
      setIsSending(false);
    }
  };

  /* 앞 답이 끝나면 줄의 맨 앞 질문을 보낸다 — 한 번에 하나씩 (2026-10-17) */
  useEffect(() => {
    if (isSending || isClearing || isRoomLoading || queued.length === 0) return;
    const [next, ...rest] = queued;
    setQueued(rest);
    void sendMessage(next.text, false, next.screenOnly === true, next.refs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSending, isClearing, isRoomLoading, queued]);

  /** 보내기 — 답을 기다리는 동안이면 줄 세운다. 앞 답이 끝나면 차례로 나간다 (Claude 처럼, 2026-10-17).
      첨부 · 답장이 붙은 질문은 줄 세우지 않는다 — 보낼 때의 초안이 바뀌어 엉뚱한 것이 붙는다 */
  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const typed = input.trim();
    if (isSending && !isClearing && engine === "V2") {
      if (!typed) return;
      if (attachments.length > 0 || v2Images.length > 0 || replyTo) {
        setError("첨부나 답장이 붙은 질문은 답이 끝난 뒤에 보내 주세요.");
        return;
      }
      setQueued((prev) => [...prev, { id: ++queueIdRef.current, text: typed }]);
      setInput("");
      return;
    }
    await sendMessage(typed, true);
  };

  const searchApplyErrorMessage = (reason?: string) => {
    switch (reason) {
      case "NO_ACTIVE_SEARCH_PANEL":
        return "이 메뉴에는 조회조건이 없습니다.";
      case "MENU_MISMATCH":
        return "SNotes AI Bot 응답 화면과 현재 활성 화면이 달라 검색조건을 적용하지 않았습니다.";
      case "NO_ALLOWED_PARAMS":
        return "이 메뉴 조회조건에 없는 항목이라 넣지 않았습니다.";
      case "SEARCH_NOT_RUN":
        return "검색조건은 확인했지만 현재 화면의 조회 실행 함수를 찾지 못했습니다.";
      case "RESET_UNSUPPORTED":
        return "이 메뉴는 조회조건 초기화를 지원하지 않습니다.";
      default:
        return "현재 화면 검색조건에 적용할 수 없는 요청입니다.";
    }
  };

  const hasChartData = (
    chart: ChatChartPayload | undefined
  ): chart is ChatChartPayload => Boolean(chart && chart.data.length > 0);

  const hasTableData = (
    table: ChatTablePayload | undefined
  ): table is ChatTablePayload =>
    Boolean(table && table.columns.length > 0 && table.rows.length > 0);

  const chartType = (type: string | undefined): RenderableChartType => {
    // 백엔드 LLM 경로가 대문자("PIE")를 보내던 이력 — 대소문자 무시로 안전하게.
    const normalized = type?.toLowerCase();
    switch (normalized) {
      case "line":
      case "pie":
      case "donut":
        return normalized;
      default:
        return "bar";
    }
  };

  // 현재 칩의 문서 id(렌더 파생값) — 교체된 문서의 후보/페이징 버튼은 410을 부르고 그 처리기가
  // 현재 칩까지 지웠다(리뷰 F4). 이 id와 다른 메시지의 버튼은 비활성.
  const activeDocumentId =
    chatDocument.state.status === "READY" ||
    chatDocument.state.status === "PROCESSING"
      ? chatDocument.state.attachmentId
      : undefined;

  return (
    <section
      className={`chatbot-page${isMenuDesigner || roomsDocked ? " has-sidebar" : ""}${
        showRooms ? " has-rooms-handle" : ""
      }${isNarrow ? " is-narrow" : ""}${isDragOver ? " is-dropzone" : ""}`}
      aria-label="SNotes AI Bot"
      ref={pageRef}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {isDragOver && (
        <div className="chatbot-dropzone-overlay" aria-hidden="true">
          <AttachFileIcon fontSize="large" />
          <span>
            {docCapability?.enabled
              ? "표(xlsx/csv) 또는 문서(PDF/PNG/JPEG) 파일을 여기에 놓으면 첨부됩니다"
              : "표 파일(xlsx/csv)을 여기에 놓으면 첨부됩니다"}
          </span>
          <span className="chatbot-dropzone-notice">
            {ATTACHMENT_PRIVACY_NOTICE}
          </span>
        </div>
      )}
      {/* 좌측 작업 메뉴 — 메뉴디자이너 탭이 활성일 때만 노출 */}
      {isMenuDesigner && (
        <DesignerSidebar
          isReady={Boolean(designerActions)}
          onNewChat={startNewChat}
        />
      )}
      {/* 좌측 대화 목록 — AI Center 엔진이고 디자이너 탭이 아닐 때, 왼쪽 아이콘 줄로 연다. 늘 옆에 붙는다 */}
      {/* 왼쪽 아이콘 줄 — 카톡 PC 처럼 기능을 아이콘으로 둔다. 지금은 대화 목록 하나, 나중에 더한다 (2026-10-16) */}
      {showRooms && (
        <nav className="chatbot-rail" aria-label="챗봇 기능">
          <ChatTip title="대화 목록" placement="right">
            <button
              aria-expanded={roomsOpen}
              aria-label={roomsOpen ? "대화 목록 접기" : "대화 목록 펼치기"}
              className={`chatbot-rail-item${roomsOpen ? " is-active" : ""}`}
              onClick={() => setRoomsOpen((v) => !v)}
              type="button"
            >
              {/* 카톡 채팅 탭처럼 펼치면 속이 채워진다 */}
              {roomsOpen ? <ChatBubbleIcon /> : <ChatBubbleOutlineIcon />}
            </button>
          </ChatTip>
        </nav>
      )}
      {showRooms && roomsOpen && (
        <RoomSidebar
          error={roomsError}
          loading={roomsLoading}
          menuGroup={menuGroup}
          menuName={menuName}
          onHide={handleRoomHide}
          onNew={startNewRoom}
          onQueryChange={setRoomQuery}
          onRefresh={() => void loadRooms()}
          onRename={handleRoomRename}
          onResize={handleRoomsResize}
          onRestore={handleRoomRestore}
          tagsEnabled={tagsReady}
          tagFilter={tagFilter}
          tagHits={tagHits}
          tagHitsLoading={tagHitsLoading}
          onTagOpen={(anchor) => openTagPalette(anchor, "filter")}
          onTagClear={() => {
            setTagFilter(null);
            setTagHits(null);
          }}
          onOpenHit={(hit) => void openTagHit(hit)}
          onSelect={(room) => void openRoom(room)}
          onViewChange={setRoomView}
          query={roomQuery}
          rooms={visibleRooms}
          selectedId={roomSelectedId}
          view={roomView}
          width={roomsWidth}
        />
      )}

      <div className="chatbot-shell" ref={shellRef}>
        <div className="chatbot-header">
          <div className="chatbot-title">
            <SmartToyIcon fontSize="small" />
            <h1>SNotes AI Bot</h1>
          </div>

          <div className="chatbot-header-tools">
            {/* 대화 내용 검색 — 카톡 PC 대화방의 🔍 (2026-10-22) */}
            {showRooms && (
              <ChatTip title="대화 검색">
                <button
                  aria-label="대화 검색"
                  aria-pressed={searchOpen}
                  className={`chatbot-rooms-toggle${searchOpen ? " is-open" : ""}`}
                  onClick={() => {
                    if (searchOpen) {
                      closeSearch();
                      return;
                    }
                    setSearchOpen(true);
                    requestAnimationFrame(() =>
                      searchInputRef.current?.focus()
                    );
                  }}
                  type="button"
                >
                  <SearchIcon fontSize="small" />
                </button>
              </ChatTip>
            )}
            {/* 엔진 고르기(레거시 / AI Center)는 없앴다 — AI Center 로만 부른다 (2026-10-16).
                그 자리에 [새 대화] — ≡ 안에 있던 것을 꺼냈다 */}
            {showRooms && (
              <button
                className="chatbot-new-chat"
                disabled={isSending || isClearing}
                onClick={() => void handleClearChat()}
                type="button"
              >
                <MapsUgcOutlinedIcon fontSize="small" />
                <span>새 대화</span>
              </button>
            )}

            {/* 위쪽 메뉴(≡) — 대화 목록 · 새 대화 · 대화 내보내기를 한곳에 (카톡 PC 처럼, 2026-10-15). 대화방이 있는 AI Center 엔진만.
                대화 목록은 전에 따로 있던 아이콘(2026-10-07)을 옮긴 것이다 */}
            {showRooms && (
              <ChatTip title="메뉴">
                <button
                  aria-label="메뉴"
                  aria-pressed={headerMenuEl !== null}
                  className={`chatbot-rooms-toggle${headerMenuEl !== null ? " is-open" : ""}`}
                  disabled={isClearing}
                  onClick={(event) => setHeaderMenuEl(event.currentTarget)}
                  type="button"
                >
                  <MenuIcon fontSize="small" />
                </button>
              </ChatTip>
            )}
            {/* 말풍선 우클릭 — 평소엔 복사(그 말풍선을 그림으로 클립보드) · 캡처(그 말풍선을 고른 채 캡처 모드),
                캡처 중엔 고른 것 전체를 복사 · 저장 (카톡 PC 처럼, 2026-10-17) */}
            <Menu
              anchorReference="anchorPosition"
              anchorPosition={
                msgMenu ? { top: msgMenu.y, left: msgMenu.x } : undefined
              }
              onClose={() => setMsgMenu(null)}
              open={msgMenu !== null}
            >
              {captureOn
                ? [
                    <MenuItem
                      dense
                      key="copy"
                      onClick={() => {
                        const id = msgMenu?.id;
                        setMsgMenu(null);
                        void handleCaptureSave(
                          true,
                          captureSel.length > 0
                            ? undefined
                            : id != null
                              ? [id]
                              : undefined
                        );
                      }}
                    >
                      <ListItemIcon>
                        <ContentCopyIcon fontSize="small" color="primary" />
                      </ListItemIcon>
                      <ListItemText>복사</ListItemText>
                    </MenuItem>,
                    <MenuItem
                      dense
                      key="save"
                      onClick={() => {
                        const id = msgMenu?.id;
                        setMsgMenu(null);
                        void handleCaptureSave(
                          false,
                          captureSel.length > 0
                            ? undefined
                            : id != null
                              ? [id]
                              : undefined
                        );
                      }}
                    >
                      <ListItemIcon>
                        <IosShareOutlinedIcon
                          fontSize="small"
                          color="primary"
                        />
                      </ListItemIcon>
                      <ListItemText>저장</ListItemText>
                    </MenuItem>,
                  ]
                : msgMenu?.inSel && captureSel.length > 0
                  ? [
                      /* 고른 말풍선 위 — 복사는 그림으로, 캡처는 그 선택 그대로 캡처 모드 */
                      <MenuItem
                        dense
                        key="copy"
                        onClick={() => {
                          setMsgMenu(null);
                          void handleCaptureSave(true);
                        }}
                      >
                        <ListItemIcon>
                          <ContentCopyIcon fontSize="small" color="primary" />
                        </ListItemIcon>
                        <ListItemText>복사</ListItemText>
                      </MenuItem>,
                      <MenuItem
                        dense
                        key="capture"
                        onClick={() => {
                          setMsgMenu(null);
                          setReplyTo(null);
                          setCaptureOn(true);
                        }}
                      >
                        <ListItemIcon>
                          <CropFreeOutlinedIcon
                            fontSize="small"
                            color="primary"
                          />
                        </ListItemIcon>
                        <ListItemText>캡처</ListItemText>
                      </MenuItem>,
                    ]
                  : [
                      /* 말풍선 하나 — 복사는 글(고른 글자가 있으면 그것), 캡처는 그 말풍선을 고른 채 캡처 모드 */
                      <MenuItem
                        dense
                        key="copy"
                        onClick={() => {
                          const menu = msgMenu;
                          setMsgMenu(null);
                          const text =
                            menu?.text ||
                            messages.find((m) => m.id === menu?.id)?.text ||
                            "";
                          if (!text) return;
                          void copyChatText(text).then((ok) => {
                            setCaptureNotice(
                              ok ? "복사했습니다." : "복사하지 못했습니다."
                            );
                            window.setTimeout(() => setCaptureNotice(""), 1500);
                          });
                        }}
                      >
                        <ListItemIcon>
                          <ContentCopyIcon fontSize="small" color="primary" />
                        </ListItemIcon>
                        <ListItemText>복사</ListItemText>
                      </MenuItem>,
                      <MenuItem
                        dense
                        key="capture"
                        onClick={() => {
                          const id = msgMenu?.id;
                          setMsgMenu(null);
                          setReplyTo(null);
                          setCaptureSel(id != null ? [id] : []);
                          setCaptureOn(true);
                        }}
                      >
                        <ListItemIcon>
                          <CropFreeOutlinedIcon
                            fontSize="small"
                            color="primary"
                          />
                        </ListItemIcon>
                        <ListItemText>캡처</ListItemText>
                      </MenuItem>,
                      /* 태그 — 저장된 턴의 말풍선만(턴 번호가 있어야 단다) (2026-10-22) */
                      ...(tagsReady &&
                      roomSelectedId &&
                      messages.find((m) => m.id === msgMenu?.id)?.turnSeq !=
                        null
                        ? [
                            <MenuItem
                              dense
                              key="tag"
                              onClick={() => {
                                const menu = msgMenu;
                                setMsgMenu(null);
                                if (menu)
                                  setTagPalette({
                                    x: menu.x,
                                    y: menu.y,
                                    mode: "apply",
                                    msgId: menu.id,
                                  });
                              }}
                            >
                              <ListItemIcon>
                                <LocalOfferOutlinedIcon
                                  fontSize="small"
                                  color="primary"
                                />
                              </ListItemIcon>
                              <ListItemText>태그</ListItemText>
                            </MenuItem>,
                          ]
                        : []),
                    ]}
            </Menu>
            {/* 태그 판 — 말풍선에 달기 · 이 방에서 찾기 · 모든 방에서 모아 보기 (2026-10-22) */}
            <Popover
              anchorPosition={
                tagPalette
                  ? { top: tagPalette.y, left: tagPalette.x }
                  : undefined
              }
              anchorReference="anchorPosition"
              onClose={() => setTagPalette(null)}
              open={tagPalette !== null}
            >
              {tagPalette && (
                <TagPalette
                  applied={
                    messages.find((m) => m.id === tagPalette.msgId)?.tags ?? []
                  }
                  mode={tagPalette.mode === "apply" ? "apply" : "pick"}
                  onCreate={createTag}
                  onHide={hideTag}
                  onPick={(tag) => {
                    const target = tagPalette;
                    setTagPalette(null);
                    if (target.mode === "apply")
                      void applyTag(tag, target.msgId);
                    else if (target.mode === "pick") void runTagSearch(tag);
                    else void pickTagFilter(tag);
                  }}
                  onUpdate={updateTag}
                  tags={myTags}
                />
              )}
            </Popover>
            <Menu
              anchorEl={headerMenuEl}
              open={headerMenuEl !== null}
              onClose={() => setHeaderMenuEl(null)}
            >
              {/* 대화 목록 · 새 대화는 밖으로 꺼냈다(왼쪽 세로 탭 · 머리 버튼, 2026-10-16) */}
              <ListSubheader sx={menuHeadSx}>대화 내용</ListSubheader>
              <MenuItem
                dense
                disabled={!activeRoom || isExporting}
                onClick={() => void handleExportChat()}
              >
                <ListItemIcon>
                  <IosShareOutlinedIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText>대화 내보내기</ListItemText>
              </MenuItem>
              <MenuItem
                dense
                disabled={isSending || isClearing || messages.length === 0}
                onClick={() => {
                  setHeaderMenuEl(null);
                  setReplyTo(null);
                  setCaptureSel([]);
                  setCaptureOn(true);
                }}
              >
                <ListItemIcon>
                  <CropFreeOutlinedIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText>대화 캡처</ListItemText>
              </MenuItem>
            </Menu>

            {/* 예전 엔진은 위쪽 메뉴가 없어 지우기 버튼을 그대로 둔다 */}
            {!showRooms && (
              <ChatTip title="대화 지우기">
                <button
                  aria-label="대화 지우기"
                  className="chatbot-history-toggle"
                  disabled={isSending || isClearing}
                  onClick={handleClearChat}
                  type="button"
                >
                  <DeleteSweepIcon fontSize="small" />
                </button>
              </ChatTip>
            )}
          </div>
        </div>

        {/* 복원한 방의 머리 — 어디서 시작한 대화인지. 메뉴를 열지는 않는다. 지금 화면에서 이어서 물을 수 있다 */}
        {showRooms && activeRoom && (
          <div className="chatbot-room-source">
            시작 메뉴:{" "}
            <strong>
              {activeRoom.cdMenu ? menuName(activeRoom.cdMenu) : "없음"}
            </strong>
            {activeRoom.noIssue ? ` · 이슈 ${activeRoom.noIssue}` : ""}
            {" · 지금 화면에서 이어서 질문할 수 있습니다"}
          </div>
        )}

        {/* 대화 검색줄 — Enter · ▲ 오래된 쪽, Shift+Enter · ▼ 최근 쪽, Esc 닫기 (카톡 PC 처럼, 2026-10-22) */}
        {showRooms && searchOpen && (
          <div className="chatbot-search-bar">
            <SearchIcon fontSize="small" className="chatbot-search-icon" />
            {searchTag && (
              <span className="chatbot-search-tag">
                <TagIcon code={searchTag.cdIcon} />
                {searchTag.nmTag}
                <button
                  aria-label="태그 찾기 끄기"
                  className="chatbot-search-tag-off"
                  onClick={() => {
                    setSearchTag(null);
                    setSearchTerm("");
                  }}
                  type="button"
                >
                  <CloseIcon sx={{ fontSize: 12 }} />
                </button>
              </span>
            )}
            <input
              aria-label="대화 내용 검색"
              className="chatbot-search-input"
              maxLength={100}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (e.shiftKey) moveSearch(1);
                  else void runSearch();
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  closeSearch();
                }
              }}
              placeholder="대화 내용 검색"
              ref={searchInputRef}
              value={searchText}
            />
            <span className="chatbot-search-count">
              {searchBusy
                ? "찾는 중…"
                : searchTerm || searchTag
                  ? searchCount > 0
                    ? `${searchIndex + 1} / ${searchCount}`
                    : "결과 없음"
                  : ""}
            </span>
            {tagsReady && (
              <ChatTip title="태그로 찾기">
                <button
                  aria-label="태그로 찾기"
                  className="chatbot-search-nav"
                  onClick={(e) => openTagPalette(e.currentTarget, "pick")}
                  type="button"
                >
                  <LocalOfferOutlinedIcon fontSize="small" />
                </button>
              </ChatTip>
            )}
            <ChatTip title="이전">
              <button
                aria-label="이전 결과"
                className="chatbot-search-nav"
                disabled={searchCount === 0}
                onClick={() => moveSearch(-1)}
                type="button"
              >
                <KeyboardArrowUpIcon fontSize="small" />
              </button>
            </ChatTip>
            <ChatTip title="다음">
              <button
                aria-label="다음 결과"
                className="chatbot-search-nav"
                disabled={searchCount === 0}
                onClick={() => moveSearch(1)}
                type="button"
              >
                <KeyboardArrowDownIcon fontSize="small" />
              </button>
            </ChatTip>
            <ChatTip title="닫기">
              <button
                aria-label="검색 닫기"
                className="chatbot-search-nav"
                onClick={closeSearch}
                type="button"
              >
                <CloseIcon fontSize="small" />
              </button>
            </ChatTip>
          </div>
        )}

        <div
          className={`chatbot-messages${captureOn ? " is-capture-mode" : ""}${captureSaving ? " is-capturing" : ""}`}
          aria-live="polite"
          onMouseDown={startBlockDrag}
          ref={messagesRef}
        >
          <ChatPrivacyNotice />
          {showRooms && history?.hasMore && (
            <button
              className="chatbot-history-more"
              disabled={historyLoading}
              onClick={() => void loadOlderTurns()}
              type="button"
            >
              {historyLoading ? "불러오는 중…" : "이전 대화 더 보기"}
            </button>
          )}
          {messages.map((message, index) => (
            <React.Fragment key={message.id}>
              {timeline[index]?.date &&
                (!captureSaving || captureSel.includes(message.id)) && (
                  <div className="chatbot-date-divider" role="separator">
                    <span>{timeline[index].date}</span>
                  </div>
                )}
              <div
                className={`chatbot-message chatbot-message-${message.role}${
                  message.isError ? " is-error" : ""
                }${captureSel.includes(message.id) ? " is-captured" : ""}${
                  captureSaving && !captureSel.includes(message.id)
                    ? " is-capture-hidden"
                    : ""
                }`}
                onContextMenu={(event) => {
                  if (captureSaving) return;
                  event.preventDefault();
                  /* 그 말풍선 안에서 고른 글자 — 있으면 글 복사에 쓴다 */
                  const picked = window.getSelection();
                  const text =
                    picked &&
                    picked.rangeCount > 0 &&
                    event.currentTarget.contains(picked.anchorNode)
                      ? picked.toString().trim()
                      : "";
                  setMsgMenu({
                    x: event.clientX,
                    y: event.clientY,
                    id: message.id,
                    text,
                    inSel: captureSel.includes(message.id),
                  });
                }}
                data-msg-id={message.id}
              >
                {captureOn && (
                  <span className="chatbot-capture-check" aria-hidden="true">
                    {captureSel.includes(message.id) ? (
                      <CheckCircleIcon fontSize="small" />
                    ) : (
                      <RadioButtonUncheckedIcon fontSize="small" />
                    )}
                  </span>
                )}
                <div className="chatbot-avatar" aria-hidden="true">
                  {message.role === "assistant" ? (
                    <SmartToyIcon fontSize="small" />
                  ) : (
                    <PersonIcon fontSize="small" />
                  )}
                </div>
                <div className="chatbot-message-content">
                  {/* 에이전트 배지(v0.6.0) — 메뉴 디자이너 v0.0.3 머지에서 렌더부만 유실되어 복구 */}
                  {message.role === "assistant" && message.agent && (
                    <AgentBadge agent={message.agent} />
                  )}
                  {message.isError && (
                    <span className="chatbot-message-failed">실패한 요청</span>
                  )}
                  {message.role === "assistant" ? (
                    message.sourceKind === "ATTACHMENT" ? (
                      <div className="chatbot-message-literal">
                        <DocumentQualityBadge meta={message.documentMeta} />
                        {message.text}
                        {message.selectionOptions &&
                          message.selectionOptions.length > 0 && (
                            <div className="chatbot-selection-options">
                              {message.selectionOptions.map((option) => (
                                <button
                                  key={option.optionId}
                                  type="button"
                                  className="chatbot-selection-option"
                                  disabled={isSending || isClearing}
                                  onClick={() =>
                                    message.selectionAttachmentId &&
                                    handleTableSelectionClick(
                                      message.selectionAttachmentId,
                                      option.optionId,
                                      option.label
                                    )
                                  }
                                >
                                  {option.label}
                                </button>
                              ))}
                            </div>
                          )}
                        <DocumentAnswerExtras
                          attachmentId={
                            activeDocumentId === message.documentAttachmentId
                              ? activeDocumentId
                              : undefined
                          }
                          busy={isSending || isClearing}
                          candidates={message.documentCandidates}
                          meta={message.documentMeta}
                          onCandidate={(
                            attachmentId,
                            questionId,
                            candidateId
                          ) => {
                            void handleDocumentCandidateClick(
                              attachmentId,
                              questionId,
                              candidateId
                            );
                          }}
                          onPage={(attachmentId, offset) => {
                            void handleDocumentPageClick(attachmentId, offset);
                          }}
                          questionId={message.documentQuestionId}
                        />
                      </div>
                    ) : (
                      <div className="chatbot-message-markdown">
                        <AiMarkdown text={message.text} />
                        {message.selectionOptions &&
                          message.selectionOptions.length > 0 &&
                          !message.selectionAttachmentId && (
                            <div className="chatbot-selection-options">
                              {message.selectionOptions.map((option) => (
                                <button
                                  key={option.optionId}
                                  type="button"
                                  className="chatbot-selection-option"
                                  // 화면 되묻기 보류는 화면당 1슬롯(최신 것으로 교체) — 지난 말풍선의 버튼을
                                  // 누르면 다른 되묻기에 매칭되므로, 마지막 메시지의 버튼만 활성(리뷰 2026-09-01).
                                  disabled={
                                    isSending ||
                                    isClearing ||
                                    messages[messages.length - 1]?.id !==
                                      message.id
                                  }
                                  onClick={() =>
                                    handleScreenOptionClick(option)
                                  }
                                >
                                  {option.label}
                                </button>
                              ))}
                            </div>
                          )}
                      </div>
                    )
                  ) : (
                    <>
                      {message.quote && (
                        <button
                          className="chatbot-quote-ref"
                          onClick={() =>
                            scrollToQuoted(message.quote as string)
                          }
                          type="button"
                        >
                          <span className="chatbot-quote-ref-text">
                            {plainQuote(message.quote)}
                          </span>
                        </button>
                      )}
                      {message.text && <p>{message.text}</p>}
                      {message.attachments &&
                        message.attachments.length > 0 && (
                          <div className="chatbot-attachments chatbot-attachments-sent">
                            {message.attachments.map((att, index) => (
                              <div className="chatbot-attachment" key={index}>
                                {att.kind === "image" &&
                                (att.previewUrl || att.fileId) ? (
                                  <ChatAttachmentImage
                                    fileId={att.fileId}
                                    name={att.name}
                                    onNotice={setCaptureNotice}
                                    previewUrl={att.previewUrl}
                                  />
                                ) : att.kind === "table" ? (
                                  <TableChartOutlinedIcon
                                    className="chatbot-attachment-icon"
                                    fontSize="small"
                                  />
                                ) : att.kind === "file" &&
                                  (att.fileId || att.previewUrl) ? (
                                  <button
                                    aria-label={`${att.name} 열기`}
                                    className="chatbot-attachment-open"
                                    onClick={() =>
                                      void openAttachmentFile(
                                        att.fileId,
                                        att.previewUrl
                                      ).then((ok) => {
                                        if (!ok)
                                          setCaptureNotice(
                                            "문서를 찾을 수 없습니다."
                                          );
                                      })
                                    }
                                    title="새 탭에서 열기"
                                    type="button"
                                  >
                                    <DescriptionOutlinedIcon
                                      className="chatbot-attachment-icon"
                                      fontSize="small"
                                    />
                                  </button>
                                ) : (
                                  <DescriptionOutlinedIcon
                                    className="chatbot-attachment-icon"
                                    fontSize="small"
                                  />
                                )}
                                <div className="chatbot-attachment-meta">
                                  <span className="chatbot-attachment-name">
                                    {att.name}
                                  </span>
                                  <span className="chatbot-attachment-sub">
                                    {att.kind === "file"
                                      ? att.fileId || att.previewUrl
                                        ? "PDF · 눌러서 열기"
                                        : "표 파일"
                                      : att.kind === "image"
                                        ? "그림"
                                        : att.kind === "table"
                                          ? "표"
                                          : `${att.charCount.toLocaleString()}자 · 텍스트`}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                    </>
                  )}
                  {message.role === "assistant" &&
                    hasChartData(message.chart) && (
                      <AssistantChart
                        chart={message.chart}
                        type={chartType(message.chart.type)}
                        sourceKind={message.sourceKind}
                      />
                    )}
                  {message.role === "assistant" &&
                    !hasChartData(message.chart) &&
                    hasTableData(message.table) && (
                      <AssistantTable table={message.table} />
                    )}
                  {message.role === "assistant" && (
                    <AssistantFeedback message={message} />
                  )}
                  {message.role === "assistant" &&
                    !message.noChat &&
                    message.idCall && (
                      <AssistantFeedbackV2
                        idCall={message.idCall}
                        onRetry={
                          canRetry(message, index)
                            ? () => void handleRetry(message)
                            : undefined
                        }
                        retryDisabled={retryBusy}
                        copyText={
                          message.text.trim() ? message.text : undefined
                        }
                      />
                    )}
                  {/* 평가 줄이 없는 답(복원한 턴 · 안내 글 · /도움) — 복사, 그리고 마지막 답이면 "다시 시도" (2026-10-14 · 2026-10-15) */}
                  {message.role === "assistant" &&
                    !message.idCall &&
                    !feedbackTargetOf(message) &&
                    !message.isError &&
                    message.text.trim() && (
                      <div className="chatbot-feedback">
                        <CopyButton text={message.text} />
                        {canRetry(message, index) && (
                          <RetryButton
                            disabled={retryBusy}
                            onClick={() => void handleRetry(message)}
                          />
                        )}
                      </div>
                    )}
                </div>
                {/* 말풍선 아래 끝 — 시간과 답장(마우스를 올렸을 때만)을 나란히, 카톡 PC 처럼 (2026-10-17) */}
                <span className="chatbot-message-side">
                  {/* 단 태그 — 아이콘, 마우스를 올리면 이름 (2026-10-22). 숨긴 태그는 안 보인다 */}
                  {message.tags && message.tags.length > 0 && (
                    <span className="chatbot-msg-tags">
                      {message.tags.map((no) => {
                        const tag = myTags.find((t) => t.noTag === no);
                        return tag ? (
                          <ChatTip key={no} title={tag.nmTag} placement="top">
                            <span className="chatbot-msg-tag">
                              <TagIcon code={tag.cdIcon} size={13} />
                            </span>
                          </ChatTip>
                        ) : null;
                      })}
                    </span>
                  )}
                  {timeline[index]?.time &&
                    (message.role === "assistant" &&
                    typeof message.point === "number" ? (
                      <ChatTip title={pointTip(message.point)} placement="top">
                        <span className="chatbot-message-time">
                          {timeline[index].time}
                        </span>
                      </ChatTip>
                    ) : (
                      <span className="chatbot-message-time">
                        {timeline[index].time}
                      </span>
                    ))}
                  {/* 캡처 모드에서는 답장 버튼을 그리지 않는다 — 고르는 데 방해만 된다 (2026-10-17) */}
                  {engine === "V2" &&
                    !captureOn &&
                    message.role === "assistant" &&
                    !message.isError &&
                    message.text.trim() !== "" &&
                    message.text !== LEGACY_ROOM_NOTICE && (
                      <ChatTip title="답장">
                        <button
                          aria-label="답장"
                          className="chatbot-reply-btn"
                          disabled={isSending || isClearing}
                          onClick={() => startReply(message.text)}
                          type="button"
                        >
                          <ReplyIcon fontSize="small" />
                        </button>
                      </ChatTip>
                    )}
                </span>
              </div>
            </React.Fragment>
          ))}
          {isSending && (
            <div className="chatbot-message chatbot-message-assistant">
              <div className="chatbot-avatar" aria-hidden="true">
                <SmartToyIcon fontSize="small" />
              </div>
              <p className="chatbot-working">
                <span className="chatbot-working-dot" />
                {/* 메뉴디자이너 대화는 화면 정의를 통째로 만들어 오래 걸린다 — 기다릴 시간을 알려 준다 (2026-09-30) */}
                {globalMenu.getState().activeMenuInfo?.cd_menu === DESIGNER_MENU
                  ? "화면을 구성하는 중입니다 (보통 30초~1분)…"
                  : "SNotes AI가 분석 중…"}
                {waitSec > 0 && (
                  <span className="chatbot-working-sec">{waitSec}초</span>
                )}
              </p>
            </div>
          )}
          {/* 줄 세운 질문 — 흐리게 · "대기 중" · 하나씩 취소 (2026-10-17) */}
          {queued.map((item) => (
            <div
              className="chatbot-message chatbot-message-user is-queued"
              key={`queued-${item.id}`}
            >
              <div className="chatbot-avatar" aria-hidden="true">
                <PersonIcon fontSize="small" />
              </div>
              <div className="chatbot-message-content">
                <p>{item.text}</p>
                <span className="chatbot-queued-mark">
                  대기 중
                  <button
                    className="chatbot-queued-cancel"
                    onClick={() =>
                      setQueued((prev) => prev.filter((q) => q.id !== item.id))
                    }
                    type="button"
                  >
                    취소
                  </button>
                </span>
              </div>
            </div>
          ))}
        </div>

        <form className="chatbot-input-area" onSubmit={handleSubmit}>
          {error && <div className="chatbot-error">{error}</div>}
          {captureNotice && (
            <div className="chatbot-capture-notice">{captureNotice}</div>
          )}
          {v2Images.length > 0 && (
            <div className="chatbot-attachments">
              {v2Images.map((c) => (
                <div
                  className={`chatbot-attachment${c.status === "UPLOADING" ? " chatbot-attachment-uploading" : ""}`}
                  key={c.key}
                  title={IMAGE_PRIVACY_NOTICE}
                >
                  {c.kind === "DOCUMENT" ? (
                    <DescriptionOutlinedIcon
                      className="chatbot-attachment-icon"
                      fontSize="small"
                    />
                  ) : c.kind === "TABLE" ? (
                    <TableChartOutlinedIcon
                      className="chatbot-attachment-icon"
                      fontSize="small"
                    />
                  ) : (
                    <img
                      className="chatbot-attachment-thumb"
                      src={c.previewUrl}
                      alt={c.name}
                    />
                  )}
                  <div className="chatbot-attachment-meta">
                    <span className="chatbot-attachment-name">{c.name}</span>
                    <span className="chatbot-attachment-sub">
                      {c.status === "UPLOADING"
                        ? "올리는 중…"
                        : `${Math.max(1, Math.round(c.size / 1024)).toLocaleString()}KB · ${
                            c.kind === "DOCUMENT"
                              ? `PDF${c.pages ? ` ${c.pages}쪽` : ""}`
                              : c.kind === "TABLE"
                                ? c.name.toLowerCase().endsWith(".csv")
                                  ? "CSV"
                                  : "엑셀"
                                : "그림"
                          }`}
                    </span>
                  </div>
                  {c.kind === "TABLE" && (
                    <button
                      className="chatbot-attachment-action"
                      disabled={isSending || isClearing}
                      onClick={() => void putTableIntoScreen(c)}
                      title="지금 화면의 엑셀 업로드 창을 열고 이 파일 내용을 채웁니다. 저장은 직접 눌러 주세요."
                      type="button"
                    >
                      화면에 넣기
                    </button>
                  )}
                  <button
                    aria-label="그림 첨부 삭제"
                    className="chatbot-attachment-remove"
                    disabled={isSending || isClearing}
                    onClick={() => removeV2Image(c.key)}
                    type="button"
                  >
                    <CloseIcon fontSize="inherit" />
                  </button>
                </div>
              ))}
            </div>
          )}
          {attachments.length > 0 && (
            <div className="chatbot-attachments">
              {attachments.map((att) => (
                <div
                  className="chatbot-attachment"
                  key={att.id}
                  title={att.content.slice(0, 200)}
                >
                  <DescriptionOutlinedIcon
                    className="chatbot-attachment-icon"
                    fontSize="small"
                  />
                  <div className="chatbot-attachment-meta">
                    <span className="chatbot-attachment-name">{att.name}</span>
                    <span className="chatbot-attachment-sub">
                      {att.charCount.toLocaleString()}자 · 텍스트
                    </span>
                  </div>
                  <button
                    aria-label="첨부 삭제"
                    className="chatbot-attachment-remove"
                    disabled={isSending || isClearing}
                    onClick={() => removeAttachment(att.id)}
                    type="button"
                  >
                    <CloseIcon fontSize="inherit" />
                  </button>
                </div>
              ))}
            </div>
          )}
          {chatAttachment.state.status !== "IDLE" && (
            <div className="chatbot-attachments">
              <div className="chatbot-attachment chatbot-attachment-file">
                <AttachFileIcon
                  className="chatbot-attachment-icon"
                  fontSize="small"
                />
                <div className="chatbot-attachment-meta">
                  <span className="chatbot-attachment-name">
                    {chatAttachment.state.status === "UPLOADING"
                      ? chatAttachment.state.displayName
                      : chatAttachment.state.ready.displayName}
                  </span>
                  <span className="chatbot-attachment-sub">
                    {chatAttachment.state.status === "UPLOADING"
                      ? "업로드 중…"
                      : chatAttachment.state.status === "REMOVING"
                        ? "제거 중…"
                        : `표 파일 · ${chatAttachment.state.ready.tables.length}개 시트`}
                  </span>
                </div>
                <button
                  aria-label="첨부 파일 제거"
                  className="chatbot-attachment-remove"
                  disabled={
                    isSending ||
                    isClearing ||
                    chatAttachment.state.status !== "READY"
                  }
                  onClick={handleAttachmentRemove}
                  type="button"
                >
                  <CloseIcon fontSize="inherit" />
                </button>
              </div>
            </div>
          )}
          {replyTo && (
            <div className="chatbot-reply-bar">
              <ReplyIcon className="chatbot-reply-bar-icon" fontSize="small" />
              <div className="chatbot-reply-bar-text">
                <span className="chatbot-reply-bar-label">AI 답에 답장</span>
                <span className="chatbot-reply-bar-quote">
                  {plainQuote(replyTo)}
                </span>
              </div>
              <button
                aria-label="답장 취소"
                className="chatbot-attachment-remove"
                disabled={isSending || isClearing}
                onClick={() => setReplyTo(null)}
                type="button"
              >
                <CloseIcon fontSize="inherit" />
              </button>
            </div>
          )}
          <ChatbotDocumentChip
            busy={isSending || isClearing}
            onDismiss={chatDocument.clearLocal}
            onRemove={handleDocumentRemove}
            onReupload={() => attachmentFileInputRef.current?.click()}
            state={chatDocument.state}
          />
          <ChatbotDocumentDisclosure capability={docCapability} />
          {/* 대화 캡처 중에는 입력칸 대신 고른 수 · 취소 · 저장 (카톡처럼) */}
          {captureOn && (
            <div className="chatbot-capture-bar">
              <span>
                {captureSel.length === 0
                  ? "캡처할 말풍선을 고르세요. 처음과 끝을 누르면 그 사이가 모두 선택됩니다."
                  : `${captureSel.length}개 선택`}
              </span>
              <button
                className="chatbot-capture-cancel"
                disabled={captureSaving}
                onClick={endCapture}
                type="button"
              >
                취소
              </button>
              <button
                className="chatbot-capture-cancel"
                disabled={captureSaving || captureSel.length === 0}
                onClick={() => void handleCaptureSave(true)}
                type="button"
              >
                복사
              </button>
              <button
                className="chatbot-capture-save"
                disabled={captureSaving || captureSel.length === 0}
                onClick={() => void handleCaptureSave()}
                type="button"
              >
                {captureSaving ? "만드는 중…" : "저장"}
              </button>
            </div>
          )}
          <div
            className="chatbot-input-row"
            style={captureOn ? { display: "none" } : undefined}
          >
            <input
              ref={attachmentFileInputRef}
              type="file"
              /* V2 는 그림(0단계) — 여러 장. 표 · 문서는 3 · 4단계에서 (2026-10-26) */
              accept={
                engine === "V2"
                  ? ".png,.jpg,.jpeg,.pdf,.xlsx,.csv"
                  : attachmentAcceptFor(docCapability)
              }
              multiple={engine === "V2"}
              style={{ display: "none" }}
              onChange={(event) => {
                if (engine === "V2" && event.target.files) {
                  addV2ImageFiles(event.target.files);
                  event.target.value = "";
                  return;
                }
                void handleAttachmentFile(event.target.files?.[0]);
              }}
            />
            <button
              aria-label={
                engine === "V2"
                  ? "그림 첨부"
                  : docCapability?.enabled
                    ? "표/문서 파일 첨부"
                    : "표 파일 첨부"
              }
              className="chatbot-attach-button"
              title={
                engine === "V2"
                  ? IMAGE_PRIVACY_NOTICE
                  : ATTACHMENT_PRIVACY_NOTICE
              }
              disabled={
                isSending ||
                isClearing ||
                (engine === "V2" && isMenuDesigner) ||
                chatAttachment.state.status === "UPLOADING" ||
                chatAttachment.state.status === "REMOVING" ||
                chatDocument.state.status === "UPLOADING" ||
                chatDocument.state.status === "REMOVING"
              }
              onClick={() => attachmentFileInputRef.current?.click()}
              type="button"
            >
              <AttachFileIcon fontSize="small" />
            </button>
            <textarea
              ref={inputRef}
              aria-label="SNotes AI Bot 메시지"
              /* 답을 기다리는 동안에도 다음 질문을 쳐 둘 수 있다. 보내기(Enter)는 handleSubmit 이 답이 끝날 때까지 막고 글은 그대로 둔다 (2026-10-17) */
              disabled={isClearing}
              onChange={(event) => {
                recallIndexRef.current = null; // 손으로 고치면 그것이 새 초안이다
                setInput(event.target.value);
                if (error) setError("");
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  event.currentTarget.form?.requestSubmit();
                  return;
                }
                handleRecallKey(event);
              }}
              onPaste={handlePaste}
              placeholder={
                isRoomLoading
                  ? "대화를 불러오는 중입니다…"
                  : "질문을 입력하세요."
              }
              rows={2}
              value={input}
            />
            {/* V2 전송 중에는 ■ 답변 중지 (aibot-v2-chat-cancel, 2026-10-14). /도움 · V1 은 그대로 전송 버튼이 잠긴다 */}
            {engine === "V2" &&
            isSending &&
            activeRequestRef.current &&
            !activeRequestRef.current.stopped ? (
              <button
                aria-label="답변 중지"
                className="chatbot-send-button chatbot-stop-button"
                disabled={stopping}
                onClick={() => void handleStop()}
                type="button"
              >
                <StopIcon fontSize="small" />
              </button>
            ) : (
              <button
                aria-label="전송"
                className="chatbot-send-button"
                disabled={
                  isSending ||
                  isClearing ||
                  isRoomLoading ||
                  chatAttachment.state.status === "UPLOADING" ||
                  chatAttachment.state.status === "REMOVING" ||
                  (!input.trim() &&
                    attachments.length === 0 &&
                    v2Images.length === 0)
                }
                type="submit"
              >
                <SendIcon fontSize="small" />
              </button>
            )}
          </div>
        </form>
      </div>
    </section>
  );
};

export default Chatbot;
